import { Entity, EntityConfig } from './Entity';
import { Vector2, ElementType } from '@shared/types';
import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { Player } from './Player';
import { LootTable, LOOT_TABLES } from '@shared/items/LootTable';
import {
  EliteModifier,
  rollEliteModifiers,
  calculateModifierStats,
  getModifierDisplay
} from '@shared/modifiers/EliteModifiers';
import { getAudioManager } from '@client/audio/AudioManager';
import { IsometricMap } from '@client/world/IsometricMap';
import { getParticleSystem } from '@client/effects/ParticleSystem';
import { COMBAT } from '@shared/constants';
import { getEnemyShadowCanvas, getEnemyAttackCanvas } from '@client/core/GradientCache';
import { BossAI, BossAttackResult } from './BossAI';
import { EnemyAI, EnemyBehavior, AIConfig, getDefaultAIConfig } from './EnemyAI';
import { getEnemySpriteRegistry } from '@client/core/EnemySpriteRegistry';
import type { Direction8 } from '@client/core/SpriteSheet';

/** Shared frame time (seconds) set by GameWorld each frame to avoid per-entity Date.now() calls */
let _sharedFrameTime = 0;
export function setEnemyFrameTime(t: number): void { _sharedFrameTime = t; }

/**
 * Determine 8-directional facing from a direction vector (dx, dy).
 * Uses atan2 to find the angle and maps it to the nearest of 8 directions.
 */
function getFacing8Dir(dx: number, dy: number): 'up' | 'down' | 'left' | 'right' | 'up-left' | 'up-right' | 'down-left' | 'down-right' {
  if (dx === 0 && dy === 0) return 'down';
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);
  // angle: 0=right, 90=down, 180/-180=left, -90=up
  if (angle >= -22.5 && angle < 22.5) return 'right';
  if (angle >= 22.5 && angle < 67.5) return 'down-right';
  if (angle >= 67.5 && angle < 112.5) return 'down';
  if (angle >= 112.5 && angle < 157.5) return 'down-left';
  if (angle >= 157.5 || angle < -157.5) return 'left';
  if (angle >= -157.5 && angle < -112.5) return 'up-left';
  if (angle >= -112.5 && angle < -67.5) return 'up';
  if (angle >= -67.5 && angle < -22.5) return 'up-right';
  return 'down';
}

/**
 * Get a facing multiplier for horizontal sprite flipping from an 8-directional facing.
 * Returns 1 for right-facing directions, -1 for left-facing directions.
 */
function getFacingMult8Dir(facing: string): number {
  if (facing === 'right' || facing === 'up-right' || facing === 'down-right') return 1;
  if (facing === 'left' || facing === 'up-left' || facing === 'down-left') return -1;
  return 1; // 'up' and 'down' default to 1
}

export type EnemyState = 'idle' | 'chase' | 'attack' | 'dead' | 'patrol' | 'alert' | 'reposition' | 'retreat';

export interface EnemyConfig extends EntityConfig {
  name: string;
  attackRange: number;
  attackDamage: number;
  attackSpeed: number;
  aggroRange: number;
  expReward: number;
  color: string;
  lootTable?: LootTable;
  itemLevel?: number;
}

export type EnemyRarity = 'normal' | 'elite' | 'boss';

export class Enemy extends Entity {
  /** When true, all enemies always render their HP bar (even at full health). */
  public static alwaysShowHP: boolean = false;

  public name: string;
  public state: EnemyState = 'idle';
  public target: Player | null = null;
  public rarity: EnemyRarity = 'normal';
  public enemyTypeId: string = 'unknown';

  public attackRange: number;
  public attackDamage: number;
  public attackSpeed: number;
  public aggroRange: number;
  public expReward: number;
  public color: string;
  public lootTable: LootTable;
  public attackElement: ElementType = 'physical';
  public itemLevel: number;

  // Zone tracking for quest item drops
  public zoneId: string = '';

  // Quest item drop (set on death, read by GameWorld)
  public questItemDrop: string | null = null;

  // Knockback
  private knockbackVelocity: Vector2 = { x: 0, y: 0 };
  public knockbackImmune: boolean = false;

  // AI
  private idleTimer: number = 0;
  private idleDirection: Vector2 = { x: 0, y: 0 };

  // Enhanced AI system
  public spawnPosition: Vector2 = { x: 0, y: 0 };
  public bossAI: BossAI | null = null;
  public aiConfig: AIConfig = getDefaultAIConfig('melee_aggro');
  public behavior: EnemyBehavior = 'melee_aggro';
  public enemyAI: EnemyAI | null = null;

  /** Last boss attack result (readable by GameWorld for applying effects) */
  public lastBossAttackResult: BossAttackResult | null = null;

  // Map reference for collision
  protected map: IsometricMap | null = null;

  // Hit feedback
  protected hitFlashTimer: number = 0;

  // Cached adjusted colors (avoid per-frame recalculation)
  private _cachedColor: string = '';
  private _cachedSecondary: string = '';
  private _cachedAccent: string = '';


  // Smooth HP bar display
  protected displayHealthPercent: number = 1.0;
  protected delayedHealthPercent: number = 1.0;
  protected delayedHealthTimer: number = 0;

  // Death animation
  public isDying: boolean = false;
  protected deathTimer: number = 0;
  protected readonly deathDuration: number = 0.5;
  protected deathScale: number = 1.0;
  protected deathAlpha: number = 1.0;
  protected deathRotation: number = 0;

  constructor(config: EnemyConfig) {
    super(config);
    this.name = config.name;
    this.attackRange = config.attackRange;
    this.attackDamage = config.attackDamage;
    this.attackSpeed = config.attackSpeed;
    this.aggroRange = config.aggroRange;
    this.expReward = config.expReward;
    this.color = config.color;
    this.lootTable = config.lootTable || LOOT_TABLES.basic;
    this.itemLevel = config.itemLevel || 1;
    // Record spawn position for leash / patrol
    this.spawnPosition = { x: config.x, y: config.y };
  }

  /**
   * Scale enemy stats based on zone level.
   * Called after construction to adjust difficulty per zone.
   * Each level above 1 increases HP by 12%, damage by 8%, exp by 10%.
   */
  scaleToLevel(zoneLevel: number): void {
    if (zoneLevel <= 1) return;
    const levelDiff = zoneLevel - 1;
    const hpScale = 1 + levelDiff * 0.12;
    const dmgScale = 1 + levelDiff * 0.08;
    const expScale = 1 + levelDiff * 0.10;

    this.stats.maxHp = Math.floor(this.stats.maxHp * hpScale);
    this.stats.hp = this.stats.maxHp;
    this.attackDamage = Math.floor(this.attackDamage * dmgScale);
    this.expReward = Math.floor(this.expReward * expScale);
    this.itemLevel = Math.max(this.itemLevel, zoneLevel);
  }

  setTarget(player: Player): void {
    this.target = player;
  }

  setMap(map: IsometricMap): void {
    this.map = map;
  }

  applyKnockback(dirX: number, dirY: number, force: number): void {
    if (this.knockbackImmune || !this.isAlive || this.isDying) return;
    const len = Math.sqrt(dirX * dirX + dirY * dirY);
    if (len === 0) return;
    this.knockbackVelocity.x = (dirX / len) * force;
    this.knockbackVelocity.y = (dirY / len) * force;
  }

  // Override move to check walkability
  move(dx: number, dy: number, deltaTime: number): void {
    if (!this.isAlive) return;

    // Normalize diagonal movement
    if (dx !== 0 && dy !== 0) {
      const length = Math.sqrt(dx * dx + dy * dy);
      dx /= length;
      dy /= length;
    }

    const effectiveSpeed = this.speed * this.slowMultiplier;
    const newX = this.position.x + dx * effectiveSpeed * deltaTime;
    const newY = this.position.y + dy * effectiveSpeed * deltaTime;

    if (this.map) {
      // Check if new position is walkable
      if (this.map.isWorldPositionWalkable(newX, newY)) {
        this.position.x = newX;
        this.position.y = newY;
        this.velocity.x = dx * effectiveSpeed;
        this.velocity.y = dy * effectiveSpeed;
      } else {
        // Try sliding along walls
        if (this.map.isWorldPositionWalkable(newX, this.position.y)) {
          this.position.x = newX;
          this.velocity.x = dx * effectiveSpeed;
          this.velocity.y = 0;
        } else if (this.map.isWorldPositionWalkable(this.position.x, newY)) {
          this.position.y = newY;
          this.velocity.x = 0;
          this.velocity.y = dy * effectiveSpeed;
        } else {
          this.velocity.x = 0;
          this.velocity.y = 0;
        }
      }
    } else {
      // No map reference, move freely
      this.position.x = newX;
      this.position.y = newY;
      this.velocity.x = dx * effectiveSpeed;
      this.velocity.y = dy * effectiveSpeed;
    }

    // Update facing direction (8-directional)
    if (dx !== 0 || dy !== 0) {
      this.facing = getFacing8Dir(dx, dy);
    }
  }

  update(deltaTime: number): void {
    if (this.isDying) {
      this.deathTimer -= deltaTime;
      const progress = 1 - (this.deathTimer / this.deathDuration);
      this.deathScale = 1.0 - progress * 0.7; // 1.0 -> 0.3
      this.deathAlpha = 1.0 - progress;        // 1.0 -> 0.0
      this.deathRotation = progress * 1.5;     // Spin during death

      if (this.deathTimer <= 0) {
        this.isAlive = false;
        this.isDying = false;
        this.velocity = { x: 0, y: 0 };
      }
      return;
    }

    if (!this.isAlive) {
      this.state = 'dead';
      return;
    }

    this.updateTimers(deltaTime);
    this.updateHitFlash(deltaTime);

    // Track this enemy as potential damage source for death screen attribution
    // Set preemptively so any takeDamage() call in this update tick gets attributed correctly
    if (this.target && this.target.isAlive && this.state !== 'idle' && this.state !== 'dead') {
      this.target.lastDamageSourceName = this.name;
    }

    // Smooth HP bar lerp
    const actualPercent = this.getHealthPercent();
    this.displayHealthPercent = actualPercent;
    if (this.delayedHealthPercent > actualPercent) {
      this.delayedHealthTimer += deltaTime;
      if (this.delayedHealthTimer > 0.3) {
        this.delayedHealthPercent = Math.max(actualPercent, this.delayedHealthPercent - deltaTime * 2);
      }
    } else {
      this.delayedHealthPercent = actualPercent;
      this.delayedHealthTimer = 0;
    }

    // Process status effects (DoT damage, slow, freeze)
    const dotDamage = this.updateStatusEffects(deltaTime);
    if (dotDamage > 0) {
      // Apply DoT damage directly to HP (bypasses armor for simplicity)
      this.stats.hp = Math.max(0, this.stats.hp - dotDamage);
      if (this.stats.hp <= 0) {
        this.die();
        return;
      }
    }

    // Process buffs (regen, etc.)
    const buffResult = this.updateBuffs(deltaTime);
    if (buffResult.hpRegen > 0) {
      this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + Math.floor(buffResult.hpRegen));
    }

    // Skip AI and knockback if frozen
    if (this.isFrozen) {
      this.knockbackVelocity.x = 0;
      this.knockbackVelocity.y = 0;
      return;
    }

    // Process knockback
    if (Math.abs(this.knockbackVelocity.x) > 0.5 || Math.abs(this.knockbackVelocity.y) > 0.5) {
      const kbDecay = 8;
      this.knockbackVelocity.x *= Math.max(0, 1 - kbDecay * deltaTime);
      this.knockbackVelocity.y *= Math.max(0, 1 - kbDecay * deltaTime);
      const newX = this.position.x + this.knockbackVelocity.x * deltaTime;
      const newY = this.position.y + this.knockbackVelocity.y * deltaTime;
      if (this.map && this.map.isWorldPositionWalkable(newX, newY)) {
        this.position.x = newX;
        this.position.y = newY;
      } else {
        this.knockbackVelocity.x = 0;
        this.knockbackVelocity.y = 0;
      }
    }

    // Clear last boss attack result each frame
    this.lastBossAttackResult = null;

    switch (this.state) {
      case 'idle':
      case 'patrol':
        this.updateIdle(deltaTime);
        break;
      case 'alert':
        this.updateAlert(deltaTime);
        break;
      case 'chase':
      case 'reposition':
        this.updateChase(deltaTime);
        break;
      case 'attack':
        this.updateAttack(deltaTime);
        break;
      case 'retreat':
        this.updateRetreat(deltaTime);
        break;
    }
  }

  private updateIdle(deltaTime: number): void {
    // If EnemyAI is available, use enhanced patrol behavior
    if (this.enemyAI && this.target) {
      const aiResult = this.enemyAI.update(
        this.position, this.spawnPosition, this.target,
        this.aggroRange, this.attackRange, deltaTime,
        false, false,
      );

      if (aiResult.shouldRetreat) {
        this.state = 'retreat';
        return;
      }

      if (aiResult.isAlert) {
        this.state = 'alert';
        return;
      }

      if (aiResult.wantsAttack) {
        this.state = 'attack';
        return;
      }

      // If AI is no longer patrolling, it detected a target -- switch to chase
      if (!aiResult.isPatrolling) {
        this.state = 'chase';
        return;
      }

      // Patrol movement
      this.move(
        aiResult.moveDirection.x * aiResult.speedMultiplier,
        aiResult.moveDirection.y * aiResult.speedMultiplier,
        deltaTime,
      );
      return;
    }

    // Fallback: original idle behavior
    // Check for player in aggro range (squared distance avoids sqrt)
    if (this.target && this.target.isAlive) {
      const distSq = this.distanceToSquared(this.target);
      if (distSq <= this.aggroRange * this.aggroRange) {
        this.state = 'chase';
        return;
      }
    }

    // Random wandering
    this.idleTimer -= deltaTime;
    if (this.idleTimer <= 0) {
      // Pick new random direction or stop
      if (Math.random() < COMBAT.IDLE_WANDER_CHANCE) {
        const angle = Math.random() * Math.PI * 2;
        this.idleDirection = {
          x: Math.cos(angle),
          y: Math.sin(angle),
        };
      } else {
        this.idleDirection = { x: 0, y: 0 };
      }
      this.idleTimer = 1 + Math.random() * 2;
    }

    // Move slowly while idle
    this.move(
      this.idleDirection.x * COMBAT.IDLE_MOVE_SPEED_MULT,
      this.idleDirection.y * COMBAT.IDLE_MOVE_SPEED_MULT,
      deltaTime
    );
  }

  private updateAlert(deltaTime: number): void {
    // Wait during alert delay (e.g. caster/ranged spotting player)
    if (this.enemyAI && this.target) {
      const aiResult = this.enemyAI.update(
        this.position, this.spawnPosition, this.target,
        this.aggroRange, this.attackRange, deltaTime,
        false, false,
      );

      if (!aiResult.isAlert) {
        // Alert delay completed, start chasing
        this.state = 'chase';
      }
      // Stay still during alert
      return;
    }

    // Fallback if no EnemyAI: immediately chase
    this.state = 'chase';
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    // If EnemyAI is available, use behavior-specific positioning
    if (this.enemyAI) {
      const aiResult = this.enemyAI.update(
        this.position, this.spawnPosition, this.target,
        this.aggroRange, this.attackRange, deltaTime,
        true, false,
      );

      if (aiResult.shouldRetreat) {
        this.state = 'retreat';
        return;
      }

      if (aiResult.wantsAttack) {
        this.state = 'attack';
        // Continue moving if repositioning while attacking (ranged/caster)
        if (aiResult.isRepositioning && (aiResult.moveDirection.x !== 0 || aiResult.moveDirection.y !== 0)) {
          this.move(
            aiResult.moveDirection.x * aiResult.speedMultiplier,
            aiResult.moveDirection.y * aiResult.speedMultiplier,
            deltaTime,
          );
        }
        return;
      }

      if (aiResult.isPatrolling) {
        // Lost target, return to idle/patrol
        this.state = 'idle';
        return;
      }

      // Move according to AI direction
      if (aiResult.moveDirection.x !== 0 || aiResult.moveDirection.y !== 0) {
        this.move(
          aiResult.moveDirection.x * aiResult.speedMultiplier,
          aiResult.moveDirection.y * aiResult.speedMultiplier,
          deltaTime,
        );
      }
      return;
    }

    // Fallback: original chase behavior
    const distSq = this.distanceToSquared(this.target);
    const deaggroRange = this.aggroRange * COMBAT.DEAGGRO_RANGE_MULT;

    // Lost aggro
    if (distSq > deaggroRange * deaggroRange) {
      this.state = 'idle';
      return;
    }

    // In attack range
    if (distSq <= this.attackRange * this.attackRange) {
      this.state = 'attack';
      return;
    }

    // Chase (directionTo still needs sqrt for normalization)
    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private updateAttack(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distSq = this.distanceToSquared(this.target);
    const leashRange = this.attackRange * COMBAT.ATTACK_LEASH_MULT;

    // Target moved out of range
    if (distSq > leashRange * leashRange) {
      this.state = 'chase';
      return;
    }

    // Face target (8-directional)
    this.facing = getFacing8Dir(
      this.target.position.x - this.position.x,
      this.target.position.y - this.position.y
    );

    // Boss AI: delegate attack selection to BossAI
    if (this.bossAI && this.target) {
      const bossResult = this.bossAI.update(this, this.target, deltaTime);
      if (bossResult) {
        this.lastBossAttackResult = bossResult;
        this.isAttacking = true;
        this.attackCooldown = 1 / this.attackSpeed;
      }
      return;
    }

    // Attack when cooldown ready
    if (this.attackCooldown <= 0) {
      this.performAttack();
    }
  }

  private updateRetreat(deltaTime: number): void {
    // Return to spawn and regen HP
    const dx = this.spawnPosition.x - this.position.x;
    const dy = this.spawnPosition.y - this.position.y;
    const distSq = dx * dx + dy * dy;

    // Heal while retreating (10% max HP per second)
    if (this.enemyAI) {
      const healAmount = this.enemyAI.getRetreatHealAmount(this.stats.maxHp, deltaTime);
      if (healAmount > 0) {
        this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + healAmount);
      }
    }

    // Reached spawn: switch to idle
    if (distSq < 20 * 20) {
      this.state = 'idle';
      if (this.enemyAI) {
        this.enemyAI.resetRetreat();
      }
      return;
    }

    // Move back to spawn
    const dist = Math.sqrt(distSq);
    if (dist > 0) {
      this.move(dx / dist, dy / dist, deltaTime);
    }
  }

  private performAttack(): void {
    if (!this.target) return;

    this.isAttacking = true;
    this.attackCooldown = 1 / this.attackSpeed;

    // Deal damage to player (squared distance check)
    const distSq = this.distanceToSquared(this.target);
    if (distSq <= this.attackRange * this.attackRange) {
      const prevHp = this.target.stats.hp;
      this.target.takeDamage(this.attackDamage, this.attackElement);
      const actualDmg = prevHp - this.target.stats.hp;
      if (actualDmg > 0) {
        const audio = getAudioManager();
        audio.play(Math.random() < 0.5 ? 'hit_soft' : 'hit_soft_1', {
          position: this.position,
          listenerPos: this.target.position,
          maxDistance: 600,
        });
      }
    }
  }

  /**
   * Initialize the BossAI component for this enemy.
   * Call this after construction for boss-type enemies.
   * @param bossType Boss identifier matching BossAI phase configs
   *                 (e.g. 'albasti_kralicesi', 'karabasan_miniboss', 'dag_devi')
   */
  initBossAI(bossType: string): void {
    this.bossAI = new BossAI(bossType);
    this.behavior = 'boss';
    this.aiConfig = getDefaultAIConfig('boss');
    this.enemyAI = new EnemyAI(this.aiConfig);
  }

  /**
   * Initialize the enhanced EnemyAI component for this enemy.
   * @param behaviorType The behavior pattern for this enemy
   * @param configOverrides Optional partial overrides for the default AIConfig
   */
  initEnemyAI(behaviorType: EnemyBehavior, configOverrides?: Partial<AIConfig>): void {
    this.behavior = behaviorType;
    this.aiConfig = getDefaultAIConfig(behaviorType);
    if (configOverrides) {
      Object.assign(this.aiConfig, configOverrides);
    }
    this.enemyAI = new EnemyAI(this.aiConfig);
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    if (this.isDying) return 0;
    const damage = super.takeDamage(amount, type, bypassES);
    if (damage > 0) {
      this.hitFlashTimer = 0.1;

      // Play hit sound
      const audio = getAudioManager();
      const hitSounds = ['enemy_hit', 'enemy_hit_1'];
      audio.play(hitSounds[Math.floor(Math.random() * hitSounds.length)]);

      // Aggro when hit
      if (this.state === 'idle') {
        this.state = 'chase';
      }

      // Play death sound
      if (this.isDying) {
        audio.play('enemy_death');
      }
    }
    return damage;
  }

  die(lastDamageElement?: string): void {
    if (this.isDying) return;
    this.isDying = true;
    this.deathTimer = this.deathDuration;

    // Quest item drop check
    this.checkQuestItemDrop();

    // Element-specific death particles
    const ps = getParticleSystem();
    if (ps) {
      const pos = { x: this.position.x, y: this.position.y - this.height / 2 };

      switch (lastDamageElement) {
        case 'od': {
          // Fire death: burning embers rising, ash falling
          ps.emit({
            position: pos,
            count: 20,
            color: ['#ff6622', '#ffaa44', '#ffdd88'],
            speed: { min: 40, max: 150 },
            size: { min: 1, max: 5 },
            life: { min: 0.5, max: 1.2 },
            direction: -Math.PI / 2, // Upward
            spread: Math.PI * 0.8,
            gravity: -30, // Rise upward
            friction: 1.5,
            alphaDecay: 0.8,
            sizeDecay: 2,
            shape: 'circle',
          });
          // Ash particles
          ps.emit({
            position: pos,
            count: 8,
            color: ['#444444', '#666666', '#888888'],
            speed: { min: 20, max: 60 },
            size: { min: 1, max: 3 },
            life: { min: 0.8, max: 1.5 },
            direction: -Math.PI / 2,
            spread: Math.PI,
            gravity: 40,
            friction: 3,
            alphaDecay: 0.6,
            sizeDecay: 1,
            shape: 'circle',
          });
          break;
        }
        case 'su': {
          // Ice death: crystal shards shattering outward
          ps.emit({
            position: pos,
            count: 18,
            color: ['#88ddff', '#aaeeff', '#ffffff'],
            speed: { min: 100, max: 280 },
            size: { min: 2, max: 5 },
            life: { min: 0.3, max: 0.6 },
            direction: 0,
            spread: Math.PI * 2,
            gravity: 200,
            friction: 2,
            alphaDecay: 1.5,
            sizeDecay: 3,
            shape: 'square', // Angular ice shards
          });
          break;
        }
        case 'gok': {
          // Lightning death: electric sparks + flash
          ps.emit({
            position: pos,
            count: 25,
            color: ['#88ccff', '#aaddff', '#ffffff'],
            speed: { min: 150, max: 350 },
            size: { min: 1, max: 3 },
            life: { min: 0.1, max: 0.4 },
            direction: 0,
            spread: Math.PI * 2,
            gravity: 0,
            friction: 5,
            alphaDecay: 3,
            sizeDecay: 5,
            shape: 'line', // Spark lines
          });
          break;
        }
        case 'karanlik': {
          // Dark death: shadow burst + dark sparkles
          ps.emit({
            position: pos,
            count: 22,
            color: ['#ddaaff', '#eeccff', '#ffffff'],
            speed: { min: 50, max: 200 },
            size: { min: 1, max: 4 },
            life: { min: 0.4, max: 1.0 },
            direction: -Math.PI / 2,
            spread: Math.PI,
            gravity: -20,
            friction: 2,
            alphaDecay: 1,
            sizeDecay: 2,
            shape: 'star',
          });
          break;
        }
        default: {
          // Physical / default: original blood-like burst
          ps.emit({
            position: pos,
            count: 15 + Math.floor(Math.random() * 6),
            color: [this.color, this.adjustColor(this.color, 40), '#ffffff'],
            speed: { min: 80, max: 220 },
            size: { min: 2, max: 6 },
            life: { min: 0.3, max: 0.7 },
            direction: 0,
            spread: Math.PI * 2,
            gravity: 120,
            friction: 2,
            alphaDecay: 1.2,
            sizeDecay: 4,
            shape: 'circle',
          });
          break;
        }
      }
    }
  }

  /**
   * Check if this enemy should drop a quest item based on zone and enemy type.
   * Sets this.questItemDrop which GameWorld can read after death.
   */
  private checkQuestItemDrop(): void {
    // Karabasan enemies in ruh_batakligi zone have 30% chance to drop ruh_damlasi
    if (this.zoneId === 'ruh_batakligi' && this.name.includes('Karabasan')) {
      if (Math.random() < 0.3) {
        this.questItemDrop = 'ruh_damlasi';
      }
    }

    // Enemies in erlik_tapinagi zone have 25% chance to drop erlik_idolu
    if (this.zoneId === 'erlik_tapinagi' && this.name.includes('Sandık')) {
      if (Math.random() < 0.25) {
        this.questItemDrop = 'erlik_idolu';
      }
    }
  }

  private updateHitFlash(deltaTime: number): void {
    if (this.hitFlashTimer > 0) {
      this.hitFlashTimer -= deltaTime;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const z = camera.zoom;
    const time = _sharedFrameTime || Date.now() / 1000;
    const isHit = this.hitFlashTimer > 0;

    // Apply death animation transform (scale, alpha, rotation)
    this.applyDeathTransform(ctx, screenPos);

    // Subtle idle animation: gentle bob and breathing
    const isMoving = this.velocity && (Math.abs(this.velocity.x) > 5 || Math.abs(this.velocity.y) > 5);
    const idleBob = isMoving ? Math.sin(time * 10) * 1.5 : Math.sin(time * 2) * 0.5;
    const breathe = isMoving ? 0 : Math.sin(time * 2.5) * 0.015;

    // Elite/Boss ground aura (flat color + pulsing alpha — avoids per-frame createRadialGradient)
    if (this.rarity === 'elite' || this.rarity === 'boss') {
      const auraRadius = (this.rarity === 'boss' ? 28 : 20) * z;
      const pulse = Math.sin(time * 3) * 0.15 + 0.35;
      ctx.globalAlpha = pulse;
      ctx.fillStyle = this.rarity === 'boss' ? 'rgba(255, 50, 50, 1)' : 'rgba(218, 165, 32, 1)';
      ctx.beginPath();
      ctx.ellipse(screenPos.x, screenPos.y + 3 * z, auraRadius, auraRadius * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Enhanced shadow with cached gradient canvas
    const shadowCanvas = getEnemyShadowCanvas(z);
    ctx.drawImage(
      shadowCanvas,
      screenPos.x - shadowCanvas.width / 2,
      screenPos.y + 3 * z - shadowCanvas.height / 2
    );

    ctx.save();

    // Try sprite-based rendering first (PixelLab format)
    const spriteRegistry = getEnemySpriteRegistry();
    let usedSprite = false;
    if (spriteRegistry.isLoaded(this.enemyTypeId)) {
      ctx.translate(screenPos.x, screenPos.y);

      const animName = this.isAttacking ? 'attack' : isMoving ? 'walk' : 'idle';
      const spriteSize = Math.max(this.width, this.height) * 2.4 * z;

      // Draw: 0=fail, 1=static rotation, 2=frame animation
      const drawResult = spriteRegistry.draw(
        this.enemyTypeId, ctx,
        this.facing as Direction8,
        -spriteSize * 0.5, -spriteSize * 0.82,
        spriteSize, spriteSize,
        animName, time, 8
      );

      if (drawResult > 0) {
        usedSprite = true;

        // Static rotation without frame anim → add code-driven walk/attack feel
        if (drawResult === 1 && isMoving) {
          // Slight squash-stretch to imply movement
          const walkT = Math.sin(time * 10);
          ctx.scale(1 + walkT * 0.03, 1 - walkT * 0.03);
        }
        if (drawResult === 1 && this.isAttacking) {
          const atkT = Math.sin(time * 14);
          ctx.scale(1 + Math.abs(atkT) * 0.08, 1 - Math.abs(atkT) * 0.04);
        }
      }
    }

    if (!usedSprite) {
      // Procedural fallback: bob + breathe animation
      ctx.translate(screenPos.x, screenPos.y + idleBob * z);
      const facingFlip = getFacingMult8Dir(this.facing);
      ctx.scale(facingFlip * (1 + breathe), 1 - breathe * 0.5);

      // Get colors (with hit flash) – use cached adjusted colors
      if (this._cachedColor !== this.color) {
        this._cachedColor = this.color;
        this._cachedSecondary = this.adjustColor(this.color, -40);
        this._cachedAccent = this.adjustColor(this.color, 50);
      }
      const primaryColor = isHit ? '#ff8888' : this.color;
      const secondaryColor = isHit ? '#ffaaaa' : this._cachedSecondary;
      const accentColor = isHit ? '#ffffff' : this._cachedAccent;

      // Render based on enemy type
      this.renderBody(ctx, z, primaryColor, secondaryColor, accentColor, time);
    }

    // Hit flash overlay (works for both sprite and procedural)
    if (isHit) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.ellipse(0, -this.height * z * 0.5, this.width * z * 0.5, this.height * z * 0.5, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Status effect visual overlays (rendered in local coords before restore)
    this.renderStatusEffectOverlays(ctx, z, time);

    ctx.restore();

    // Enhanced HP bar (skip during death animation)
    // Show HP bar if alwaysShowHP is enabled, or if the enemy has taken damage
    if (!this.isDying && (Enemy.alwaysShowHP || this.getHealthPercent() < 1)) {
      this.renderHealthBar(renderer, screenPos, z);
    }

    // Attack indicator with animation
    if (this.isAttacking && !this.isDying) {
      const attackPhase = (time * 8) % 1;
      const attackX = getFacingMult8Dir(this.facing) * 25 * z;
      const attackY = screenPos.y - 25 * z + idleBob * z;

      // Outer pulse ring
      ctx.beginPath();
      ctx.arc(screenPos.x + attackX, attackY, (10 + attackPhase * 8) * z, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 68, 68, ${0.6 - attackPhase * 0.6})`;
      ctx.lineWidth = 2 * z;
      ctx.stroke();

      // Inner glow (cached gradient canvas)
      const attackCanvas = getEnemyAttackCanvas(z);
      ctx.drawImage(
        attackCanvas,
        screenPos.x + attackX - attackCanvas.width / 2,
        attackY - attackCanvas.height / 2
      );
    }

    // Restore death animation transform
    this.restoreDeathTransform(ctx);
  }

  public applyDeathTransform(ctx: CanvasRenderingContext2D, screenPos: { x: number; y: number }): void {
    if (this.isDying) {
      ctx.save();
      ctx.globalAlpha = this.deathAlpha;
      ctx.translate(screenPos.x, screenPos.y);
      ctx.rotate(this.deathRotation);
      ctx.scale(this.deathScale, this.deathScale);
      ctx.translate(-screenPos.x, -screenPos.y);
    }
  }

  public restoreDeathTransform(ctx: CanvasRenderingContext2D): void {
    if (this.isDying) {
      ctx.restore();
    }
  }

  /** Get horizontal flip multiplier (1 = right, -1 = left) based on current facing direction */
  protected get facingDir(): number {
    return getFacingMult8Dir(this.facing);
  }

  /**
   * Render visual overlays for active status effects.
   * Called in local (translated) coordinates, so (0,0) is the entity's feet.
   */
  protected renderStatusEffectOverlays(ctx: CanvasRenderingContext2D, z: number, time: number): void {
    const effects = this.statusEffects.getAll();
    if (effects.length === 0) return;

    const hw = this.width * z * 0.5;
    const hh = this.height * z;

    for (const effect of effects) {
      switch (effect.type) {
        case 'burn': {
          // Flickering orange/red tint over body
          const flicker = 0.15 + Math.sin(time * 12) * 0.05;
          ctx.fillStyle = `rgba(255, 100, 0, ${flicker})`;
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw, hh * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Small rising flame particles
          for (let i = 0; i < 3; i++) {
            const px = (Math.sin(time * 5 + i * 2.1) * hw * 0.6);
            const py = -hh * (0.2 + ((time * 1.5 + i * 0.3) % 1) * 0.7);
            const pSize = (2 + Math.sin(time * 8 + i) * 1) * z;
            ctx.fillStyle = `rgba(255, ${150 + i * 30}, 0, ${0.6 - ((time * 1.5 + i * 0.3) % 1) * 0.5})`;
            ctx.beginPath();
            ctx.arc(px, py, pSize, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'poison': {
          // Green tint + drip particles
          const pulse = 0.08 + Math.sin(time * 4) * 0.04;
          ctx.fillStyle = `rgba(50, 200, 50, ${pulse})`;
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw, hh * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Poison drips
          for (let i = 0; i < 2; i++) {
            const dripX = (i === 0 ? -1 : 1) * hw * 0.4;
            const dripY = ((time * 0.8 + i * 0.5) % 1) * 8 * z;
            ctx.fillStyle = `rgba(30, 180, 30, ${0.5 - ((time * 0.8 + i * 0.5) % 1) * 0.4})`;
            ctx.beginPath();
            ctx.ellipse(dripX, dripY, 2 * z, 3 * z, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'bleed': {
          // Red droplets falling
          const bleedAlpha = 0.1 + Math.sin(time * 6) * 0.05;
          ctx.fillStyle = `rgba(180, 0, 0, ${bleedAlpha})`;
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw, hh * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Blood drops
          for (let i = 0; i < effect.stacks && i < 5; i++) {
            const dropX = Math.sin(time * 2 + i * 1.3) * hw * 0.5;
            const dropPhase = (time * 1.2 + i * 0.25) % 1;
            const dropY = -hh * 0.3 + dropPhase * hh * 0.4;
            ctx.fillStyle = `rgba(200, 0, 0, ${0.7 - dropPhase * 0.6})`;
            ctx.beginPath();
            ctx.arc(dropX, dropY, (1.5 + Math.sin(i) * 0.5) * z, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 'chill': {
          // Blue tint
          ctx.fillStyle = 'rgba(100, 150, 255, 0.15)';
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw, hh * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 'freeze': {
          // Strong ice overlay + frozen border
          ctx.fillStyle = 'rgba(150, 200, 255, 0.3)';
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw + 2 * z, hh * 0.5 + 2 * z, 0, 0, Math.PI * 2);
          ctx.fill();
          // Ice crystal border
          ctx.strokeStyle = 'rgba(180, 220, 255, 0.6)';
          ctx.lineWidth = 2 * z;
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw + 2 * z, hh * 0.5 + 2 * z, 0, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'shock': {
          // Lightning sparks
          const sparkAlpha = 0.1 + Math.abs(Math.sin(time * 15)) * 0.15;
          ctx.fillStyle = `rgba(255, 255, 100, ${sparkAlpha})`;
          ctx.beginPath();
          ctx.ellipse(0, -hh * 0.5, hw, hh * 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Spark lines
          if (Math.sin(time * 20) > 0.3) {
            ctx.strokeStyle = 'rgba(255, 255, 150, 0.7)';
            ctx.lineWidth = 1 * z;
            const sx = (Math.random() - 0.5) * hw;
            const sy = -hh * Math.random();
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(sx + (Math.random() - 0.5) * 8 * z, sy + (Math.random() - 0.5) * 8 * z);
            ctx.lineTo(sx + (Math.random() - 0.5) * 12 * z, sy + (Math.random() - 0.5) * 12 * z);
            ctx.stroke();
          }
          break;
        }
      }
    }
  }

  protected renderBody(
    ctx: CanvasRenderingContext2D,
    z: number,
    primary: string,
    secondary: string,
    _accent: string,
    time: number
  ): void {
    // Enhanced monster body - menacing slime creature
    const bodyHeight = this.height * z;
    const bodyWidth = this.width * z;
    const wobble = Math.sin(time * 4) * 2 * z;

    // Solid body fill (replaces per-frame radial gradient for performance)
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.moveTo(-bodyWidth / 2, 0);
    ctx.bezierCurveTo(
      -bodyWidth / 2 - wobble, -bodyHeight * 0.3,
      -bodyWidth / 2, -bodyHeight * 0.7,
      -bodyWidth * 0.2, -bodyHeight
    );
    ctx.bezierCurveTo(
      0, -bodyHeight - 3 * z,
      bodyWidth * 0.2, -bodyHeight,
      bodyWidth * 0.2, -bodyHeight
    );
    ctx.bezierCurveTo(
      bodyWidth / 2, -bodyHeight * 0.7,
      bodyWidth / 2 + wobble, -bodyHeight * 0.3,
      bodyWidth / 2, 0
    );
    ctx.closePath();
    ctx.fill();

    // Shiny highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.ellipse(-bodyWidth * 0.15, -bodyHeight * 0.7, bodyWidth * 0.2, bodyHeight * 0.15, -0.3, 0, Math.PI * 2);
    ctx.fill();

    // Dark underbelly
    ctx.fillStyle = secondary + '80';
    ctx.beginPath();
    ctx.ellipse(0, -bodyHeight * 0.15, bodyWidth * 0.4, bodyHeight * 0.12, 0, 0, Math.PI);
    ctx.fill();

    // Dripping effect
    const dripPhase = (time * 0.5) % 1;
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.ellipse(-bodyWidth * 0.25, 2 * z + dripPhase * 5 * z, 3 * z, (4 + dripPhase * 3) * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Eyes with glow
    const eyeY = -bodyHeight * 0.6;
    const eyeSpacing = 7 * z;
    const blinkPhase = Math.floor(time * 0.5) % 4 === 0 ? Math.abs(Math.sin(time * 20)) : 1;

    // Eye glow
    ctx.fillStyle = 'rgba(255, 200, 0, 0.3)';
    ctx.beginPath();
    ctx.arc(-eyeSpacing, eyeY, 8 * z, 0, Math.PI * 2);
    ctx.arc(eyeSpacing, eyeY, 8 * z, 0, Math.PI * 2);
    ctx.fill();

    // Eye sockets
    ctx.fillStyle = '#1a0a00';
    ctx.beginPath();
    ctx.ellipse(-eyeSpacing, eyeY, 5 * z, 6 * z * blinkPhase, 0, 0, Math.PI * 2);
    ctx.ellipse(eyeSpacing, eyeY, 5 * z, 6 * z * blinkPhase, 0, 0, Math.PI * 2);
    ctx.fill();

    // Glowing pupils
    const pupilLook = Math.sin(time * 1.5) * 2;
    if (blinkPhase > 0.3) {
      // Solid pupil fill (replaces per-frame radial gradients for performance)
      ctx.fillStyle = '#ff6600';
      ctx.beginPath();
      ctx.arc(-eyeSpacing + pupilLook * z, eyeY, 3 * z, 0, Math.PI * 2);
      ctx.fill();

      ctx.beginPath();
      ctx.arc(eyeSpacing + pupilLook * z, eyeY, 3 * z, 0, Math.PI * 2);
      ctx.fill();

      // Eye highlights
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(-eyeSpacing + pupilLook * z - 1 * z, eyeY - 1.5 * z, 1 * z, 0, Math.PI * 2);
      ctx.arc(eyeSpacing + pupilLook * z - 1 * z, eyeY - 1.5 * z, 1 * z, 0, Math.PI * 2);
      ctx.fill();
    }

    // Menacing mouth
    const mouthOpen = Math.abs(Math.sin(time * 2)) * 0.3 + 0.2;
    ctx.fillStyle = '#1a0000';
    ctx.beginPath();
    ctx.ellipse(0, -bodyHeight * 0.32, 10 * z, 6 * z * mouthOpen, 0, 0, Math.PI * 2);
    ctx.fill();

    // Teeth
    if (mouthOpen > 0.3) {
      ctx.fillStyle = '#fff';
      for (let i = -2; i <= 2; i++) {
        const toothX = i * 3.5 * z;
        const toothSize = 2 * z * (1 - Math.abs(i) * 0.15);
        ctx.beginPath();
        ctx.moveTo(toothX - toothSize * 0.5, -bodyHeight * 0.38);
        ctx.lineTo(toothX, -bodyHeight * 0.32 + 3 * z * mouthOpen);
        ctx.lineTo(toothX + toothSize * 0.5, -bodyHeight * 0.38);
        ctx.fill();
      }
    }

    // Inner mouth darkness
    ctx.fillStyle = '#0a0000';
    ctx.beginPath();
    ctx.ellipse(0, -bodyHeight * 0.3, 6 * z, 3 * z * mouthOpen, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  protected adjustColor(color: string, amount: number): string {
    const hex = color.replace('#', '');
    const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
    const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
    const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  protected renderHealthBar(
    renderer: Renderer,
    screenPos: Vector2,
    zoom: number
  ): void {
    const ctx = renderer.context;
    const isElite = this.rarity === 'elite';
    const isBoss = this.rarity === 'boss';

    // Boss/Elite get wider bars
    const baseWidth = isBoss ? 70 : isElite ? 54 : 44;
    const baseHeight = isBoss ? 9 : isElite ? 8 : 7;
    const barWidth = baseWidth * zoom;
    const barHeight = baseHeight * zoom;
    const barX = screenPos.x - barWidth / 2;
    const barY = screenPos.y - this.height * zoom - 18 * zoom;
    const healthPercent = this.getHealthPercent();

    // Outer frame shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.beginPath();
    ctx.roundRect(barX - 1, barY + 2, barWidth + 2, barHeight, 2 * zoom);
    ctx.fill();

    // Background
    ctx.fillStyle = '#222';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barWidth, barHeight, 2 * zoom);
    ctx.fill();

    // Delayed health (shows previous HP as white/orange trail)
    if (this.delayedHealthPercent > healthPercent) {
      const delayedWidth = barWidth * this.delayedHealthPercent;
      ctx.fillStyle = isElite ? 'rgba(218, 165, 32, 0.4)' : isBoss ? 'rgba(255, 100, 100, 0.4)' : 'rgba(255, 200, 100, 0.4)';
      ctx.beginPath();
      ctx.roundRect(barX + 1, barY + 1, delayedWidth - 2, barHeight - 2, 1.5 * zoom);
      ctx.fill();
    }

    // Health fill
    if (healthPercent > 0) {
      const healthWidth = barWidth * healthPercent;

      if (isElite) {
        // Elite: orange-gold tones
        ctx.fillStyle = healthPercent > 0.5 ? '#cc8822' : healthPercent > 0.25 ? '#aa6611' : '#cc3322';
      } else if (isBoss) {
        // Boss: deep red-purple
        ctx.fillStyle = healthPercent > 0.5 ? '#aa2255' : healthPercent > 0.25 ? '#aa3322' : '#cc1111';
      } else {
        if (healthPercent > 0.5) {
          ctx.fillStyle = '#44aa44';
        } else if (healthPercent > 0.25) {
          ctx.fillStyle = '#aaaa22';
        } else {
          ctx.fillStyle = '#cc3322';
        }
      }

      ctx.beginPath();
      ctx.roundRect(barX + 1, barY + 1, healthWidth - 2, barHeight - 2, 1.5 * zoom);
      ctx.fill();

      // Shine highlight (top half)
      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.beginPath();
      ctx.roundRect(barX + 2, barY + 1, healthWidth - 4, barHeight * 0.35, 1 * zoom);
      ctx.fill();
    }

    // Frame border - gold for elite, red-gold for boss, gray for normal
    if (isElite) {
      ctx.strokeStyle = '#daa520';
      ctx.lineWidth = 1.5;
    } else if (isBoss) {
      ctx.strokeStyle = '#ff4444';
      ctx.lineWidth = 2;
    } else {
      ctx.strokeStyle = '#444';
      ctx.lineWidth = 1;
    }
    ctx.beginPath();
    ctx.roundRect(barX, barY, barWidth, barHeight, 2 * zoom);
    ctx.stroke();

    // Elite: small diamond icon to the left of the bar
    if (isElite) {
      const iconX = barX - 8 * zoom;
      const iconY = barY + barHeight / 2;
      const iconSize = 4 * zoom;
      ctx.fillStyle = '#daa520';
      ctx.beginPath();
      ctx.moveTo(iconX, iconY - iconSize);
      ctx.lineTo(iconX + iconSize, iconY);
      ctx.lineTo(iconX, iconY + iconSize);
      ctx.lineTo(iconX - iconSize, iconY);
      ctx.closePath();
      ctx.fill();
    }

    // Boss: show name above bar and HP percentage
    if (isBoss) {
      ctx.save();
      ctx.font = `bold ${Math.round(10 * zoom)}px Arial`;
      ctx.fillStyle = '#ff6666';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 3;
      ctx.fillText(this.name, screenPos.x, barY - 2 * zoom);

      // HP percentage on the bar
      ctx.font = `bold ${Math.round(7 * zoom)}px Arial`;
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'middle';
      ctx.shadowBlur = 2;
      ctx.fillText(`${Math.ceil(healthPercent * 100)}%`, screenPos.x, barY + barHeight / 2);
      ctx.restore();
    }
  }
}

// Körmös - Basic melee enemy (kötü ruhlar)
// Balance: Lv 1-3 starter enemy. HP 40-60, Damage 5-8, XP 10-15
export class Kormos extends Enemy {

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 28,
      height: 36,
      speed: 75,
      stats: {
        hp: 45,
        maxHp: 45,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 3,
        evasion: 0,
        strength: 6,
        agility: 4,
        spirit: 2,
      },
      name: 'Körmös',
      attackRange: 40,
      attackDamage: 6,
      attackSpeed: 0.9,
      aggroRange: 180,
      expReward: 12,
      color: '#4a3a5a', // Dark purple
    });
    this.attackElement = 'karanlik';
  }

  protected override renderBody(
    ctx: CanvasRenderingContext2D,
    z: number,
    primary: string,
    secondary: string,
    _accent: string,
    time: number
  ): void {
    // Körmös - Goblin-like dark spirit creature
    const bodyHeight = this.height * z;

    // Legs - wiggle only when moving
    const isEnemyMoving = this.velocity && (Math.abs(this.velocity.x) > 0.1 || Math.abs(this.velocity.y) > 0.1);
    const legWiggle = isEnemyMoving ? Math.sin(time * 8) * 2 : 0;
    ctx.fillStyle = secondary;
    ctx.fillRect(-10 * z + legWiggle * z, -10 * z, 6 * z, 10 * z);
    ctx.fillRect(4 * z - legWiggle * z, -10 * z, 6 * z, 10 * z);

    // Feet (clawed)
    ctx.fillStyle = '#2a1a2a';
    ctx.beginPath();
    ctx.moveTo(-12 * z + legWiggle * z, 0);
    ctx.lineTo(-4 * z + legWiggle * z, 0);
    ctx.lineTo(-8 * z + legWiggle * z, -4 * z);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(2 * z - legWiggle * z, 0);
    ctx.lineTo(10 * z - legWiggle * z, 0);
    ctx.lineTo(6 * z - legWiggle * z, -4 * z);
    ctx.closePath();
    ctx.fill();

    // Main body (hunched)
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.ellipse(0, -bodyHeight * 0.45, 14 * z, 18 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body texture/spots
    ctx.fillStyle = secondary;
    ctx.beginPath();
    ctx.arc(-5 * z, -bodyHeight * 0.5, 4 * z, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(6 * z, -bodyHeight * 0.35, 3 * z, 0, Math.PI * 2);
    ctx.fill();

    // Arms
    const armSwing = Math.sin(time * 6) * 0.2;
    ctx.fillStyle = primary;
    // Left arm
    ctx.save();
    ctx.translate(-12 * z, -bodyHeight * 0.5);
    ctx.rotate(-0.5 + armSwing);
    ctx.fillRect(-3 * z, 0, 6 * z, 16 * z);
    // Claw
    ctx.fillStyle = '#2a1a2a';
    ctx.beginPath();
    ctx.moveTo(-4 * z, 16 * z);
    ctx.lineTo(0, 22 * z);
    ctx.lineTo(4 * z, 16 * z);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Right arm
    ctx.save();
    ctx.translate(12 * z, -bodyHeight * 0.5);
    ctx.rotate(0.5 - armSwing);
    ctx.fillStyle = primary;
    ctx.fillRect(-3 * z, 0, 6 * z, 16 * z);
    // Claw
    ctx.fillStyle = '#2a1a2a';
    ctx.beginPath();
    ctx.moveTo(-4 * z, 16 * z);
    ctx.lineTo(0, 22 * z);
    ctx.lineTo(4 * z, 16 * z);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Head
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.ellipse(0, -bodyHeight * 0.75, 10 * z, 12 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Pointy ears
    ctx.fillStyle = primary;
    ctx.beginPath();
    ctx.moveTo(-8 * z, -bodyHeight * 0.8);
    ctx.lineTo(-16 * z, -bodyHeight * 1.0);
    ctx.lineTo(-6 * z, -bodyHeight * 0.7);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(8 * z, -bodyHeight * 0.8);
    ctx.lineTo(16 * z, -bodyHeight * 1.0);
    ctx.lineTo(6 * z, -bodyHeight * 0.7);
    ctx.closePath();
    ctx.fill();

    // Eyes (glowing red)
    const eyeGlow = 0.5 + Math.sin(time * 4) * 0.3;
    ctx.fillStyle = `rgba(255, 50, 50, ${eyeGlow})`;
    ctx.beginPath();
    ctx.arc(-4 * z, -bodyHeight * 0.78, 4 * z, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(4 * z, -bodyHeight * 0.78, 4 * z, 0, Math.PI * 2);
    ctx.fill();

    // Eye pupils
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.arc(-4 * z, -bodyHeight * 0.78, 2 * z, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(4 * z, -bodyHeight * 0.78, 2 * z, 0, Math.PI * 2);
    ctx.fill();

    // Mouth with fangs
    ctx.strokeStyle = '#1a0a1a';
    ctx.lineWidth = 2 * z;
    ctx.beginPath();
    ctx.arc(0, -bodyHeight * 0.65, 6 * z, 0.3, Math.PI - 0.3);
    ctx.stroke();

    // Fangs
    ctx.fillStyle = '#ddd';
    ctx.beginPath();
    ctx.moveTo(-4 * z, -bodyHeight * 0.62);
    ctx.lineTo(-3 * z, -bodyHeight * 0.55);
    ctx.lineTo(-2 * z, -bodyHeight * 0.62);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(2 * z, -bodyHeight * 0.62);
    ctx.lineTo(3 * z, -bodyHeight * 0.55);
    ctx.lineTo(4 * z, -bodyHeight * 0.62);
    ctx.closePath();
    ctx.fill();
  }
}

// Al Körmös - Elite Körmös (daha güçlü, parıldayan)
// Balance: Lv 3-5 elite. Base HP 85, Damage 10, XP 20 (before modifier scaling)
export class AlKormos extends Enemy {
  public rarity: EnemyRarity = 'elite';
  private glowPhase: number = 0;

  // Elite modifiers
  public modifiers: EliteModifier[] = [];
  public modifierDisplay: { names: string[]; colors: string[]; descriptions: string[] };

  // Cached font and text measurements for modifier names
  private _modFontZoom: number = 0;
  private _modFont: string = '';
  private _modTextWidths: number[] = [];
  private _modTotalWidth: number = 0;

  constructor(x: number, y: number, modifierCount: number = 1) {
    // Roll modifiers first
    const modifiers = rollEliteModifiers(modifierCount);
    const modStats = calculateModifierStats(modifiers);

    const baseHp = 85;
    const baseDamage = 10;
    const baseSpeed = 85;
    const baseArmor = 8;
    const baseExp = 20;

    super({
      x,
      y,
      width: 32,
      height: 42,
      speed: baseSpeed * modStats.speedMultiplier,
      stats: {
        hp: Math.floor(baseHp * modStats.hpMultiplier),
        maxHp: Math.floor(baseHp * modStats.hpMultiplier),
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: Math.floor(baseArmor * modStats.armorMultiplier),
        evasion: 5,
        strength: 10,
        agility: 7,
        spirit: 4,
      },
      name: 'Al Körmös',
      attackRange: 50,
      attackDamage: Math.floor(baseDamage * modStats.damageMultiplier),
      attackSpeed: 1.1,
      aggroRange: 220,
      expReward: Math.floor(baseExp * modStats.expMultiplier),
      color: modifiers.length > 0 ? modifiers[0].color : '#8a4a9a',
      lootTable: LOOT_TABLES.elite,
      itemLevel: 4,
    });

    this.modifiers = modifiers;
    this.modifierDisplay = getModifierDisplay(modifiers);
    this.attackElement = 'karanlik';

    // Update name with modifiers
    if (modifiers.length > 0) {
      this.name = `${modifiers.map(m => m.name).join(' ')} Al Körmös`;
    }
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.glowPhase += deltaTime * 3;

    // Handle periodic effects
    for (const mod of this.modifiers) {
      if (mod.periodicEffect === 'regen' && this.isAlive) {
        // Regenerate 2% HP per second
        const regenAmount = this.stats.maxHp * 0.02 * deltaTime;
        this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + regenAmount);
      }
    }
  }

  // Check if has specific modifier
  hasModifier(id: string): boolean {
    return this.modifiers.some(m => m.id === id);
  }

  // Get on-hit effect if any
  getOnHitEffect(): 'slow' | 'burn' | 'shock' | 'fear' | null {
    for (const mod of this.modifiers) {
      if (mod.onHitEffect) return mod.onHitEffect;
    }
    return null;
  }

  // Get on-death effect if any
  getOnDeathEffect(): 'explode' | 'spawn' | null {
    for (const mod of this.modifiers) {
      if (mod.onDeathEffect) return mod.onDeathEffect;
    }
    return null;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Elite glow effect
    const glowIntensity = 0.3 + Math.sin(this.glowPhase) * 0.2;
    const glowRadius = 30 * camera.zoom;

    ctx.save();
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * camera.zoom / 2,
      0,
      screenPos.x, screenPos.y - this.height * camera.zoom / 2,
      glowRadius
    );
    gradient.addColorStop(0, `rgba(180, 100, 220, ${glowIntensity})`);
    gradient.addColorStop(1, 'rgba(180, 100, 220, 0)');

    ctx.beginPath();
    ctx.arc(
      screenPos.x,
      screenPos.y - this.height * camera.zoom / 2,
      glowRadius,
      0,
      Math.PI * 2
    );
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Call parent render
    super.render(renderer, camera);

    // Elite crown indicator
    ctx.save();
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    const crownY = screenPos.y - this.height * camera.zoom - 25 * camera.zoom;
    ctx.moveTo(screenPos.x - 8 * camera.zoom, crownY + 8 * camera.zoom);
    ctx.lineTo(screenPos.x - 8 * camera.zoom, crownY);
    ctx.lineTo(screenPos.x - 4 * camera.zoom, crownY + 4 * camera.zoom);
    ctx.lineTo(screenPos.x, crownY);
    ctx.lineTo(screenPos.x + 4 * camera.zoom, crownY + 4 * camera.zoom);
    ctx.lineTo(screenPos.x + 8 * camera.zoom, crownY);
    ctx.lineTo(screenPos.x + 8 * camera.zoom, crownY + 8 * camera.zoom);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Render modifier names above the health bar
    if (this.modifiers.length > 0) {
      ctx.save();

      // Cache font string and text measurements when zoom changes
      if (this._modFontZoom !== camera.zoom) {
        this._modFontZoom = camera.zoom;
        this._modFont = `bold ${10 * camera.zoom}px Arial`;
        ctx.font = this._modFont;

        const modNames = this.modifierDisplay.names;
        const spacing = 5 * camera.zoom;
        this._modTextWidths = [];
        this._modTotalWidth = 0;
        for (let i = 0; i < modNames.length; i++) {
          const w = ctx.measureText(modNames[i]).width;
          this._modTextWidths.push(w);
          this._modTotalWidth += w;
          if (i < modNames.length - 1) this._modTotalWidth += spacing;
        }
      } else {
        ctx.font = this._modFont;
      }

      ctx.textAlign = 'center';

      const modNameY = screenPos.y - this.height * camera.zoom - 35 * camera.zoom;

      const modNames = this.modifierDisplay.names;
      const modColors = this.modifierDisplay.colors;
      const spacing = 5 * camera.zoom;

      // Draw centered using cached measurements
      let currentX = screenPos.x - this._modTotalWidth / 2;
      for (let i = 0; i < modNames.length; i++) {
        const textWidth = this._modTextWidths[i];

        // Draw text shadow
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillText(modNames[i], currentX + textWidth / 2 + 1, modNameY + 1);

        // Draw colored text
        ctx.fillStyle = modColors[i];
        ctx.fillText(modNames[i], currentX + textWidth / 2, modNameY);

        currentX += textWidth + spacing;
      }

      ctx.restore();
    }
  }
}

// Karabasan - Nightmare spirit (ranged enemy)
export class Karabasan extends Enemy {
  public rarity: EnemyRarity = 'normal';
  private floatPhase: number = 0;
  private projectileCooldown: number = 0;
  private wasAttacking: boolean = false;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 24,
      height: 32,
      speed: 55,
      stats: {
        hp: 100,
        maxHp: 100,
        mana: 50,
        maxMana: 50,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 4,
        evasion: 12,
        strength: 5,
        agility: 8,
        spirit: 12,
      },
      name: 'Karabasan',
      attackRange: 200, // Ranged attack
      attackDamage: 12,
      attackSpeed: 0.8,
      aggroRange: 280,
      expReward: 28,
      color: '#2a2a4a', // Dark blue
    });
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    // Detect attack events for projectile visual
    const wasAttackingBefore = this.wasAttacking;
    super.update(deltaTime);
    this.floatPhase += deltaTime * 2;

    if (this.projectileCooldown > 0) {
      this.projectileCooldown -= deltaTime;
    }

    // Emit purple spirit projectile when attack fires
    if (this.isAttacking && !wasAttackingBefore && this.target && this.target.isAlive) {
      const ps = getParticleSystem();
      ps.emitProjectile(
        this.position.x, this.position.y,
        this.target.position.x, this.target.position.y,
        ['#6633aa', '#8844cc', '#aa66ee'],
        3
      );
      this.wasAttacking = true;
    } else if (!this.isAttacking) {
      this.wasAttacking = false;
    }
  }

  // Override chase to keep distance
  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Lost aggro
    if (distance > this.aggroRange * 1.5) {
      this.state = 'idle';
      return;
    }

    // Maintain distance - stay at range
    const idealDistance = 150;
    if (distance < idealDistance - 20) {
      // Too close, back away
      const direction = this.directionTo(this.target);
      this.move(-direction.x, -direction.y, deltaTime);
    } else if (distance > this.attackRange) {
      // Too far, get closer
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    } else {
      // In range, attack
      this.state = 'attack';
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const floatOffset = Math.sin(this.floatPhase) * 5 * camera.zoom;

    const ctx = renderer.context;

    // Ghost trail effect
    ctx.save();
    ctx.globalAlpha = 0.3;
    for (let i = 1; i <= 3; i++) {
      const trailY = screenPos.y + i * 5 * camera.zoom;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.ellipse(
        screenPos.x,
        trailY - this.height * camera.zoom / 2 + floatOffset,
        (this.width / 2 - i * 2) * camera.zoom,
        (this.height / 2 - i * 3) * camera.zoom,
        0, 0, Math.PI * 2
      );
      ctx.fill();
    }
    ctx.restore();

    // Main body (ghostly)
    ctx.save();
    ctx.globalAlpha = 0.8;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 10 * camera.zoom,
      12 * camera.zoom,
      6 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.fill();

    // Body
    const bodyY = screenPos.y - this.height * camera.zoom + floatOffset;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      bodyY + this.height * camera.zoom / 2,
      this.width / 2 * camera.zoom,
      this.height / 2 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Eyes (glowing red)
    ctx.fillStyle = '#ff4444';
    ctx.beginPath();
    ctx.arc(screenPos.x - 6 * camera.zoom, bodyY + this.height * camera.zoom / 2 - 5 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 6 * camera.zoom, bodyY + this.height * camera.zoom / 2 - 5 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // HP bar (use parent's method)
    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

// Albastı - Fast demon (aggressive melee)
// Balance: Lv 6-9. HP 120-180, Damage 15-20, XP 30-50
export class Albasti extends Enemy {
  public rarity: EnemyRarity = 'normal';
  private dashCooldown: number = 0;
  private isDashing: boolean = false;
  private dashTimer: number = 0;
  private dashDirection: Vector2 = { x: 0, y: 0 };

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 26,
      height: 34,
      speed: 110,
      stats: {
        hp: 150,
        maxHp: 150,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 6,
        evasion: 15,
        strength: 14,
        agility: 15,
        spirit: 4,
      },
      name: 'Albastı',
      attackRange: 35,
      attackDamage: 17,
      attackSpeed: 1.5,
      aggroRange: 240,
      expReward: 40,
      color: '#8a2a2a', // Dark red
    });
    this.attackElement = 'su';
  }

  update(deltaTime: number): void {
    if (this.isDashing) {
      // Dash movement
      this.position.x += this.dashDirection.x * this.speed * 4 * deltaTime;
      this.position.y += this.dashDirection.y * this.speed * 4 * deltaTime;

      this.dashTimer -= deltaTime;
      if (this.dashTimer <= 0) {
        this.isDashing = false;
      }
      return;
    }

    super.update(deltaTime);

    if (this.dashCooldown > 0) {
      this.dashCooldown -= deltaTime;
    }

    // Dash towards player when in chase and cooldown ready
    if (this.state === 'chase' && this.target && this.dashCooldown <= 0) {
      const distance = this.distanceTo(this.target);
      if (distance < 150 && distance > this.attackRange) {
        this.startDash();
      }
    }
  }

  private startDash(): void {
    if (!this.target) return;

    this.isDashing = true;
    this.dashTimer = 0.2;
    this.dashCooldown = 3;
    this.dashDirection = this.directionTo(this.target);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const z = camera.zoom;
    const time = _sharedFrameTime || Date.now() / 1000;
    const dir = this.facingDir;
    const isHit = this.hitFlashTimer > 0;

    this.applyDeathTransform(ctx, screenPos);

    // Dash trail (afterimages)
    if (this.isDashing) {
      ctx.save();
      for (let i = 1; i <= 3; i++) {
        const trailX = screenPos.x - this.dashDirection.x * i * 15 * z;
        const trailY = screenPos.y - this.dashDirection.y * i * 15 * z;
        ctx.globalAlpha = 0.4 - i * 0.12;
        ctx.fillStyle = '#aa2222';
        ctx.beginPath();
        ctx.ellipse(trailX, trailY - 16 * z, 12 * z, 18 * z, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // Shadow
    const shadowCanvas = getEnemyShadowCanvas(z);
    ctx.drawImage(shadowCanvas, screenPos.x - shadowCanvas.width / 2, screenPos.y + 3 * z - shadowCanvas.height / 2);

    ctx.save();
    ctx.translate(screenPos.x, screenPos.y);

    const color = isHit ? '#ff8888' : (this.isDashing ? '#ff4444' : this.color);
    const isMoving = this.velocity && (Math.abs(this.velocity.x) > 5 || Math.abs(this.velocity.y) > 5);
    const legWiggle = isMoving ? Math.sin(time * 10) * 3 : 0;

    // Legs (digitigrade)
    ctx.fillStyle = isHit ? '#ffaaaa' : '#5a1a2a';
    ctx.fillRect(-8 * z + legWiggle * z, -8 * z, 5 * z, 10 * z);
    ctx.fillRect(3 * z - legWiggle * z, -8 * z, 5 * z, 10 * z);

    // Body (hunched, organic shape)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(0, -20 * z, 13 * z, 16 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Belly highlight
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.beginPath();
    ctx.ellipse(0, -16 * z, 9 * z, 8 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Arms (with claws, directional)
    const armSwing = isMoving ? Math.sin(time * 8) * 0.4 : Math.sin(time * 2) * 0.1;
    ctx.fillStyle = color;
    // Leading arm (closer to facing direction)
    ctx.save();
    ctx.translate(dir * 11 * z, -22 * z);
    ctx.rotate(dir * (0.3 + armSwing));
    ctx.fillRect(-2 * z, 0, 4 * z, 14 * z);
    ctx.fillStyle = '#2a0a0a';
    ctx.beginPath();
    ctx.moveTo(-3 * z, 14 * z);
    ctx.lineTo(0, 19 * z);
    ctx.lineTo(3 * z, 14 * z);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    // Trailing arm
    ctx.fillStyle = color;
    ctx.save();
    ctx.translate(-dir * 11 * z, -22 * z);
    ctx.rotate(-dir * (0.3 - armSwing));
    ctx.fillRect(-2 * z, 0, 4 * z, 12 * z);
    ctx.fillStyle = '#2a0a0a';
    ctx.beginPath();
    ctx.moveTo(-2 * z, 12 * z);
    ctx.lineTo(0, 16 * z);
    ctx.lineTo(2 * z, 12 * z);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Head
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(dir * 2 * z, -34 * z, 9 * z, 10 * z, 0, 0, Math.PI * 2);
    ctx.fill();

    // Horns (curved)
    ctx.fillStyle = '#3a0a0a';
    ctx.beginPath();
    ctx.moveTo(-6 * z, -40 * z);
    ctx.quadraticCurveTo(-14 * z, -52 * z, -10 * z, -46 * z);
    ctx.lineTo(-4 * z, -38 * z);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(6 * z, -40 * z);
    ctx.quadraticCurveTo(14 * z, -52 * z, 10 * z, -46 * z);
    ctx.lineTo(4 * z, -38 * z);
    ctx.closePath();
    ctx.fill();

    // Eyes (glowing, directional)
    const eyeX = dir * 4 * z;
    ctx.fillStyle = isHit ? '#ffffff' : '#ffdd00';
    ctx.beginPath();
    ctx.arc(eyeX - 3 * z, -35 * z, 2 * z, 0, Math.PI * 2);
    ctx.arc(eyeX + 3 * z, -35 * z, 2 * z, 0, Math.PI * 2);
    ctx.fill();
    // Pupils
    ctx.fillStyle = '#ff2200';
    ctx.beginPath();
    ctx.arc(eyeX - 3 * z + dir * z, -35 * z, 1 * z, 0, Math.PI * 2);
    ctx.arc(eyeX + 3 * z + dir * z, -35 * z, 1 * z, 0, Math.PI * 2);
    ctx.fill();

    // Dash glow effect
    if (this.isDashing) {
      ctx.fillStyle = 'rgba(255, 50, 50, 0.3)';
      ctx.beginPath();
      ctx.ellipse(0, -20 * z, 18 * z, 22 * z, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();

    // HP bar (using base system)
    if (!this.isDying && (Enemy.alwaysShowHP || this.getHealthPercent() < 1)) {
      this.renderHealthBar(renderer, screenPos, z);
    }

    this.restoreDeathTransform(ctx);
  }
}

// Erlik Muhafızı - First Boss
export class ErlikGuard extends Enemy {
  public rarity: EnemyRarity = 'boss';
  private phase: number = 1;
  private phaseTransitioned: boolean = false;
  private phaseTimer: number = 0;
  private specialCooldown: number = 0;
  private summonCooldown: number = 10;
  private auraPhase: number = 0;
  private slamEffect: { active: boolean; progress: number; x: number; y: number } = {
    active: false,
    progress: 0,
    x: 0,
    y: 0
  };

  // Phase 2: Çığlık (Scream) AoE shockwave
  private ciglikCooldown: number = 0;
  private ciglikActive: boolean = false;
  private ciglikTimer: number = 0;

  // Enrage timer (5 minutes)
  private enrageTimer: number = 300;
  private isEnraged: boolean = false;

  // Phase transition invulnerability
  private invulnerableTimer: number = 0;

  // Red tint overlay for phase 2
  private redTintAlpha: number = 0;

  // Screen shake request (read by GameWorld)
  public screenShakeRequest: number = 0;

  // Base values for enrage calculation
  private readonly baseAttackDamage: number = 35;
  private readonly baseSpeed: number = 70;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 48,
      height: 64,
      speed: 70,
      stats: {
        hp: 1000,
        maxHp: 1000,
        mana: 100,
        maxMana: 100,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 30,
        evasion: 5,
        strength: 30,
        agility: 10,
        spirit: 20,
      },
      name: 'Erlik Muhafızı',
      attackRange: 60,
      attackDamage: 35,
      attackSpeed: 0.8,
      aggroRange: 400,
      expReward: 500,
      color: '#1a1a2a', // Very dark blue/black
      lootTable: LOOT_TABLES.boss,
      itemLevel: 10,
    });
    this.attackElement = 'karanlik';
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    // Phase transition invulnerability
    if (this.invulnerableTimer > 0) return 0;
    return super.takeDamage(amount, type, bypassES);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.auraPhase += deltaTime * 2;

    // Invulnerability timer
    if (this.invulnerableTimer > 0) {
      this.invulnerableTimer -= deltaTime;
    }

    // Enrage timer countdown
    if (!this.isEnraged && this.isAlive && !this.isDying) {
      this.enrageTimer -= deltaTime;
      if (this.enrageTimer <= 0) {
        this.isEnraged = true;
        this.enrageTimer = 0;
        this.attackDamage = this.baseAttackDamage * 2; // +100% damage
        this.speed = this.baseSpeed * 1.5; // +50% speed
      }
    }

    // Update slam effect
    if (this.slamEffect.active) {
      this.slamEffect.progress += deltaTime * 3;
      if (this.slamEffect.progress >= 1) {
        this.slamEffect.active = false;
      }
    }

    // Update Çığlık effect
    if (this.ciglikActive) {
      this.ciglikTimer -= deltaTime;
      if (this.ciglikTimer <= 0) {
        this.ciglikActive = false;
      }
    }

    // Reduce screen shake request
    if (this.screenShakeRequest > 0) {
      this.screenShakeRequest -= deltaTime;
    }

    // Phase transitions based on HP
    const healthPercent = this.getHealthPercent();
    if (healthPercent < 0.3 && this.phase < 3) {
      this.phase = 3;
      if (!this.isEnraged) {
        this.speed = 100; // Enraged speed
      }
    } else if (healthPercent < 0.4 && this.phase < 2 && !this.phaseTransitioned) {
      this.phase = 2;
      this.phaseTransitioned = true;
      this.onPhaseTransition(2);
    } else if (healthPercent < 0.6 && this.phase < 2) {
      // Keep original phase 2 trigger at 60% if not yet transitioned
      if (!this.phaseTransitioned) {
        this.phase = 2;
        this.phaseTransitioned = true;
        this.onPhaseTransition(2);
      }
    }

    this.phaseTimer += deltaTime;

    if (this.specialCooldown > 0) {
      this.specialCooldown -= deltaTime;
    }

    if (this.summonCooldown > 0) {
      this.summonCooldown -= deltaTime;
    }

    if (this.ciglikCooldown > 0) {
      this.ciglikCooldown -= deltaTime;
    }

    // Special attack in phases 2 and 3
    if (this.phase >= 2 && this.specialCooldown <= 0 && this.state === 'attack') {
      this.performGroundSlam();
      this.specialCooldown = this.phase === 3 ? 4 : 6;
    }

    // Phase 2+: Çığlık AoE shockwave attack
    if (this.phase >= 2 && this.ciglikCooldown <= 0 && this.state === 'attack') {
      this.performCiglik();
      this.ciglikCooldown = this.phase === 3 ? 6 : 8;
    }

    // Phase 2+: Attack speed +50% applied via attackSpeed
    if (this.phase >= 2) {
      this.attackSpeed = 1.2; // Base 0.8 * 1.5 = 1.2
    }

    // Update red tint for phase 2+
    if (this.phase >= 2) {
      this.redTintAlpha = 0.15 + Math.sin(this.auraPhase * 3) * 0.1;
    }
  }

  private onPhaseTransition(_newPhase: number): void {
    // Screen shake effect
    this.screenShakeRequest = 0.5;

    // Brief invulnerability
    this.invulnerableTimer = 1.0;

    // Phase 2: increase speed
    if (!this.isEnraged) {
      this.speed = 85;
    }

    // Particle burst for transition
    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 30,
        color: ['#ff3333', '#ff6666', '#aa0000'],
        speed: { min: 100, max: 300 },
        size: { min: 3, max: 7 },
        life: { min: 0.5, max: 1.0 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 50,
        friction: 1,
        alphaDecay: 1.5,
        sizeDecay: 2,
      });
    }
  }

  private performGroundSlam(): void {
    if (!this.target) return;

    // Trigger slam effect
    this.slamEffect = {
      active: true,
      progress: 0,
      x: this.position.x,
      y: this.position.y
    };

    // Deal AoE damage
    const distance = this.distanceTo(this.target);
    if (distance <= 150) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.5), this.attackElement);
    }
  }

  /**
   * Çığlık (Scream) - AoE shockwave around self (150px radius, high damage)
   * Available in Phase 2+
   */
  private performCiglik(): void {
    if (!this.target) return;

    this.ciglikActive = true;
    this.ciglikTimer = 0.8;

    // Screen shake on ciglik
    this.screenShakeRequest = 0.3;

    // Deal high AoE damage in 150px radius
    const distance = this.distanceTo(this.target);
    if (distance <= 150) {
      this.target.takeDamage(Math.floor(this.attackDamage * 2.0), this.attackElement);
    }

    // Particle burst
    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 20,
        color: ['#ff4444', '#ff8888', '#ffffff'],
        speed: { min: 150, max: 350 },
        size: { min: 2, max: 5 },
        life: { min: 0.3, max: 0.6 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 0,
        friction: 3,
        alphaDecay: 2,
        sizeDecay: 3,
      });
    }
  }

  // This method will be called by GameWorld to check if boss wants to summon
  shouldSummon(): boolean {
    if (this.summonCooldown <= 0 && this.phase >= 2 && this.isAlive) {
      this.summonCooldown = 15;
      return true;
    }
    return false;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Çığlık shockwave effect
    if (this.ciglikActive) {
      ctx.save();
      const ciglikProgress = 1 - this.ciglikTimer / 0.8;
      const ciglikRadius = ciglikProgress * 150 * camera.zoom;
      const ciglikAlpha = 1 - ciglikProgress;

      ctx.strokeStyle = `rgba(255, 50, 50, ${ciglikAlpha})`;
      ctx.lineWidth = 6 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2, ciglikRadius, 0, Math.PI * 2);
      ctx.stroke();

      ctx.fillStyle = `rgba(255, 100, 100, ${ciglikAlpha * 0.2})`;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2, ciglikRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Boss aura
    ctx.save();
    const auraRadius = (60 + Math.sin(this.auraPhase) * 10) * camera.zoom;
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * camera.zoom / 2,
      0,
      screenPos.x, screenPos.y - this.height * camera.zoom / 2,
      auraRadius
    );

    const auraColor = this.isEnraged ? 'rgba(255, 0, 0,' : this.phase === 3 ? 'rgba(255, 50, 50,' : this.phase === 2 ? 'rgba(150, 50, 150,' : 'rgba(50, 50, 150,';
    gradient.addColorStop(0, auraColor + '0.3)');
    gradient.addColorStop(1, auraColor + '0)');

    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2, auraRadius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Ground slam effect
    if (this.slamEffect.active) {
      ctx.save();
      const slamScreenPos = camera.worldToScreen(this.slamEffect.x, this.slamEffect.y);
      const slamRadius = this.slamEffect.progress * 150 * camera.zoom;
      const slamAlpha = 1 - this.slamEffect.progress;

      ctx.beginPath();
      ctx.arc(slamScreenPos.x, slamScreenPos.y, slamRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 100, 50, ${slamAlpha})`;
      ctx.lineWidth = 4 * camera.zoom;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(slamScreenPos.x, slamScreenPos.y, slamRadius * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 150, 50, ${slamAlpha * 0.3})`;
      ctx.fill();
      ctx.restore();
    }

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 20 * camera.zoom,
      30 * camera.zoom,
      15 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();

    // Red tint overlay for phase 2+
    if (this.phase >= 2) {
      ctx.save();
      ctx.globalAlpha = this.redTintAlpha;
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(
        screenPos.x - this.width / 2 * camera.zoom,
        screenPos.y - this.height * camera.zoom,
        this.width * camera.zoom,
        this.height * camera.zoom
      );
      ctx.restore();
    }

    // Body
    const bodyColor = this.isEnraged ? '#4a0000' : this.phase === 3 ? '#3a1a1a' : this.phase === 2 ? '#2a1a2a' : this.color;
    renderer.drawRect(
      screenPos.x - this.width / 2 * camera.zoom,
      screenPos.y - this.height * camera.zoom,
      this.width * camera.zoom,
      this.height * camera.zoom,
      { fillColor: bodyColor }
    );

    // Armor details
    ctx.fillStyle = '#333344';
    ctx.fillRect(
      screenPos.x - this.width / 2 * camera.zoom + 4 * camera.zoom,
      screenPos.y - this.height * camera.zoom + 10 * camera.zoom,
      (this.width - 8) * camera.zoom,
      20 * camera.zoom
    );

    // Helmet
    ctx.fillStyle = '#222233';
    const helmetY = screenPos.y - this.height * camera.zoom - 5 * camera.zoom;
    ctx.fillRect(
      screenPos.x - 15 * camera.zoom,
      helmetY,
      30 * camera.zoom,
      15 * camera.zoom
    );

    // Glowing eyes
    const eyeGlow = 0.5 + Math.sin(this.auraPhase * 2) * 0.5;
    ctx.fillStyle = this.isEnraged ? `rgba(255, 0, 0, ${eyeGlow})` : this.phase === 3 ? `rgba(255, 50, 50, ${eyeGlow})` : `rgba(255, 200, 50, ${eyeGlow})`;
    ctx.beginPath();
    ctx.arc(screenPos.x - 6 * camera.zoom, helmetY + 8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 6 * camera.zoom, helmetY + 8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Enrage pulsing aura
    if (this.isEnraged) {
      ctx.save();
      const enragePulse = 0.3 + Math.sin(this.auraPhase * 4) * 0.2;
      ctx.globalAlpha = enragePulse;
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2, 50 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Invulnerability shield visual
    if (this.invulnerableTimer > 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2, 40 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Boss HP bar (larger)
    const barWidth = 80 * camera.zoom;
    const barHeight = 10 * camera.zoom;
    const barY = screenPos.y - this.height * camera.zoom - 30 * camera.zoom;

    // Bar background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(screenPos.x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);

    // HP
    const healthPercent = this.getHealthPercent();
    const healthColor = this.isEnraged ? '#ff0000' : this.phase === 3 ? '#ff4444' : this.phase === 2 ? '#aa44aa' : '#4444aa';
    ctx.fillStyle = '#333333';
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);
    ctx.fillStyle = healthColor;
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth * healthPercent, barHeight);

    // Border
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.strokeRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);

    // Boss name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${14 * camera.zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(this.name, screenPos.x, barY - 5 * camera.zoom);

    // Phase indicator
    ctx.fillStyle = '#ffffff';
    ctx.font = `${10 * camera.zoom}px Arial`;
    ctx.fillText(`Faz ${this.phase}`, screenPos.x, barY + barHeight + 12 * camera.zoom);

    // Enrage timer display
    if (!this.isEnraged) {
      const minutes = Math.floor(this.enrageTimer / 60);
      const seconds = Math.floor(this.enrageTimer % 60);
      const timerColor = this.enrageTimer < 30 ? '#ff4444' : '#aaaaaa';
      ctx.fillStyle = timerColor;
      ctx.font = `${9 * camera.zoom}px Arial`;
      ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, screenPos.x, barY + barHeight + 24 * camera.zoom);
    } else {
      ctx.fillStyle = '#ff0000';
      ctx.font = `bold ${10 * camera.zoom}px Arial`;
      ctx.fillText('GAZAP!', screenPos.x, barY + barHeight + 24 * camera.zoom);
    }
  }
}

// Karakura - Wolf pack enemy (fast, attacks in groups)
export class Karakura extends Enemy {
  public rarity: EnemyRarity = 'normal';
  private howlCooldown: number = 0;
  private isHowling: boolean = false;
  private howlTimer: number = 0;
  private runPhase: number = 0;
  private packCheckTimer: number = 0;
  public hasPackBonus: boolean = false;
  private baseAttackDamage: number = 12;
  private baseSpeed: number = 140;

  // Static registry for pack detection
  static activeKarakuras: Karakura[] = [];

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 30,
      height: 24,
      speed: 140, // Very fast
      stats: {
        hp: 30,
        maxHp: 30,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 2,
        evasion: 25,
        strength: 10,
        agility: 18,
        spirit: 2,
      },
      name: 'Karakura',
      attackRange: 35,
      attackDamage: 12,
      attackSpeed: 2.0, // Fast attacks
      aggroRange: 300,
      expReward: 18,
      color: '#3a3a3a', // Dark gray
    });
    this.attackElement = 'physical';
    Karakura.activeKarakuras.push(this);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.runPhase += deltaTime * 8;

    if (this.howlCooldown > 0) {
      this.howlCooldown -= deltaTime;
    }

    // Howl when seeing target for first time
    if (this.state === 'chase' && this.howlCooldown <= 0 && !this.isHowling) {
      this.isHowling = true;
      this.howlCooldown = 10;
      this.howlTimer = 0.5;
    }

    // Howl animation timer (replaces setTimeout for GC safety)
    if (this.isHowling && this.howlTimer > 0) {
      this.howlTimer -= deltaTime;
      if (this.howlTimer <= 0) {
        this.isHowling = false;
      }
    }

    // Pack bonus check (every ~1 second)
    this.packCheckTimer += deltaTime;
    if (this.packCheckTimer >= 1.0) {
      this.packCheckTimer = 0;
      this.updatePackBonus();
    }

    // Remove from registry when dead
    if (!this.isAlive) {
      const idx = Karakura.activeKarakuras.indexOf(this);
      if (idx !== -1) Karakura.activeKarakuras.splice(idx, 1);
    }
  }

  private updatePackBonus(): void {
    let nearbyCount = 0;
    for (const other of Karakura.activeKarakuras) {
      if (other === this || !other.isAlive) continue;
      const dx = other.position.x - this.position.x;
      const dy = other.position.y - this.position.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 150) {
        nearbyCount++;
      }
    }

    if (nearbyCount >= 2) {
      // Apply pack bonus: +15% damage, +10% speed
      this.hasPackBonus = true;
      this.attackDamage = Math.floor(this.baseAttackDamage * 1.15);
      this.speed = Math.floor(this.baseSpeed * 1.10);
    } else {
      this.hasPackBonus = false;
      this.attackDamage = this.baseAttackDamage;
      this.speed = this.baseSpeed;
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Bob only when actually moving, not idle
    const isWolfMoving = this.velocity && (Math.abs(this.velocity.x) > 0.1 || Math.abs(this.velocity.y) > 0.1);
    const bobOffset = isWolfMoving ? Math.sin(this.runPhase) * 3 * camera.zoom : 0;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 8 * camera.zoom,
      12 * camera.zoom,
      5 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Pack bonus glow aura
    if (this.hasPackBonus) {
      ctx.save();
      ctx.globalAlpha = 0.3 + Math.sin(this.runPhase * 0.5) * 0.15;
      ctx.fillStyle = '#ff2200';
      ctx.beginPath();
      ctx.ellipse(
        screenPos.x,
        screenPos.y - this.height / 2 * camera.zoom + bobOffset,
        (this.width / 2 + 6) * camera.zoom,
        (this.height / 2 + 4) * camera.zoom,
        0, 0, Math.PI * 2
      );
      ctx.fill();
      ctx.restore();
    }

    // Wolf body (horizontal oval)
    ctx.fillStyle = this.hasPackBonus ? '#4a2a2a' : this.color;
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y - this.height / 2 * camera.zoom + bobOffset,
      this.width / 2 * camera.zoom,
      this.height / 2 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Head
    const headX = screenPos.x + getFacingMult8Dir(this.facing) * 15 * camera.zoom;
    ctx.beginPath();
    ctx.ellipse(
      headX,
      screenPos.y - this.height / 2 * camera.zoom + bobOffset - 5 * camera.zoom,
      10 * camera.zoom,
      8 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Ears
    const earDir = getFacingMult8Dir(this.facing);
    ctx.beginPath();
    ctx.moveTo(headX - 5 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom + bobOffset);
    ctx.lineTo(headX - 8 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom - 10 * camera.zoom + bobOffset);
    ctx.lineTo(headX - 2 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom + bobOffset);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(headX + 2 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom + bobOffset);
    ctx.lineTo(headX + 5 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom - 10 * camera.zoom + bobOffset);
    ctx.lineTo(headX + 8 * earDir * camera.zoom, screenPos.y - this.height * camera.zoom + bobOffset);
    ctx.fill();

    // Eyes (glowing red, brighter with pack bonus)
    ctx.fillStyle = this.isHowling ? '#ffff00' : (this.hasPackBonus ? '#ff4400' : '#ff0000');
    const eyeSize = (this.hasPackBonus ? 3 : 2) * camera.zoom;
    ctx.beginPath();
    ctx.arc(headX + 3 * earDir * camera.zoom, screenPos.y - this.height / 2 * camera.zoom + bobOffset - 6 * camera.zoom, eyeSize, 0, Math.PI * 2);
    ctx.fill();

    // Tail
    const tailPhase = Math.sin(this.runPhase * 0.5) * 0.3;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 4 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 15 * earDir * camera.zoom, screenPos.y - this.height / 2 * camera.zoom + bobOffset);
    ctx.quadraticCurveTo(
      screenPos.x - 25 * earDir * camera.zoom,
      screenPos.y - this.height / 2 * camera.zoom + bobOffset + tailPhase * 20 * camera.zoom,
      screenPos.x - 30 * earDir * camera.zoom,
      screenPos.y - this.height / 2 * camera.zoom + bobOffset - 10 * camera.zoom
    );
    ctx.stroke();

    // Howl indicator
    if (this.isHowling) {
      ctx.strokeStyle = '#ffff44';
      ctx.lineWidth = 2;
      for (let i = 1; i <= 3; i++) {
        ctx.beginPath();
        ctx.arc(headX, screenPos.y - this.height * camera.zoom - 15 * camera.zoom, i * 8 * camera.zoom, -Math.PI * 0.8, -Math.PI * 0.2);
        ctx.stroke();
      }
    }

    // HP bar
    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

// Şulbus - Ambush spirit (stealthy, surprise attacks)
export class Sulbus extends Enemy {
  public rarity: EnemyRarity = 'normal';
  private isHidden: boolean = true;
  private revealTimer: number = 0;
  private ambushCooldown: number = 0;
  private fadeAlpha: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 26,
      height: 38,
      speed: 90,
      stats: {
        hp: 45,
        maxHp: 45,
        mana: 20,
        maxMana: 20,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 5,
        evasion: 30,
        strength: 14,
        agility: 12,
        spirit: 8,
      },
      name: 'Şulbus',
      attackRange: 40,
      attackDamage: 25, // High burst damage
      attackSpeed: 0.8,
      aggroRange: 150, // Short aggro range (ambush)
      expReward: 30,
      color: '#2a4a2a', // Dark green
    });
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    // Custom update for stealth behavior
    if (this.isDying) {
      this.deathTimer -= deltaTime;
      const progress = 1 - (this.deathTimer / this.deathDuration);
      this.deathScale = 1.0 - progress * 0.7;
      this.deathAlpha = 1.0 - progress;
      this.deathRotation = progress * 1.5;

      if (this.deathTimer <= 0) {
        this.isAlive = false;
        this.isDying = false;
        this.velocity = { x: 0, y: 0 };
      }
      return;
    }

    if (!this.isAlive) {
      this.state = 'dead';
      return;
    }

    this.updateTimers(deltaTime);

    if (this.ambushCooldown > 0) {
      this.ambushCooldown -= deltaTime;
    }

    // Check for player proximity while hidden
    if (this.isHidden && this.target && this.target.isAlive) {
      const distance = this.distanceTo(this.target);
      if (distance <= this.aggroRange) {
        // Reveal and ambush!
        this.isHidden = false;
        this.revealTimer = 0.5;
        this.state = 'attack'; // Immediate attack
      }
    }

    // Fade in effect
    if (!this.isHidden && this.fadeAlpha < 1) {
      this.fadeAlpha += deltaTime * 3;
      if (this.fadeAlpha > 1) this.fadeAlpha = 1;
    }

    // Reveal animation
    if (this.revealTimer > 0) {
      this.revealTimer -= deltaTime;
      return; // Don't move during reveal
    }

    // Normal AI after reveal
    if (!this.isHidden) {
      switch (this.state) {
        case 'idle':
          // Try to hide again if far from player
          if (this.target && this.distanceTo(this.target) > this.aggroRange * 2 && this.ambushCooldown <= 0) {
            this.isHidden = true;
            this.fadeAlpha = 0;
            this.ambushCooldown = 5;
          }
          break;
        case 'chase':
          this.updateChase(deltaTime);
          break;
        case 'attack':
          this.updateAttackState(deltaTime);
          break;
      }
    }
  }

  private updateAttackState(_deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    if (distance > this.attackRange * 1.2) {
      this.state = 'chase';
      return;
    }

    // Face target (8-directional)
    this.facing = getFacing8Dir(
      this.target.position.x - this.position.x,
      this.target.position.y - this.position.y
    );

    // Attack when cooldown ready
    if (this.attackCooldown <= 0) {
      this.isAttacking = true;
      this.attackCooldown = 1 / this.attackSpeed;

      if (distance <= this.attackRange && this.target) {
        this.target.takeDamage(this.attackDamage, this.attackElement);
      }
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    // Don't render if fully hidden
    if (this.isHidden && this.fadeAlpha <= 0) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    ctx.save();
    ctx.globalAlpha = this.isHidden ? 0.2 : this.fadeAlpha;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 12 * camera.zoom,
      14 * camera.zoom,
      7 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Body - shadowy figure
    const bodyGradient = ctx.createLinearGradient(
      screenPos.x,
      screenPos.y - this.height * camera.zoom,
      screenPos.x,
      screenPos.y
    );
    bodyGradient.addColorStop(0, this.color);
    bodyGradient.addColorStop(1, '#000000');

    ctx.fillStyle = bodyGradient;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - this.width / 2 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x - this.width / 3 * camera.zoom, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x + this.width / 3 * camera.zoom, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x + this.width / 2 * camera.zoom, screenPos.y);
    ctx.closePath();
    ctx.fill();

    // Hood
    ctx.fillStyle = '#1a2a1a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom + 8 * camera.zoom, 12 * camera.zoom, Math.PI, 0);
    ctx.fill();

    // Glowing eyes (with manual glow instead of expensive shadowBlur)
    if (!this.isHidden) {
      const eyeY = screenPos.y - this.height * camera.zoom + 12 * camera.zoom;
      // Glow halo
      ctx.fillStyle = 'rgba(136, 255, 136, 0.2)';
      ctx.beginPath();
      ctx.arc(screenPos.x - 4 * camera.zoom, eyeY, 6 * camera.zoom, 0, Math.PI * 2);
      ctx.arc(screenPos.x + 4 * camera.zoom, eyeY, 6 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      // Eye core
      ctx.fillStyle = '#88ff88';
      ctx.beginPath();
      ctx.arc(screenPos.x - 4 * camera.zoom, eyeY, 2 * camera.zoom, 0, Math.PI * 2);
      ctx.arc(screenPos.x + 4 * camera.zoom, eyeY, 2 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
    }

    // Reveal effect
    if (this.revealTimer > 0) {
      const progress = 1 - this.revealTimer / 0.5;
      ctx.strokeStyle = `rgba(136, 255, 136, ${1 - progress})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height / 2 * camera.zoom, progress * 40 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();

    // HP bar (only when visible)
    if (!this.isHidden) {
      this.renderHealthBar(renderer, screenPos, camera.zoom);
    }
  }
}

// Erlik Okçusu - Archer enemy (ranged, keeps distance)
export class ErlikOkcusu extends Enemy {
  public rarity: EnemyRarity = 'normal';
  private arrowCooldown: number = 0;
  private aimPhase: number = 0;
  private isAiming: boolean = false;
  private wasAttackingArrow: boolean = false;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 24,
      height: 40,
      speed: 70,
      stats: {
        hp: 35,
        maxHp: 35,
        mana: 30,
        maxMana: 30,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 3,
        evasion: 15,
        strength: 8,
        agility: 14,
        spirit: 6,
      },
      name: 'Erlik Okçusu',
      attackRange: 250, // Long range
      attackDamage: 18,
      attackSpeed: 0.7,
      aggroRange: 350,
      expReward: 22,
      color: '#4a2a4a', // Dark purple
    });
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    const wasAttBefore = this.wasAttackingArrow;
    super.update(deltaTime);

    if (this.arrowCooldown > 0) {
      this.arrowCooldown -= deltaTime;
    }

    // Aim animation
    if (this.isAiming) {
      this.aimPhase += deltaTime;
      if (this.aimPhase >= 0.5) {
        this.isAiming = false;
        this.aimPhase = 0;
      }
    }

    // Emit red arrow trail when attack fires
    if (this.isAttacking && !wasAttBefore && this.target && this.target.isAlive) {
      const ps = getParticleSystem();
      ps.emitProjectile(
        this.position.x, this.position.y,
        this.target.position.x, this.target.position.y,
        ['#cc2222', '#ff4444', '#ff6644'],
        2
      );
      this.wasAttackingArrow = true;
    } else if (!this.isAttacking) {
      this.wasAttackingArrow = false;
    }
  }

  // Override chase to maintain distance
  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Lost aggro
    if (distance > this.aggroRange * 1.5) {
      this.state = 'idle';
      return;
    }

    // Too close - back away
    const idealDistance = 180;
    if (distance < idealDistance - 30) {
      const direction = this.directionTo(this.target);
      this.move(-direction.x, -direction.y, deltaTime);
    } else if (distance > this.attackRange) {
      // Too far - get closer
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    } else {
      // In range - attack
      this.state = 'attack';
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 15 * camera.zoom,
      12 * camera.zoom,
      6 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Body
    ctx.fillStyle = this.color;
    ctx.fillRect(
      screenPos.x - this.width / 2 * camera.zoom,
      screenPos.y - this.height * camera.zoom,
      this.width * camera.zoom,
      this.height * camera.zoom
    );

    // Cloak
    ctx.fillStyle = '#3a1a3a';
    ctx.beginPath();
    ctx.moveTo(screenPos.x - this.width / 2 * camera.zoom - 5 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x, screenPos.y - this.height * 0.7 * camera.zoom);
    ctx.lineTo(screenPos.x + this.width / 2 * camera.zoom + 5 * camera.zoom, screenPos.y);
    ctx.closePath();
    ctx.fill();

    // Hood
    ctx.fillStyle = '#2a1a2a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom + 10 * camera.zoom, 10 * camera.zoom, Math.PI, 0);
    ctx.fill();

    // Eyes (glowing purple)
    ctx.fillStyle = '#aa44ff';
    ctx.beginPath();
    ctx.arc(screenPos.x - 3 * camera.zoom, screenPos.y - this.height * camera.zoom + 14 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 3 * camera.zoom, screenPos.y - this.height * camera.zoom + 14 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Bow
    const bowX = screenPos.x + getFacingMult8Dir(this.facing) * 15 * camera.zoom;
    const bowY = screenPos.y - this.height / 2 * camera.zoom;

    ctx.strokeStyle = '#8B4513';
    ctx.lineWidth = 3 * camera.zoom;
    ctx.beginPath();
    ctx.arc(bowX, bowY, 15 * camera.zoom, -Math.PI / 3, Math.PI / 3);
    ctx.stroke();

    // Bowstring
    ctx.strokeStyle = '#aaaaaa';
    ctx.lineWidth = 1 * camera.zoom;
    ctx.beginPath();
    const stringPull = this.isAiming ? 5 * camera.zoom : 0;
    ctx.moveTo(bowX + Math.cos(-Math.PI / 3) * 15 * camera.zoom, bowY + Math.sin(-Math.PI / 3) * 15 * camera.zoom);
    ctx.lineTo(bowX - stringPull, bowY);
    ctx.lineTo(bowX + Math.cos(Math.PI / 3) * 15 * camera.zoom, bowY + Math.sin(Math.PI / 3) * 15 * camera.zoom);
    ctx.stroke();

    // Arrow (when aiming)
    if (this.isAiming) {
      ctx.strokeStyle = '#654321';
      ctx.lineWidth = 2 * camera.zoom;
      const arrowDir = getFacingMult8Dir(this.facing);
      ctx.beginPath();
      ctx.moveTo(bowX - stringPull, bowY);
      ctx.lineTo(bowX + arrowDir * 20 * camera.zoom, bowY);
      ctx.stroke();

      // Arrowhead
      ctx.fillStyle = '#888888';
      ctx.beginPath();
      ctx.moveTo(bowX + arrowDir * 20 * camera.zoom, bowY);
      ctx.lineTo(bowX + arrowDir * 25 * camera.zoom, bowY - 3 * camera.zoom);
      ctx.lineTo(bowX + arrowDir * 25 * camera.zoom, bowY + 3 * camera.zoom);
      ctx.fill();
    }

    // HP bar
    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

// Albastı Kraliçesi - Act 1 Final Boss
export class AlbastiKralicesi extends Enemy {
  public rarity: EnemyRarity = 'boss';
  private phase: number = 1;
  private phaseTransitioned2: boolean = false;
  private phaseTimer: number = 0;
  private specialCooldown: number = 0;
  private summonCooldown: number = 15;
  private floatPhase: number = 0;
  private wingPhase: number = 0;

  // Special attack states
  private isScreaming: boolean = false;
  private screamTimer: number = 0;
  private screamPrepActive: boolean = false;
  private screamPrepTimer: number = 0;
  private bloodRainActive: boolean = false;
  private bloodRainTimer: number = 0;
  private bloodRainTickTimer: number = 0;
  private bloodRainTelegraphActive: boolean = false;
  private bloodRainTelegraphTimer: number = 0;
  private teleportCooldown: number = 0;
  private isTeleporting: boolean = false;
  private teleportFlashTimer: number = 0;
  private phaseTransitionFlash: number = 0;

  // Phase 2: Buz Fırtınası (Ice Storm) - spiral ice projectiles
  private buzFirtinaCooldown: number = 0;
  private buzFirtinaActive: boolean = false;
  private buzFirtinaTimer: number = 0;
  private buzFirtinaAngle: number = 0;

  // Phase 2: Albastı minion summon timer (every 15s)
  private minionSummonTimer: number = 15;

  // Phase transition invulnerability
  private invulnerableTimer: number = 0;

  // Phase 3: Lava crack mechanic
  private lavaCracks: { x: number; y: number; timer: number }[] = [];
  private lavaCrackSpawnTimer: number = 10;

  // Enrage timer (5 minutes)
  private enrageTimer: number = 300;
  private isEnraged: boolean = false;
  private readonly baseAttackDamage: number = 30;
  private readonly baseSpeed: number = 80;

  // Screen shake request (read by GameWorld)
  public screenShakeRequest: number = 0;

  // Arena reference positions (pillar positions for shelter calculation)
  private readonly pillarPositions = [
    { x: 0, y: -80 },   // North pillar (relative to arena center)
    { x: 0, y: 80 },    // South pillar
    { x: -80, y: 0 },   // West pillar
    { x: 80, y: 0 },    // East pillar
  ];
  private arenaCenter: { x: number; y: number } = { x: 0, y: 0 };
  private arenaCenterSet: boolean = false;

  constructor(x: number, y: number) {
    // Balance: Boss Lv 10. HP 800-1000, Damage 25-35, XP 200
    super({
      x,
      y,
      width: 40,
      height: 56,
      speed: 80,
      stats: {
        hp: 900,
        maxHp: 900,
        mana: 150,
        maxMana: 150,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 15,
        evasion: 12,
        strength: 20,
        agility: 15,
        spirit: 25,
      },
      name: 'Albastı Kraliçesi',
      attackRange: 60,
      attackDamage: 30,
      attackSpeed: 1.0,
      aggroRange: 500,
      expReward: 200,
      color: '#8a1a2a', // Blood red
      lootTable: LOOT_TABLES.boss,
      itemLevel: 15,
    });
    this.attackElement = 'su';
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    if (this.invulnerableTimer > 0) return 0;
    return super.takeDamage(amount, type, bypassES);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 2;
    this.wingPhase += deltaTime * 4;
    this.phaseTimer += deltaTime;

    // Invulnerability timer
    if (this.invulnerableTimer > 0) {
      this.invulnerableTimer -= deltaTime;
    }

    // Enrage timer countdown
    if (!this.isEnraged && this.isAlive && !this.isDying) {
      this.enrageTimer -= deltaTime;
      if (this.enrageTimer <= 0) {
        this.isEnraged = true;
        this.enrageTimer = 0;
        this.attackDamage = this.baseAttackDamage * 2;
        this.speed = this.baseSpeed * 1.5;
      }
    }

    // Reduce screen shake request
    if (this.screenShakeRequest > 0) {
      this.screenShakeRequest -= deltaTime;
    }

    // Set arena center on first update (boss spawn position)
    if (!this.arenaCenterSet) {
      this.arenaCenter = { x: this.position.x, y: this.position.y };
      this.arenaCenterSet = true;
    }

    // Phase transitions
    const healthPercent = this.getHealthPercent();
    if (healthPercent < 0.3 && this.phase < 3) {
      this.phase = 3;
      if (!this.isEnraged) {
        this.speed = 100;
        this.attackDamage = 35;
      }
      this.phaseTransitionFlash = 1.0;
    } else if (healthPercent < 0.5 && this.phase < 2 && !this.phaseTransitioned2) {
      this.phase = 2;
      this.phaseTransitioned2 = true;
      this.onPhaseTransition2();
    } else if (healthPercent < 0.6 && this.phase < 2 && !this.phaseTransitioned2) {
      // Original phase 2 trigger at 60% still works
      this.phase = 2;
      this.phaseTransitioned2 = true;
      this.onPhaseTransition2();
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      this.phaseTransitionFlash -= deltaTime * 2;
    }

    // Update special attack timers
    if (this.specialCooldown > 0) this.specialCooldown -= deltaTime;
    if (this.summonCooldown > 0) this.summonCooldown -= deltaTime;
    if (this.buzFirtinaCooldown > 0) this.buzFirtinaCooldown -= deltaTime;

    // Phase 2+: Buz Fırtınası (Ice Storm) - spiral projectiles
    if (this.buzFirtinaActive) {
      this.buzFirtinaTimer -= deltaTime;
      this.buzFirtinaAngle += deltaTime * 3; // Rotating spiral
      if (this.buzFirtinaTimer <= 0) {
        this.buzFirtinaActive = false;
      }
      // Deal damage to player if in range of any spiral arm
      if (this.target) {
        const dist = this.distanceTo(this.target);
        if (dist <= 200) {
          // Check if player is hit by any of the 8 spiral arms
          const playerAngle = Math.atan2(
            this.target.position.y - this.position.y,
            this.target.position.x - this.position.x
          );
          for (let i = 0; i < 8; i++) {
            const armAngle = this.buzFirtinaAngle + (i * Math.PI / 4);
            const angleDiff = Math.abs(((playerAngle - armAngle + Math.PI) % (Math.PI * 2)) - Math.PI);
            if (angleDiff < 0.3) { // ~17 degree hit zone per arm
              this.target.takeDamage(Math.floor(12 * deltaTime), this.attackElement);
              break;
            }
          }
        }
      }
    }

    // Phase 2+: Minion summon timer (every 15s, summon 2 Albastı minions)
    if (this.phase >= 2) {
      this.minionSummonTimer -= deltaTime;
    }

    // Scream prep telegraph (expanding purple rings before damage)
    if (this.screamPrepActive) {
      this.screamPrepTimer -= deltaTime;
      if (this.screamPrepTimer <= 0) {
        this.screamPrepActive = false;
        this.executeScream();
      }
    }

    // Scream visual after-effect
    if (this.isScreaming) {
      this.screamTimer -= deltaTime;
      if (this.screamTimer <= 0) {
        this.isScreaming = false;
      }
    }

    // Blood rain telegraph (3s dark red sky warning)
    if (this.bloodRainTelegraphActive) {
      this.bloodRainTelegraphTimer -= deltaTime;
      if (this.bloodRainTelegraphTimer <= 0) {
        this.bloodRainTelegraphActive = false;
        this.executeBloodRain();
      }
    }

    // Blood rain - deal periodic damage (arena-wide, shelter behind pillars reduces damage)
    if (this.bloodRainActive) {
      this.bloodRainTimer -= deltaTime;
      this.bloodRainTickTimer -= deltaTime;
      if (this.bloodRainTickTimer <= 0 && this.target) {
        const sheltered = this.isTargetSheltered();
        const dps = 15; // 15 DPS as per design
        const tickDamage = sheltered ? Math.floor(dps * 0.5 * 0.5) : Math.floor(dps * 0.5);
        this.target.takeDamage(tickDamage, this.attackElement);
        this.bloodRainTickTimer = 0.5;
      }
      if (this.bloodRainTimer <= 0) {
        this.bloodRainActive = false;
      }
    }

    // Phase 3: Lava crack spawning
    if (this.phase === 3) {
      this.lavaCrackSpawnTimer -= deltaTime;
      if (this.lavaCrackSpawnTimer <= 0) {
        this.spawnLavaCrack();
        this.lavaCrackSpawnTimer = 10;
      }

      // Update lava cracks - deal damage and expire
      for (let i = this.lavaCracks.length - 1; i >= 0; i--) {
        this.lavaCracks[i].timer -= deltaTime;
        if (this.lavaCracks[i].timer <= 0) {
          this.lavaCracks.splice(i, 1);
          continue;
        }
        // Damage player if standing on crack
        if (this.target) {
          const dx = this.target.position.x - this.lavaCracks[i].x;
          const dy = this.target.position.y - this.lavaCracks[i].y;
          if (Math.sqrt(dx * dx + dy * dy) < 24) {
            // 10 DPS from lava, tick every frame
            this.target.takeDamage(Math.floor(10 * deltaTime), this.attackElement);
          }
        }
      }
    }

    // Teleport timer
    if (this.teleportCooldown > 0) this.teleportCooldown -= deltaTime;
    if (this.isTeleporting) {
      this.teleportFlashTimer -= deltaTime;
      if (this.teleportFlashTimer <= 0) {
        this.isTeleporting = false;
      }
    }

    // Special attacks based on phase
    if (this.state === 'attack' && this.specialCooldown <= 0) {
      if (this.phase >= 2 && !this.screamPrepActive && Math.random() < 0.3) {
        this.performScream();
        this.specialCooldown = this.phase === 3 ? 8 : 12;
      } else if (this.phase >= 2 && !this.buzFirtinaActive && this.buzFirtinaCooldown <= 0 && Math.random() < 0.2) {
        this.performBuzFirtina();
        this.specialCooldown = 5;
      } else if (this.phase === 3 && !this.bloodRainTelegraphActive && !this.bloodRainActive && Math.random() < 0.15) {
        this.startBloodRain();
        this.specialCooldown = 20;
      }
    }

    // Teleport - all phases with different cooldowns
    if (this.teleportCooldown <= 0 && this.target) {
      const distance = this.distanceTo(this.target);
      if (distance > 200) {
        this.performTeleport();
        if (this.phase === 3) this.teleportCooldown = 5;
        else if (this.phase === 2) this.teleportCooldown = 7;
        else this.teleportCooldown = 10;
      }
    }
  }

  /**
   * Phase 2 transition: Screen flash, teleport to center, brief invulnerability, speed +30%
   */
  private onPhaseTransition2(): void {
    // Screen flash
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 0.5;

    // Teleport to arena center
    if (this.arenaCenterSet) {
      this.position.x = this.arenaCenter.x;
      this.position.y = this.arenaCenter.y;
    }

    // Brief invulnerability (1 second)
    this.invulnerableTimer = 1.0;

    // Increase speed by 30%
    if (!this.isEnraged) {
      this.speed = this.baseSpeed * 1.3;
      this.attackDamage = 48;
    }

    // Particle burst
    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 40,
        color: ['#66ccff', '#aaeeff', '#ffffff'],
        speed: { min: 120, max: 350 },
        size: { min: 3, max: 8 },
        life: { min: 0.5, max: 1.2 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 30,
        friction: 1,
        alphaDecay: 1.5,
        sizeDecay: 2,
      });
    }
  }

  /**
   * Buz Fırtınası (Ice Storm) - Spiral ice projectiles outward in 8 directions, rotating
   */
  private performBuzFirtina(): void {
    this.buzFirtinaActive = true;
    this.buzFirtinaTimer = 4.0; // 4 second duration
    this.buzFirtinaCooldown = 12;
    this.buzFirtinaAngle = 0;

    // Ice burst particle effect
    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 24,
        color: ['#88ddff', '#aaeeff', '#ffffff'],
        speed: { min: 80, max: 200 },
        size: { min: 2, max: 6 },
        life: { min: 0.4, max: 0.8 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 0,
        friction: 2,
        alphaDecay: 1.5,
        sizeDecay: 2,
      });
    }
  }

  private performScream(): void {
    if (!this.target) return;
    // Start 2s prep telegraph (expanding purple rings)
    this.screamPrepActive = true;
    this.screamPrepTimer = 2.0;
  }

  private executeScream(): void {
    if (!this.target) return;
    this.isScreaming = true;
    this.screamTimer = 1.0;

    // Deal AoE damage + stun (distance-based: behind pillar = safe)
    const distance = this.distanceTo(this.target);
    const sheltered = this.isTargetSheltered();
    if (distance <= 250 && !sheltered) {
      this.target.takeDamage(40, this.attackElement);
    }
  }

  private startBloodRain(): void {
    // 3s telegraph before rain starts
    this.bloodRainTelegraphActive = true;
    this.bloodRainTelegraphTimer = 3.0;
  }

  private executeBloodRain(): void {
    this.bloodRainActive = true;
    this.bloodRainTimer = 8.0;
    this.bloodRainTickTimer = 0;
  }

  private performTeleport(): void {
    if (!this.target) return;

    // Teleport behind the player
    const angle = Math.atan2(
      this.target.position.y - this.position.y,
      this.target.position.x - this.position.x
    );
    const behindAngle = angle + Math.PI;
    const teleportDist = 60;

    this.position.x = this.target.position.x + Math.cos(behindAngle) * teleportDist;
    this.position.y = this.target.position.y + Math.sin(behindAngle) * teleportDist;

    this.isTeleporting = true;
    // Phase 3: instant teleport (very short flash), others: 0.4s flash
    this.teleportFlashTimer = this.phase === 3 ? 0.15 : 0.4;
  }

  private spawnLavaCrack(): void {
    // Spawn a random 1-tile lava crack in the arena (phase 3)
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * 120 + 30;
    this.lavaCracks.push({
      x: this.arenaCenter.x + Math.cos(angle) * dist,
      y: this.arenaCenter.y + Math.sin(angle) * dist,
      timer: 5.0,
    });
  }

  private isTargetSheltered(): boolean {
    if (!this.target) return false;
    // Check if target is behind a pillar relative to boss position
    for (const pillar of this.pillarPositions) {
      const pillarWorld = {
        x: this.arenaCenter.x + pillar.x,
        y: this.arenaCenter.y + pillar.y,
      };
      // Is pillar between boss and player?
      const bossToPlayer = {
        x: this.target.position.x - this.position.x,
        y: this.target.position.y - this.position.y,
      };
      const bossToPillar = {
        x: pillarWorld.x - this.position.x,
        y: pillarWorld.y - this.position.y,
      };
      const distToPlayer = Math.sqrt(bossToPlayer.x ** 2 + bossToPlayer.y ** 2);
      const distToPillar = Math.sqrt(bossToPillar.x ** 2 + bossToPillar.y ** 2);

      if (distToPillar < distToPlayer) {
        // Pillar is closer to boss than player - check angular alignment
        const dotProduct = (bossToPlayer.x * bossToPillar.x + bossToPlayer.y * bossToPillar.y) / (distToPlayer * distToPillar);
        if (dotProduct > 0.9) {
          // Player is behind this pillar (within ~25 degree cone)
          const playerToPillar = Math.sqrt(
            (this.target.position.x - pillarWorld.x) ** 2 +
            (this.target.position.y - pillarWorld.y) ** 2
          );
          if (playerToPillar < 48) return true;
        }
      }
    }
    return false;
  }

  getLavaCracks(): { x: number; y: number; timer: number }[] {
    return this.lavaCracks;
  }

  isBloodRainTelegraphActive(): boolean {
    return this.bloodRainTelegraphActive;
  }

  isBloodRainActive(): boolean {
    return this.bloodRainActive;
  }

  isScreamPrepActive(): boolean {
    return this.screamPrepActive;
  }

  getScreamPrepProgress(): number {
    return this.screamPrepActive ? 1 - this.screamPrepTimer / 2.0 : 0;
  }

  shouldSummon(): boolean {
    // Phase 1: summon albasti every 15s
    // Phase 2: summon karabasan every 12s + Albastı minions every 15s
    // Phase 3: no summons (full personal attack mode)
    if (this.phase === 3) return false;
    if (this.summonCooldown <= 0 && this.isAlive) {
      this.summonCooldown = this.phase === 2 ? 12 : 15;
      return true;
    }
    return false;
  }

  /**
   * Phase 2: Check if should summon Albastı minions (2 minions every 15s)
   */
  shouldSummonMinions(): boolean {
    if (this.phase >= 2 && this.minionSummonTimer <= 0 && this.isAlive) {
      this.minionSummonTimer = 15;
      return true;
    }
    return false;
  }

  getSummonType(): string {
    return this.phase === 2 ? 'karabasan' : 'albasti';
  }

  isBuzFirtinaActive(): boolean {
    return this.buzFirtinaActive;
  }

  getBuzFirtinaAngle(): number {
    return this.buzFirtinaAngle;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 8 * camera.zoom;

    // Blood rain telegraph (dark red sky overlay)
    if (this.bloodRainTelegraphActive) {
      ctx.save();
      const telegraphProgress = 1 - this.bloodRainTelegraphTimer / 3.0;
      ctx.globalAlpha = telegraphProgress * 0.4;
      ctx.fillStyle = '#440000';
      ctx.fillRect(screenPos.x - 400 * camera.zoom, screenPos.y - 300 * camera.zoom, 800 * camera.zoom, 600 * camera.zoom);
      ctx.restore();
    }

    // Blood rain effect (arena-wide)
    if (this.bloodRainActive) {
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = '#aa0000';
      for (let i = 0; i < 50; i++) {
        const rainX = screenPos.x + (Math.random() - 0.5) * 500 * camera.zoom;
        const rainY = screenPos.y + (Math.random() - 0.5) * 400 * camera.zoom;
        ctx.fillRect(rainX, rainY, 2 * camera.zoom, 12 * camera.zoom);
      }
      // Red tint overlay
      ctx.globalAlpha = 0.15;
      ctx.fillStyle = '#660000';
      ctx.fillRect(screenPos.x - 400 * camera.zoom, screenPos.y - 300 * camera.zoom, 800 * camera.zoom, 600 * camera.zoom);
      ctx.restore();
    }

    // Lava cracks (phase 3)
    for (const crack of this.lavaCracks) {
      const crackScreenX = screenPos.x + (crack.x - this.position.x) * camera.zoom;
      const crackScreenY = screenPos.y + (crack.y - this.position.y) * camera.zoom;
      ctx.save();
      const flicker = 0.6 + Math.sin(this.floatPhase * 8 + crack.x) * 0.4;
      ctx.globalAlpha = Math.min(crack.timer / 1.0, 1.0) * flicker;
      ctx.fillStyle = '#ff4400';
      ctx.beginPath();
      ctx.arc(crackScreenX, crackScreenY, 12 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffaa00';
      ctx.beginPath();
      ctx.arc(crackScreenX, crackScreenY, 6 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Scream prep telegraph (expanding purple rings)
    if (this.screamPrepActive) {
      ctx.save();
      const prepProgress = 1 - this.screamPrepTimer / 2.0;
      ctx.strokeStyle = `rgba(128, 0, 128, ${0.8 - prepProgress * 0.5})`;
      ctx.lineWidth = 3 * camera.zoom;
      for (let i = 1; i <= 3; i++) {
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, prepProgress * i * 80 * camera.zoom, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Buz Fırtınası (Ice Storm) spiral visual
    if (this.buzFirtinaActive) {
      ctx.save();
      const buzAlpha = Math.min(this.buzFirtinaTimer / 0.5, 1.0);
      ctx.globalAlpha = buzAlpha * 0.6;
      for (let i = 0; i < 8; i++) {
        const armAngle = this.buzFirtinaAngle + (i * Math.PI / 4);
        for (let d = 20; d < 200; d += 25) {
          const spiralAngle = armAngle + d * 0.01;
          const px = screenPos.x + Math.cos(spiralAngle) * d * camera.zoom;
          const py = screenPos.y - this.height / 2 * camera.zoom + floatOffset + Math.sin(spiralAngle) * d * 0.3 * camera.zoom;
          const size = (3 + d * 0.02) * camera.zoom;
          ctx.fillStyle = d % 50 < 25 ? '#88ddff' : '#aaeeff';
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // Invulnerability shield visual
    if (this.invulnerableTimer > 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2 + floatOffset, 50 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Boss aura
    ctx.save();
    const auraRadius = (70 + Math.sin(this.floatPhase) * 15) * camera.zoom;
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * camera.zoom / 2 + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * camera.zoom / 2 + floatOffset,
      auraRadius
    );

    const auraColor = this.isEnraged ? 'rgba(255, 0, 0,' : this.phase === 3 ? 'rgba(170, 0, 50,' : this.phase === 2 ? 'rgba(138, 26, 42,' : 'rgba(100, 20, 30,';
    gradient.addColorStop(0, auraColor + '0.4)');
    gradient.addColorStop(1, auraColor + '0)');

    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2 + floatOffset, auraRadius, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Enrage pulsing aura
    if (this.isEnraged) {
      ctx.save();
      const enragePulse = 0.3 + Math.sin(this.floatPhase * 4) * 0.2;
      ctx.globalAlpha = enragePulse;
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom / 2 + floatOffset, 60 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Wings
    ctx.save();
    const wingSpread = Math.sin(this.wingPhase) * 0.2;
    ctx.fillStyle = this.phase === 3 ? '#4a0015' : '#2a0010';

    // Left wing
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 10 * camera.zoom, screenPos.y - this.height * 0.7 * camera.zoom + floatOffset);
    ctx.quadraticCurveTo(
      screenPos.x - 50 * camera.zoom,
      screenPos.y - this.height * camera.zoom - 20 * camera.zoom + floatOffset,
      screenPos.x - 60 * (1 + wingSpread) * camera.zoom,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset
    );
    ctx.quadraticCurveTo(
      screenPos.x - 40 * camera.zoom,
      screenPos.y - this.height * 0.3 * camera.zoom + floatOffset,
      screenPos.x - 10 * camera.zoom,
      screenPos.y - this.height * 0.4 * camera.zoom + floatOffset
    );
    ctx.fill();

    // Right wing
    ctx.beginPath();
    ctx.moveTo(screenPos.x + 10 * camera.zoom, screenPos.y - this.height * 0.7 * camera.zoom + floatOffset);
    ctx.quadraticCurveTo(
      screenPos.x + 50 * camera.zoom,
      screenPos.y - this.height * camera.zoom - 20 * camera.zoom + floatOffset,
      screenPos.x + 60 * (1 + wingSpread) * camera.zoom,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset
    );
    ctx.quadraticCurveTo(
      screenPos.x + 40 * camera.zoom,
      screenPos.y - this.height * 0.3 * camera.zoom + floatOffset,
      screenPos.x + 10 * camera.zoom,
      screenPos.y - this.height * 0.4 * camera.zoom + floatOffset
    );
    ctx.fill();
    ctx.restore();

    // Shadow
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y + 15 * camera.zoom,
      25 * camera.zoom,
      12 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();

    // Body
    const bodyColor = this.phase === 3 ? '#6a1a2a' : this.color;
    ctx.fillStyle = bodyColor;
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y - this.height / 2 * camera.zoom + floatOffset,
      this.width / 2 * camera.zoom,
      this.height / 2 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Dress/robe flowing effect
    ctx.fillStyle = '#5a1020';
    ctx.beginPath();
    ctx.moveTo(screenPos.x - this.width / 2 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom + floatOffset);
    ctx.quadraticCurveTo(
      screenPos.x,
      screenPos.y + 10 * camera.zoom + floatOffset,
      screenPos.x + this.width / 2 * camera.zoom,
      screenPos.y - this.height * 0.3 * camera.zoom + floatOffset
    );
    ctx.fill();

    // Head
    ctx.fillStyle = '#d4a4a4';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom + 5 * camera.zoom + floatOffset, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Hair
    ctx.fillStyle = '#1a0a0a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * camera.zoom + 2 * camera.zoom + floatOffset, 14 * camera.zoom, Math.PI, 0);
    ctx.fill();

    // Flowing hair strands
    ctx.strokeStyle = '#1a0a0a';
    ctx.lineWidth = 3 * camera.zoom;
    for (let i = -2; i <= 2; i++) {
      const strand = Math.sin(this.floatPhase + i) * 5;
      ctx.beginPath();
      ctx.moveTo(screenPos.x + i * 6 * camera.zoom, screenPos.y - this.height * camera.zoom + floatOffset);
      ctx.quadraticCurveTo(
        screenPos.x + i * 8 * camera.zoom + strand * camera.zoom,
        screenPos.y - this.height * camera.zoom + 30 * camera.zoom + floatOffset,
        screenPos.x + i * 10 * camera.zoom,
        screenPos.y - this.height * 0.5 * camera.zoom + floatOffset
      );
      ctx.stroke();
    }

    // Eyes (glowing, changes with phase)
    const eyeGlow = 0.5 + Math.sin(this.floatPhase * 3) * 0.5;
    const eyeColor = this.phase === 3 ? `rgba(255, 0, 0, ${eyeGlow})` : `rgba(255, 100, 100, ${eyeGlow})`;
    ctx.fillStyle = eyeColor;
    ctx.shadowColor = eyeColor;
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(screenPos.x - 4 * camera.zoom, screenPos.y - this.height * camera.zoom + 6 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 4 * camera.zoom, screenPos.y - this.height * camera.zoom + 6 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Scream effect
    if (this.isScreaming) {
      ctx.save();
      const screamProgress = 1 - this.screamTimer / 1.0;
      ctx.strokeStyle = `rgba(255, 0, 0, ${1 - screamProgress})`;
      ctx.lineWidth = 4 * camera.zoom;
      for (let i = 1; i <= 3; i++) {
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, screamProgress * i * 60 * camera.zoom, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      ctx.save();
      ctx.globalAlpha = this.phaseTransitionFlash * 0.6;
      const flashRadius = (1 - this.phaseTransitionFlash) * 150 * camera.zoom;
      const flashGrad = ctx.createRadialGradient(
        screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, 0,
        screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, flashRadius
      );
      flashGrad.addColorStop(0, '#ff0044');
      flashGrad.addColorStop(1, 'rgba(170, 0, 50, 0)');
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = flashGrad;
      ctx.fill();
      ctx.restore();
    }

    // Teleport flash
    if (this.isTeleporting) {
      ctx.save();
      ctx.globalAlpha = this.teleportFlashTimer / 0.4;
      ctx.strokeStyle = '#ff44aa';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.setLineDash([5 * camera.zoom, 5 * camera.zoom]);
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height / 2 * camera.zoom + floatOffset, 40 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Crown
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    const crownY = screenPos.y - this.height * camera.zoom - 8 * camera.zoom + floatOffset;
    ctx.moveTo(screenPos.x - 12 * camera.zoom, crownY + 10 * camera.zoom);
    ctx.lineTo(screenPos.x - 12 * camera.zoom, crownY);
    ctx.lineTo(screenPos.x - 6 * camera.zoom, crownY + 5 * camera.zoom);
    ctx.lineTo(screenPos.x, crownY - 5 * camera.zoom);
    ctx.lineTo(screenPos.x + 6 * camera.zoom, crownY + 5 * camera.zoom);
    ctx.lineTo(screenPos.x + 12 * camera.zoom, crownY);
    ctx.lineTo(screenPos.x + 12 * camera.zoom, crownY + 10 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Crown jewels
    ctx.fillStyle = '#ff0044';
    ctx.beginPath();
    ctx.arc(screenPos.x, crownY - 2 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Boss HP bar
    const barWidth = 100 * camera.zoom;
    const barHeight = 12 * camera.zoom;
    const barY = screenPos.y - this.height * camera.zoom - 40 * camera.zoom + floatOffset;

    // Bar background
    ctx.fillStyle = '#0a0505';
    ctx.fillRect(screenPos.x - barWidth / 2 - 3, barY - 3, barWidth + 6, barHeight + 6);

    // Decorative border
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.strokeRect(screenPos.x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);

    // HP
    const healthPercent = this.getHealthPercent();
    const healthColor = this.isEnraged ? '#ff0000' : this.phase === 3 ? '#ff0044' : this.phase === 2 ? '#aa2244' : '#882244';
    ctx.fillStyle = '#331111';
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);
    ctx.fillStyle = healthColor;
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth * healthPercent, barHeight);

    // Boss name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${16 * camera.zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(this.name, screenPos.x, barY - 8 * camera.zoom);

    // Phase indicator
    ctx.fillStyle = '#ffffff';
    ctx.font = `${11 * camera.zoom}px Arial`;
    ctx.fillText(`Faz ${this.phase}`, screenPos.x, barY + barHeight + 14 * camera.zoom);

    // Enrage timer display
    if (!this.isEnraged) {
      const minutes = Math.floor(this.enrageTimer / 60);
      const seconds = Math.floor(this.enrageTimer % 60);
      const timerColor = this.enrageTimer < 30 ? '#ff4444' : '#aaaaaa';
      ctx.fillStyle = timerColor;
      ctx.font = `${9 * camera.zoom}px Arial`;
      ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, screenPos.x, barY + barHeight + 26 * camera.zoom);
    } else {
      ctx.fillStyle = '#ff0000';
      ctx.font = `bold ${10 * camera.zoom}px Arial`;
      ctx.fillText('GAZAP!', screenPos.x, barY + barHeight + 26 * camera.zoom);
    }
  }
}

// ==========================================
// ACT 2: ERGENEKON ENEMIES
// ==========================================

/**
 * Demir Kurt - Iron Wolf
 * Armored wolf with high defense, charges at player
 */
export class DemirKurt extends Enemy {
  private chargeTimer: number = 0;
  private chargeCooldown: number = 0;
  private isCharging: boolean = false;
  private chargeDirection: { x: number; y: number } = { x: 0, y: 0 };
  private armorPhase: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 38,
      height: 32,
      speed: 110,
      stats: {
        hp: 180,
        maxHp: 180,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 25,
        evasion: 0,
        strength: 18,
        agility: 12,
        spirit: 5,
      },
      name: 'Demir Kurt',
      attackRange: 45,
      attackDamage: 28,
      attackSpeed: 1.2,
      aggroRange: 220,
      expReward: 45,
      color: '#708090',
    });
    this.itemLevel = 12;
    this.attackElement = 'physical';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.armorPhase += deltaTime * 2;

    if (this.chargeCooldown > 0) {
      this.chargeCooldown -= deltaTime;
    }

    // Charging movement
    if (this.isCharging) {
      this.position.x += this.chargeDirection.x * this.speed * 3 * deltaTime;
      this.position.y += this.chargeDirection.y * this.speed * 3 * deltaTime;

      this.chargeTimer -= deltaTime;
      if (this.chargeTimer <= 0) {
        this.isCharging = false;
      }
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Initiate charge attack from medium range
    if (distance > 80 && distance < 180 && this.chargeCooldown <= 0 && !this.isCharging) {
      this.startCharge();
      return;
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      return;
    }

    if (!this.isCharging) {
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    }
  }

  private startCharge(): void {
    if (!this.target) return;
    this.isCharging = true;
    this.chargeTimer = 0.4;
    this.chargeCooldown = 5;
    this.chargeDirection = this.directionTo(this.target);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 2 * camera.zoom, 18 * camera.zoom, 8 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Armor glow when charging
    if (this.isCharging) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom, 30 * camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 200, 100, 0.3)';
      ctx.fill();
      ctx.restore();
    }

    // Body (wolf shape)
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;

    // Main body
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y - this.height * 0.4 * camera.zoom,
      this.width * 0.5 * camera.zoom,
      this.height * 0.4 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Armor plates (metallic shine)
    const shine = Math.sin(this.armorPhase) * 0.3 + 0.7;
    ctx.fillStyle = `rgba(180, 190, 200, ${shine})`;
    ctx.beginPath();
    ctx.ellipse(
      screenPos.x,
      screenPos.y - this.height * 0.5 * camera.zoom,
      this.width * 0.35 * camera.zoom,
      this.height * 0.25 * camera.zoom,
      0, 0, Math.PI * 2
    );
    ctx.fill();

    // Head
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(
      screenPos.x + this.width * 0.4 * camera.zoom,
      screenPos.y - this.height * 0.5 * camera.zoom,
      10 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fill();

    // Red eyes
    ctx.fillStyle = '#ff3333';
    ctx.beginPath();
    ctx.arc(
      screenPos.x + this.width * 0.5 * camera.zoom,
      screenPos.y - this.height * 0.55 * camera.zoom,
      2 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Dağ Devi - Mountain Giant
 * Slow but powerful, ground slam AoE attack
 */
export class DagDevi extends Enemy {
  private slamCooldown: number = 0;
  private isSlaming: boolean = false;
  private slamTimer: number = 0;
  private bodyPhase: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 55,
      height: 80,
      speed: 45,
      stats: {
        hp: 450,
        maxHp: 450,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 30,
        evasion: 0,
        strength: 35,
        agility: 5,
        spirit: 10,
      },
      name: 'Dağ Devi',
      attackRange: 70,
      attackDamage: 55,
      attackSpeed: 0.6,
      aggroRange: 180,
      expReward: 80,
      color: '#5d4037',
    });
    this.itemLevel = 14;
    this.attackElement = 'physical';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.bodyPhase += deltaTime;

    if (this.slamCooldown > 0) {
      this.slamCooldown -= deltaTime;
    }

    if (this.isSlaming) {
      this.slamTimer -= deltaTime;
      if (this.slamTimer <= 0) {
        this.isSlaming = false;
      }
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Ground slam when close
    if (distance <= 100 && this.slamCooldown <= 0) {
      this.performSlam();
      return;
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      return;
    }

    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private performSlam(): void {
    if (!this.target) return;
    this.isSlaming = true;
    this.slamTimer = 0.8;
    this.slamCooldown = 6;

    // AoE damage
    const distance = this.distanceTo(this.target);
    if (distance <= 120) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.5), this.attackElement);
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Slam shockwave effect
    if (this.isSlaming) {
      const progress = 1 - this.slamTimer / 0.8;
      ctx.save();
      ctx.globalAlpha = 1 - progress;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, 80 * progress * camera.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = '#8b4513';
      ctx.lineWidth = 4 * camera.zoom;
      ctx.stroke();
      ctx.restore();
    }

    // Large shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 5 * camera.zoom, 30 * camera.zoom, 15 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();

    // Legs
    ctx.fillStyle = this.color;
    ctx.fillRect(screenPos.x - 18 * camera.zoom, screenPos.y - 30 * camera.zoom, 12 * camera.zoom, 35 * camera.zoom);
    ctx.fillRect(screenPos.x + 6 * camera.zoom, screenPos.y - 30 * camera.zoom, 12 * camera.zoom, 35 * camera.zoom);

    // Body (large rock-like)
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y - 25 * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.6 * camera.zoom);
    ctx.lineTo(screenPos.x, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x + this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.6 * camera.zoom);
    ctx.lineTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y - 25 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Rock texture lines
    ctx.strokeStyle = '#3e2723';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 10 * camera.zoom, screenPos.y - 50 * camera.zoom);
    ctx.lineTo(screenPos.x - 5 * camera.zoom, screenPos.y - 35 * camera.zoom);
    ctx.moveTo(screenPos.x + 8 * camera.zoom, screenPos.y - 60 * camera.zoom);
    ctx.lineTo(screenPos.x + 12 * camera.zoom, screenPos.y - 40 * camera.zoom);
    ctx.stroke();

    // Eyes (glowing)
    ctx.fillStyle = '#ffcc00';
    ctx.beginPath();
    ctx.arc(screenPos.x - 8 * camera.zoom, screenPos.y - this.height * 0.75 * camera.zoom, 4 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 8 * camera.zoom, screenPos.y - this.height * 0.75 * camera.zoom, 4 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Rüzgar Ruhu - Wind Spirit
 * Very fast, evasive, hit-and-run attacks
 */
export class RuzgarRuhu extends Enemy {
  private floatPhase: number = 0;
  private dashCooldown: number = 0;
  private isDashing: boolean = false;
  private dashTimer: number = 0;
  private dashDirection: { x: number; y: number } = { x: 0, y: 0 };
  private fadeAlpha: number = 1;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 28,
      height: 40,
      speed: 180,
      stats: {
        hp: 90,
        maxHp: 90,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 5,
        evasion: 30,
        strength: 8,
        agility: 25,
        spirit: 15,
      },
      name: 'Rüzgar Ruhu',
      attackRange: 50,
      attackDamage: 22,
      attackSpeed: 1.5,
      aggroRange: 250,
      expReward: 35,
      color: '#b0c4de',
    });
    this.itemLevel = 11;
    this.attackElement = 'gok';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 4;

    if (this.dashCooldown > 0) {
      this.dashCooldown -= deltaTime;
    }

    if (this.isDashing) {
      this.position.x += this.dashDirection.x * this.speed * 2 * deltaTime;
      this.position.y += this.dashDirection.y * this.speed * 2 * deltaTime;
      this.fadeAlpha = 0.3;

      this.dashTimer -= deltaTime;
      if (this.dashTimer <= 0) {
        this.isDashing = false;
        this.fadeAlpha = 1;
      }
    } else {
      // Gradually fade back in
      if (this.fadeAlpha < 1) {
        this.fadeAlpha += deltaTime * 3;
        if (this.fadeAlpha > 1) this.fadeAlpha = 1;
      }
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Evasive dash when close
    if (distance < 60 && this.dashCooldown <= 0) {
      this.performEvasiveDash();
      return;
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      return;
    }

    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private performEvasiveDash(): void {
    this.isDashing = true;
    this.dashTimer = 0.3;
    this.dashCooldown = 2;

    // Dash to a random direction (evasive)
    const angle = Math.random() * Math.PI * 2;
    this.dashDirection = {
      x: Math.cos(angle),
      y: Math.sin(angle)
    };
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 8 * camera.zoom;

    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    // Wind trail effect
    if (this.isDashing) {
      for (let i = 0; i < 3; i++) {
        ctx.globalAlpha = 0.1 * (3 - i);
        ctx.beginPath();
        ctx.arc(
          screenPos.x - this.dashDirection.x * (20 + i * 15) * camera.zoom,
          screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
          (12 - i * 3) * camera.zoom,
          0, Math.PI * 2
        );
        ctx.fillStyle = this.color;
        ctx.fill();
      }
      ctx.globalAlpha = this.fadeAlpha;
    }

    // Ethereal body
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      20 * camera.zoom
    );
    gradient.addColorStop(0, this.hitFlashTimer > 0 ? '#ffffff' : this.color);
    gradient.addColorStop(1, 'rgba(176, 196, 222, 0)');

    ctx.beginPath();
    ctx.arc(
      screenPos.x,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      18 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fillStyle = gradient;
    ctx.fill();

    // Core
    ctx.beginPath();
    ctx.arc(
      screenPos.x,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      8 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fillStyle = '#e0e8f0';
    ctx.fill();

    // Eyes
    ctx.fillStyle = '#4169e1';
    ctx.beginPath();
    ctx.arc(screenPos.x - 3 * camera.zoom, screenPos.y - this.height * 0.55 * camera.zoom + floatOffset, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 3 * camera.zoom, screenPos.y - this.height * 0.55 * camera.zoom + floatOffset, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Ateş İblisi - Fire Demon
 * Fire-based attacks, leaves burning ground
 */
export class AtesIblisi extends Enemy {
  private flamePhase: number = 0;
  private fireballCooldown: number = 0;
  private burningGroundTimer: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 35,
      height: 50,
      speed: 85,
      stats: {
        hp: 160,
        maxHp: 160,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 12,
        evasion: 10,
        strength: 15,
        agility: 12,
        spirit: 25,
      },
      name: 'Ateş İblisi',
      attackRange: 120,
      attackDamage: 35,
      attackSpeed: 1.0,
      aggroRange: 200,
      expReward: 55,
      color: '#ff4500',
    });
    this.itemLevel = 13;
    this.attackElement = 'od';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.flamePhase += deltaTime * 6;

    if (this.fireballCooldown > 0) {
      this.fireballCooldown -= deltaTime;
    }

    if (this.burningGroundTimer > 0) {
      this.burningGroundTimer -= deltaTime;
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Ranged fireball attack
    if (distance <= this.attackRange && distance > 50 && this.fireballCooldown <= 0) {
      this.shootFireball();
      return;
    }

    if (distance <= 50) {
      this.state = 'attack';
      return;
    }

    // Keep some distance
    if (distance < 70) {
      const direction = this.directionTo(this.target);
      this.move(-direction.x, -direction.y, deltaTime);
    } else if (distance > this.attackRange) {
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    }
  }

  private shootFireball(): void {
    if (!this.target) return;
    this.fireballCooldown = 2.5;
    this.burningGroundTimer = 0.5;

    // Deal damage
    const distance = this.distanceTo(this.target);
    if (distance <= this.attackRange) {
      this.target.takeDamage(this.attackDamage, this.attackElement);
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Burning ground effect
    if (this.burningGroundTimer > 0) {
      ctx.save();
      ctx.globalAlpha = this.burningGroundTimer;
      for (let i = 0; i < 5; i++) {
        const angle = (this.flamePhase + i * 1.2) % (Math.PI * 2);
        const fx = screenPos.x + Math.cos(angle) * 30 * camera.zoom;
        const fy = screenPos.y + Math.sin(angle) * 15 * camera.zoom;
        ctx.beginPath();
        ctx.arc(fx, fy, 5 * camera.zoom, 0, Math.PI * 2);
        ctx.fillStyle = '#ff6600';
        ctx.fill();
      }
      ctx.restore();
    }

    // Fire aura
    ctx.save();
    const auraFlicker = Math.sin(this.flamePhase) * 5 + 25;
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      auraFlicker * camera.zoom
    );
    gradient.addColorStop(0, 'rgba(255, 100, 0, 0.4)');
    gradient.addColorStop(1, 'rgba(255, 69, 0, 0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom, auraFlicker * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 2 * camera.zoom, 16 * camera.zoom, 8 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Body
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.3 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.3 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Flame head
    for (let i = 0; i < 3; i++) {
      const flameY = screenPos.y - this.height * camera.zoom - (5 + i * 5 + Math.sin(this.flamePhase + i) * 3) * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x + (i - 1) * 6 * camera.zoom, flameY, (5 - i) * camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? '#ffcc00' : i === 1 ? '#ff9900' : '#ff6600';
      ctx.fill();
    }

    // Eyes
    ctx.fillStyle = '#ffff00';
    ctx.beginPath();
    ctx.arc(screenPos.x - 5 * camera.zoom, screenPos.y - this.height * 0.7 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 5 * camera.zoom, screenPos.y - this.height * 0.7 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Kaya Golemi - Rock Golem
 * Very high defense, slow, immune to knockback
 */
export class KayaGolemi extends Enemy {
  private rumblePhase: number = 0;
  private throwCooldown: number = 0;
  private isThrowing: boolean = false;
  private throwTimer: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 50,
      height: 65,
      speed: 35,
      stats: {
        hp: 350,
        maxHp: 350,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 45,
        evasion: 0,
        strength: 30,
        agility: 5,
        spirit: 10,
      },
      name: 'Kaya Golemi',
      attackRange: 55,
      attackDamage: 40,
      attackSpeed: 0.5,
      aggroRange: 150,
      expReward: 70,
      color: '#696969',
    });
    this.itemLevel = 14;
    this.knockbackImmune = true;
    this.attackElement = 'physical';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.rumblePhase += deltaTime;

    if (this.throwCooldown > 0) {
      this.throwCooldown -= deltaTime;
    }

    if (this.isThrowing) {
      this.throwTimer -= deltaTime;
      if (this.throwTimer <= 0) {
        this.isThrowing = false;
      }
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Rock throw at range
    if (distance > 80 && distance < 150 && this.throwCooldown <= 0) {
      this.throwRock();
      return;
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      return;
    }

    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private throwRock(): void {
    if (!this.target) return;
    this.isThrowing = true;
    this.throwTimer = 0.6;
    this.throwCooldown = 4;

    // Ranged damage
    this.target.takeDamage(Math.floor(this.attackDamage * 0.7), this.attackElement);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const rumble = Math.sin(this.rumblePhase * 8) * (this.state === 'chase' ? 2 : 0);

    // Large shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 5 * camera.zoom, 28 * camera.zoom, 14 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();

    // Throwing animation
    if (this.isThrowing) {
      ctx.save();
      ctx.beginPath();
      const rockX = screenPos.x + (1 - this.throwTimer / 0.6) * 80 * camera.zoom;
      const rockY = screenPos.y - this.height * 0.5 * camera.zoom - Math.sin((1 - this.throwTimer / 0.6) * Math.PI) * 30 * camera.zoom;
      ctx.arc(rockX, rockY, 8 * camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = '#555555';
      ctx.fill();
      ctx.restore();
    }

    // Body parts (rocky chunks)
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;

    // Legs
    ctx.beginPath();
    ctx.arc(screenPos.x - 12 * camera.zoom + rumble, screenPos.y - 15 * camera.zoom, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 12 * camera.zoom + rumble, screenPos.y - 15 * camera.zoom, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Torso
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 25 * camera.zoom + rumble, screenPos.y - 25 * camera.zoom);
    ctx.lineTo(screenPos.x - 20 * camera.zoom + rumble, screenPos.y - this.height * 0.7 * camera.zoom);
    ctx.lineTo(screenPos.x + rumble, screenPos.y - this.height * 0.85 * camera.zoom);
    ctx.lineTo(screenPos.x + 20 * camera.zoom + rumble, screenPos.y - this.height * 0.7 * camera.zoom);
    ctx.lineTo(screenPos.x + 25 * camera.zoom + rumble, screenPos.y - 25 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Head
    ctx.beginPath();
    ctx.arc(screenPos.x + rumble, screenPos.y - this.height * camera.zoom + 10 * camera.zoom, 15 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Rock texture
    ctx.strokeStyle = '#4a4a4a';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 10 * camera.zoom + rumble, screenPos.y - 40 * camera.zoom);
    ctx.lineTo(screenPos.x - 5 * camera.zoom + rumble, screenPos.y - 30 * camera.zoom);
    ctx.moveTo(screenPos.x + 8 * camera.zoom + rumble, screenPos.y - 50 * camera.zoom);
    ctx.lineTo(screenPos.x + 5 * camera.zoom + rumble, screenPos.y - 35 * camera.zoom);
    ctx.stroke();

    // Glowing eyes
    ctx.fillStyle = '#00ff88';
    ctx.beginPath();
    ctx.arc(screenPos.x - 5 * camera.zoom + rumble, screenPos.y - this.height * camera.zoom + 8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 5 * camera.zoom + rumble, screenPos.y - this.height * camera.zoom + 8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Yer-Sub - Act 2 Boss (Earth-Water Spirit)
 * Dual element boss with ground and water attacks
 */
export class YerSub extends Enemy {
  private phase: number = 1;
  private phaseTransitioned2: boolean = false;
  private phaseTransitioned3: boolean = false;
  private phaseTimer: number = 0;
  private elementPhase: number = 0;
  private currentElement: 'earth' | 'water' = 'earth';
  private specialCooldown: number = 0;
  private summonCooldown: number = 0;
  private isCasting: boolean = false;
  private castTimer: number = 0;
  private quakeActive: boolean = false;
  private quakeTimer: number = 0;
  private waveActive: boolean = false;
  private waveTimer: number = 0;

  // Phase 2: Lava pools (persistent danger zones)
  private lavaPools: { x: number; y: number; timer: number; radius: number }[] = [];
  private lavaPoolSpawnTimer: number = 8;

  // Phase 2: Rock pillars that block movement
  private rockPillars: { x: number; y: number; timer: number }[] = [];
  private rockPillarSpawnTimer: number = 12;

  // Phase 3: Enrage - size grows, fire trail
  private enrageGrowth: number = 0; // 0 to 0.2 (20% growth)
  private fireTrail: { x: number; y: number; timer: number }[] = [];
  private lastTrailPos: { x: number; y: number } = { x: 0, y: 0 };

  // Enrage timer (5 minutes)
  private enrageTimer: number = 300;
  private isEnraged: boolean = false;
  private readonly baseAttackDamage: number = 65;
  private readonly baseSpeed: number = 55;

  // Phase transition invulnerability
  private invulnerableTimer: number = 0;
  private phaseTransitionFlash: number = 0;

  // Screen shake request
  public screenShakeRequest: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 70,
      height: 100,
      speed: 55,
      stats: {
        hp: 2500,
        maxHp: 2500,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 35,
        evasion: 15,
        strength: 45,
        agility: 20,
        spirit: 40,
      },
      name: 'Yer-Sub',
      attackRange: 80,
      attackDamage: 65,
      attackSpeed: 0.8,
      aggroRange: 400,
      expReward: 500,
      color: '#4a7c59',
    });
    this.itemLevel = 18;
    this.lastTrailPos = { x: x, y: y };
    this.attackElement = 'physical';
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    if (this.invulnerableTimer > 0) return 0;
    return super.takeDamage(amount, type, bypassES);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.elementPhase += deltaTime * 2;
    this.phaseTimer += deltaTime;

    // Invulnerability timer
    if (this.invulnerableTimer > 0) {
      this.invulnerableTimer -= deltaTime;
    }

    // Enrage timer countdown
    if (!this.isEnraged && this.isAlive && !this.isDying) {
      this.enrageTimer -= deltaTime;
      if (this.enrageTimer <= 0) {
        this.isEnraged = true;
        this.enrageTimer = 0;
        this.attackDamage = this.baseAttackDamage * 2;
        this.speed = this.baseSpeed * 1.5;
      }
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      this.phaseTransitionFlash -= deltaTime * 2;
    }

    // Screen shake
    if (this.screenShakeRequest > 0) {
      this.screenShakeRequest -= deltaTime;
    }

    // Phase transitions
    const hpPercent = this.getHealthPercent();
    if (hpPercent <= 0.3 && this.phase < 3 && !this.phaseTransitioned3) {
      this.phase = 3;
      this.phaseTransitioned3 = true;
      this.onPhaseTransition3();
    } else if (hpPercent <= 0.6 && this.phase < 2 && !this.phaseTransitioned2) {
      this.phase = 2;
      this.phaseTransitioned2 = true;
      this.onPhaseTransition2();
    }

    // Element switching in phase 2
    if (this.phase === 2 && this.phaseTimer > 10) {
      this.currentElement = this.currentElement === 'earth' ? 'water' : 'earth';
      this.phaseTimer = 0;
    }

    if (this.specialCooldown > 0) this.specialCooldown -= deltaTime;
    if (this.summonCooldown > 0) this.summonCooldown -= deltaTime;

    if (this.isCasting) {
      this.castTimer -= deltaTime;
      if (this.castTimer <= 0) {
        this.isCasting = false;
      }
    }

    if (this.quakeActive) {
      this.quakeTimer -= deltaTime;
      if (this.quakeTimer <= 0) this.quakeActive = false;
    }

    if (this.waveActive) {
      this.waveTimer -= deltaTime;
      if (this.waveTimer <= 0) this.waveActive = false;
    }

    // Phase 2+: Lava pool spawning (from ground slams)
    if (this.phase >= 2) {
      this.lavaPoolSpawnTimer -= deltaTime;
      if (this.lavaPoolSpawnTimer <= 0 && this.quakeActive) {
        this.spawnLavaPool();
        this.lavaPoolSpawnTimer = 8;
      }

      // Update lava pools - deal damage and expire
      for (let i = this.lavaPools.length - 1; i >= 0; i--) {
        this.lavaPools[i].timer -= deltaTime;
        if (this.lavaPools[i].timer <= 0) {
          this.lavaPools.splice(i, 1);
          continue;
        }
        // Damage player if standing in lava pool
        if (this.target) {
          const dx = this.target.position.x - this.lavaPools[i].x;
          const dy = this.target.position.y - this.lavaPools[i].y;
          if (Math.sqrt(dx * dx + dy * dy) < this.lavaPools[i].radius) {
            this.target.takeDamage(Math.floor(15 * deltaTime), this.attackElement);
          }
        }
      }

      // Rock pillar spawning
      this.rockPillarSpawnTimer -= deltaTime;
      if (this.rockPillarSpawnTimer <= 0) {
        this.spawnRockPillar();
        this.rockPillarSpawnTimer = 12;
      }

      // Update rock pillars - expire
      for (let i = this.rockPillars.length - 1; i >= 0; i--) {
        this.rockPillars[i].timer -= deltaTime;
        if (this.rockPillars[i].timer <= 0) {
          this.rockPillars.splice(i, 1);
        }
      }
    }

    // Phase 3: Enrage growth animation and fire trail
    if (this.phase === 3) {
      // Gradually grow to 20% larger
      if (this.enrageGrowth < 0.2) {
        this.enrageGrowth = Math.min(0.2, this.enrageGrowth + deltaTime * 0.1);
      }

      // Phase 3: damage +40%
      if (!this.isEnraged) {
        this.attackDamage = Math.floor(this.baseAttackDamage * 1.4);
      }

      // Leave fire trail behind
      const trailDx = this.position.x - this.lastTrailPos.x;
      const trailDy = this.position.y - this.lastTrailPos.y;
      const trailDist = Math.sqrt(trailDx * trailDx + trailDy * trailDy);
      if (trailDist > 20) {
        this.fireTrail.push({ x: this.position.x, y: this.position.y, timer: 3.0 });
        this.lastTrailPos = { x: this.position.x, y: this.position.y };
      }

      // Update fire trail - deal damage and expire
      for (let i = this.fireTrail.length - 1; i >= 0; i--) {
        this.fireTrail[i].timer -= deltaTime;
        if (this.fireTrail[i].timer <= 0) {
          this.fireTrail.splice(i, 1);
          continue;
        }
        if (this.target) {
          const dx = this.target.position.x - this.fireTrail[i].x;
          const dy = this.target.position.y - this.fireTrail[i].y;
          if (Math.sqrt(dx * dx + dy * dy) < 20) {
            this.target.takeDamage(Math.floor(10 * deltaTime), this.attackElement);
          }
        }
      }
    }

    // Special attacks
    if (this.state === 'attack' && this.specialCooldown <= 0) {
      if (this.currentElement === 'earth') {
        this.performQuake();
      } else {
        this.performWave();
      }
      this.specialCooldown = 5;
    }
  }

  private onPhaseTransition2(): void {
    this.invulnerableTimer = 1.0;
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 0.5;
    this.currentElement = 'earth'; // Phase 2 starts earth

    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 30,
        color: ['#8b5a2b', '#ffcc00', '#ff6600'],
        speed: { min: 100, max: 300 },
        size: { min: 3, max: 7 },
        life: { min: 0.5, max: 1.0 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 50,
        friction: 1,
        alphaDecay: 1.5,
        sizeDecay: 2,
      });
    }
  }

  private onPhaseTransition3(): void {
    this.invulnerableTimer = 1.5;
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 0.8;
    this.currentElement = 'water';

    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 40,
        color: ['#ff4400', '#ff8800', '#ffcc00'],
        speed: { min: 150, max: 400 },
        size: { min: 4, max: 9 },
        life: { min: 0.6, max: 1.2 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 30,
        friction: 1,
        alphaDecay: 1.2,
        sizeDecay: 2,
      });
    }
  }

  private spawnLavaPool(): void {
    // Spawn a lava pool at the quake location (boss position)
    this.lavaPools.push({
      x: this.position.x,
      y: this.position.y,
      timer: 8.0,
      radius: 40,
    });
  }

  private spawnRockPillar(): void {
    if (!this.target) return;
    // Spawn a rock pillar near the player to block movement
    const angle = Math.random() * Math.PI * 2;
    const dist = 50 + Math.random() * 60;
    this.rockPillars.push({
      x: this.target.position.x + Math.cos(angle) * dist,
      y: this.target.position.y + Math.sin(angle) * dist,
      timer: 10.0,
    });
  }

  private performQuake(): void {
    if (!this.target) return;
    this.quakeActive = true;
    this.quakeTimer = 1.0;
    this.isCasting = true;
    this.castTimer = 0.5;

    const distance = this.distanceTo(this.target);
    if (distance <= 150) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.2), this.attackElement);
    }
  }

  private performWave(): void {
    if (!this.target) return;
    this.waveActive = true;
    this.waveTimer = 1.5;
    this.isCasting = true;
    this.castTimer = 0.5;

    const distance = this.distanceTo(this.target);
    if (distance <= 180) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.0), this.attackElement);
    }
  }

  shouldSummon(): boolean {
    if (this.summonCooldown <= 0 && this.phase >= 2 && this.isAlive) {
      this.summonCooldown = 15;
      return true;
    }
    return false;
  }

  getLavaPools(): { x: number; y: number; timer: number; radius: number }[] {
    return this.lavaPools;
  }

  getRockPillars(): { x: number; y: number; timer: number }[] {
    return this.rockPillars;
  }

  getFireTrail(): { x: number; y: number; timer: number }[] {
    return this.fireTrail;
  }

  getHealthPercent(): number {
    return this.stats.hp / this.stats.maxHp;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const sizeScale = 1 + this.enrageGrowth;
    const floatOffset = Math.sin(this.elementPhase) * 5 * camera.zoom;

    // Lava pools (phase 2+)
    for (const pool of this.lavaPools) {
      const poolScreenX = screenPos.x + (pool.x - this.position.x) * camera.zoom;
      const poolScreenY = screenPos.y + (pool.y - this.position.y) * camera.zoom;
      ctx.save();
      const poolFlicker = 0.5 + Math.sin(this.elementPhase * 6 + pool.x) * 0.3;
      ctx.globalAlpha = Math.min(pool.timer / 1.5, 1.0) * poolFlicker;
      ctx.fillStyle = '#ff4400';
      ctx.beginPath();
      ctx.arc(poolScreenX, poolScreenY, pool.radius * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffaa00';
      ctx.beginPath();
      ctx.arc(poolScreenX, poolScreenY, pool.radius * 0.5 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Rock pillars (phase 2+)
    for (const pillar of this.rockPillars) {
      const pillarScreenX = screenPos.x + (pillar.x - this.position.x) * camera.zoom;
      const pillarScreenY = screenPos.y + (pillar.y - this.position.y) * camera.zoom;
      ctx.save();
      ctx.globalAlpha = Math.min(pillar.timer / 1.0, 1.0);
      ctx.fillStyle = '#8b7355';
      ctx.fillRect(pillarScreenX - 10 * camera.zoom, pillarScreenY - 30 * camera.zoom, 20 * camera.zoom, 30 * camera.zoom);
      ctx.fillStyle = '#6b5335';
      ctx.fillRect(pillarScreenX - 8 * camera.zoom, pillarScreenY - 28 * camera.zoom, 16 * camera.zoom, 5 * camera.zoom);
      ctx.restore();
    }

    // Fire trail (phase 3)
    for (const fire of this.fireTrail) {
      const fireScreenX = screenPos.x + (fire.x - this.position.x) * camera.zoom;
      const fireScreenY = screenPos.y + (fire.y - this.position.y) * camera.zoom;
      ctx.save();
      const fireFlicker = 0.4 + Math.sin(this.elementPhase * 10 + fire.x * 0.1) * 0.3;
      ctx.globalAlpha = Math.min(fire.timer / 0.5, 1.0) * fireFlicker;
      ctx.fillStyle = '#ff6600';
      ctx.beginPath();
      ctx.arc(fireScreenX, fireScreenY, 10 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ffcc00';
      ctx.beginPath();
      ctx.arc(fireScreenX, fireScreenY, 5 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Quake effect
    if (this.quakeActive) {
      const progress = 1 - this.quakeTimer;
      ctx.save();
      ctx.globalAlpha = 0.5 * this.quakeTimer;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(screenPos.x, screenPos.y, (50 + progress * 100 + i * 30) * camera.zoom, 0, Math.PI * 2);
        ctx.strokeStyle = '#8b4513';
        ctx.lineWidth = (3 - i) * camera.zoom;
        ctx.stroke();
      }
      ctx.restore();
    }

    // Wave effect
    if (this.waveActive) {
      ctx.save();
      ctx.globalAlpha = 0.4 * this.waveTimer;
      const waveProgress = 1 - this.waveTimer / 1.5;
      ctx.beginPath();
      ctx.ellipse(
        screenPos.x + waveProgress * 150 * camera.zoom,
        screenPos.y,
        30 * camera.zoom,
        60 * camera.zoom,
        0, 0, Math.PI * 2
      );
      ctx.fillStyle = '#4169e1';
      ctx.fill();
      ctx.restore();
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      ctx.save();
      ctx.globalAlpha = this.phaseTransitionFlash * 0.5;
      const flashRadius = (1 - this.phaseTransitionFlash) * 150 * camera.zoom;
      const flashGrad = ctx.createRadialGradient(
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 0,
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius
      );
      flashGrad.addColorStop(0, this.phase === 3 ? '#ff4400' : '#8b5a2b');
      flashGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = flashGrad;
      ctx.fill();
      ctx.restore();
    }

    // Invulnerability shield
    if (this.invulnerableTimer > 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 50 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Boss aura
    ctx.save();
    const auraColor = this.isEnraged ? 'rgba(255, 0, 0,' : this.currentElement === 'earth' ? 'rgba(139, 90, 43,' : 'rgba(65, 105, 225,';
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      60 * sizeScale * camera.zoom
    );
    gradient.addColorStop(0, auraColor + '0.4)');
    gradient.addColorStop(1, auraColor + '0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 60 * sizeScale * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Enrage pulsing aura
    if (this.isEnraged || this.phase === 3) {
      ctx.save();
      const enragePulse = 0.2 + Math.sin(this.elementPhase * 4) * 0.15;
      ctx.globalAlpha = enragePulse;
      ctx.strokeStyle = this.isEnraged ? '#ff0000' : '#ff6600';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 55 * sizeScale * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Large shadow (scaled for phase 3)
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 10 * camera.zoom, 35 * sizeScale * camera.zoom, 18 * sizeScale * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fill();

    // Body - dual colored (earth/water split), scaled for phase 3
    const bodyRadius = 35 * sizeScale;
    const bodyColor = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.fillStyle = bodyColor;

    // Left side (earth)
    ctx.save();
    ctx.beginPath();
    ctx.rect(screenPos.x - 40 * sizeScale * camera.zoom, screenPos.y - this.height * sizeScale * camera.zoom + floatOffset, 40 * sizeScale * camera.zoom, this.height * sizeScale * camera.zoom);
    ctx.clip();
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : (this.phase === 3 ? '#aa6030' : '#8b5a2b');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, bodyRadius * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Right side (water)
    ctx.save();
    ctx.beginPath();
    ctx.rect(screenPos.x, screenPos.y - this.height * sizeScale * camera.zoom + floatOffset, 40 * sizeScale * camera.zoom, this.height * sizeScale * camera.zoom);
    ctx.clip();
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : (this.phase === 3 ? '#5588ff' : '#4169e1');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, bodyRadius * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Face
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.75 * camera.zoom + floatOffset, 20 * sizeScale * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = this.phase === 3 ? '#4a6a3a' : '#3a5a3a';
    ctx.fill();

    // Eyes (element colored)
    ctx.fillStyle = this.currentElement === 'earth' ? '#ffcc00' : '#00ffff';
    ctx.beginPath();
    ctx.arc(screenPos.x - 7 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom + floatOffset, 4 * sizeScale * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 7 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom + floatOffset, 4 * sizeScale * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Boss health bar
    const barWidth = 120 * camera.zoom;
    const barHeight = 12 * camera.zoom;
    const barY = screenPos.y - this.height * sizeScale * camera.zoom - 40 * camera.zoom + floatOffset;

    // Border
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 2;
    ctx.strokeRect(screenPos.x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);

    // Background
    ctx.fillStyle = '#222222';
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);

    // Health
    const healthColor = this.isEnraged ? '#ff0000' : this.phase === 3 ? '#00aaff' : this.phase === 2 ? '#44aa44' : '#228822';
    ctx.fillStyle = healthColor;
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth * this.getHealthPercent(), barHeight);

    // Boss name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${16 * camera.zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(this.name, screenPos.x, barY - 10 * camera.zoom);

    // Phase and element indicator
    ctx.fillStyle = '#ffffff';
    ctx.font = `${11 * camera.zoom}px Arial`;
    const elementText = this.currentElement === 'earth' ? 'Yer' : 'Su';
    ctx.fillText(`Faz ${this.phase} - ${elementText}`, screenPos.x, barY + barHeight + 15 * camera.zoom);

    // Enrage timer display
    if (!this.isEnraged) {
      const minutes = Math.floor(this.enrageTimer / 60);
      const seconds = Math.floor(this.enrageTimer % 60);
      const timerColor = this.enrageTimer < 30 ? '#ff4444' : '#aaaaaa';
      ctx.fillStyle = timerColor;
      ctx.font = `${9 * camera.zoom}px Arial`;
      ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, screenPos.x, barY + barHeight + 27 * camera.zoom);
    } else {
      ctx.fillStyle = '#ff0000';
      ctx.font = `bold ${10 * camera.zoom}px Arial`;
      ctx.fillText('GAZAP!', screenPos.x, barY + barHeight + 27 * camera.zoom);
    }
  }
}

// ===========================================
// ACT 3 - YERALTI (UNDERGROUND) ENEMIES
// ===========================================

/**
 * Gölge Avcısı - Shadow Hunter
 * Stealthy enemy that can become invisible and backstab
 */
export class GolgeAvcisi extends Enemy {
  private isInvisible: boolean = false;
  private invisibleTimer: number = 0;
  private invisibleCooldown: number = 0;
  private backstabReady: boolean = false;
  private shadowPhase: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 32,
      height: 45,
      speed: 130,
      stats: {
        hp: 200,
        maxHp: 200,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 15,
        evasion: 35,
        strength: 22,
        agility: 30,
        spirit: 15,
      },
      name: 'Gölge Avcısı',
      attackRange: 40,
      attackDamage: 45,
      attackSpeed: 1.5,
      aggroRange: 180,
      expReward: 80,
      color: '#2f2f4f',
    });
    this.itemLevel = 20;
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.shadowPhase += deltaTime * 4;

    if (this.invisibleCooldown > 0) {
      this.invisibleCooldown -= deltaTime;
    }

    if (this.isInvisible) {
      this.invisibleTimer -= deltaTime;
      if (this.invisibleTimer <= 0) {
        this.isInvisible = false;
      }
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Go invisible if not already and cooldown is ready
    if (!this.isInvisible && this.invisibleCooldown <= 0 && distance > 100) {
      this.isInvisible = true;
      this.invisibleTimer = 3;
      this.invisibleCooldown = 8;
      this.backstabReady = true;
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      // Backstab deals double damage
      if (this.backstabReady && this.isInvisible) {
        this.backstabReady = false;
        this.isInvisible = false;
        this.target.takeDamage(this.attackDamage * 2, this.attackElement);
      }
      return;
    }

    // Move faster when invisible
    const speedMult = this.isInvisible ? 1.5 : 1.0;
    const direction = this.directionTo(this.target);
    this.move(direction.x * speedMult, direction.y * speedMult, deltaTime);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    ctx.save();
    // Invisible = very low opacity
    ctx.globalAlpha = this.isInvisible ? 0.15 : 1.0;

    // Shadow trail when invisible
    if (this.isInvisible) {
      for (let i = 0; i < 3; i++) {
        const trailX = screenPos.x - (i + 1) * 10 * camera.zoom;
        ctx.globalAlpha = 0.1 - i * 0.03;
        ctx.beginPath();
        ctx.ellipse(trailX, screenPos.y - this.height * 0.5 * camera.zoom, 12 * camera.zoom, 18 * camera.zoom, 0, 0, Math.PI * 2);
        ctx.fillStyle = '#1a1a2e';
        ctx.fill();
      }
      ctx.globalAlpha = 0.15;
    }

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 2 * camera.zoom, 14 * camera.zoom, 7 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fill();

    // Cloaked body
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.2 * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.3 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.3 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.2 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Hood
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.85 * camera.zoom, 10 * camera.zoom, Math.PI, 0);
    ctx.fill();

    // Glowing eyes (only visible when not invisible)
    if (!this.isInvisible) {
      ctx.fillStyle = '#ff4444';
      ctx.beginPath();
      ctx.arc(screenPos.x - 3 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
      ctx.arc(screenPos.x + 3 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
    }

    // Daggers
    ctx.strokeStyle = '#c0c0c0';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.6 * camera.zoom, screenPos.y - this.height * 0.5 * camera.zoom);
    ctx.moveTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom);
    ctx.lineTo(screenPos.x + this.width * 0.6 * camera.zoom, screenPos.y - this.height * 0.5 * camera.zoom);
    ctx.stroke();

    ctx.restore();

    if (!this.isInvisible) {
      this.renderHealthBar(renderer, screenPos, camera.zoom);
    }
  }
}

/**
 * Kemik Savaşçı - Bone Warrior
 * Undead warrior that can reassemble after death
 */
export class KemikSavasci extends Enemy {
  private hasReassembled: boolean = false;
  private isReassembling: boolean = false;
  private reassembleTimer: number = 0;
  private boneRattle: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 35,
      height: 55,
      speed: 65,
      stats: {
        hp: 280,
        maxHp: 280,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 25,
        evasion: 5,
        strength: 28,
        agility: 10,
        spirit: 5,
      },
      name: 'Kemik Savaşçı',
      attackRange: 50,
      attackDamage: 38,
      attackSpeed: 0.9,
      aggroRange: 200,
      expReward: 75,
      color: '#d4c9a8',
    });
    this.itemLevel = 21;
  }

  update(deltaTime: number): void {
    this.boneRattle += deltaTime * 10;

    if (this.isReassembling) {
      this.reassembleTimer -= deltaTime;
      if (this.reassembleTimer <= 0) {
        this.isReassembling = false;
        // Revive with 50% HP
        this.stats.hp = Math.floor(this.stats.maxHp * 0.5);
        this.isAlive = true;
      }
      return; // Don't update normally while reassembling
    }

    super.update(deltaTime);
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    const damage = super.takeDamage(amount, type, bypassES);

    // Check for reassembly
    if (!this.isAlive && !this.hasReassembled) {
      this.hasReassembled = true;
      this.isReassembling = true;
      this.reassembleTimer = 2.0;
    }

    return damage;
  }

  render(renderer: Renderer, camera: Camera): void {
    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const rattle = Math.sin(this.boneRattle) * 2 * camera.zoom;

    if (this.isReassembling) {
      // Scattered bones reassembling
      ctx.save();
      const progress = 1 - this.reassembleTimer / 2.0;

      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const dist = (1 - progress) * 40;
        const boneX = screenPos.x + Math.cos(angle) * dist * camera.zoom;
        const boneY = screenPos.y - this.height * 0.5 * camera.zoom + Math.sin(angle) * dist * camera.zoom;

        ctx.fillStyle = '#d4c9a8';
        ctx.beginPath();
        ctx.ellipse(boneX, boneY, 8 * camera.zoom, 4 * camera.zoom, angle, 0, Math.PI * 2);
        ctx.fill();
      }

      // Central glow
      ctx.globalAlpha = progress;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom, 20 * camera.zoom * progress, 0, Math.PI * 2);
      ctx.fillStyle = '#88ff88';
      ctx.fill();

      ctx.restore();
      return;
    }

    if (!this.isAlive) return;

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 2 * camera.zoom, 15 * camera.zoom, 8 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    const boneColor = this.hitFlashTimer > 0 ? '#ffffff' : this.color;

    // Legs (bone)
    ctx.strokeStyle = boneColor;
    ctx.lineWidth = 4 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 8 * camera.zoom, screenPos.y - 25 * camera.zoom + rattle);
    ctx.lineTo(screenPos.x - 10 * camera.zoom, screenPos.y);
    ctx.moveTo(screenPos.x + 8 * camera.zoom, screenPos.y - 25 * camera.zoom + rattle);
    ctx.lineTo(screenPos.x + 10 * camera.zoom, screenPos.y);
    ctx.stroke();

    // Ribcage
    ctx.fillStyle = boneColor;
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y - 35 * camera.zoom + rattle, 15 * camera.zoom, 20 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    // Ribs detail
    ctx.strokeStyle = '#b8a888';
    ctx.lineWidth = 2 * camera.zoom;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - 35 * camera.zoom + rattle, (8 + i * 3) * camera.zoom, -0.8, 0.8);
      ctx.stroke();
    }

    // Skull
    ctx.fillStyle = boneColor;
    ctx.beginPath();
    ctx.arc(screenPos.x + rattle * 0.5, screenPos.y - this.height * 0.9 * camera.zoom, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Eye sockets with green glow
    ctx.fillStyle = '#00ff44';
    ctx.beginPath();
    ctx.arc(screenPos.x - 4 * camera.zoom + rattle * 0.5, screenPos.y - this.height * 0.92 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 4 * camera.zoom + rattle * 0.5, screenPos.y - this.height * 0.92 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Sword
    ctx.strokeStyle = '#a0a0a0';
    ctx.lineWidth = 3 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x + 18 * camera.zoom, screenPos.y - 30 * camera.zoom + rattle);
    ctx.lineTo(screenPos.x + 25 * camera.zoom, screenPos.y - 60 * camera.zoom + rattle);
    ctx.stroke();

    // Shield
    ctx.fillStyle = '#5a5a5a';
    ctx.beginPath();
    ctx.ellipse(screenPos.x - 20 * camera.zoom, screenPos.y - 35 * camera.zoom + rattle, 8 * camera.zoom, 12 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Lanetli Ruh - Cursed Spirit
 * Ghost that curses players, reducing their stats
 */
export class LanetliRuh extends Enemy {
  private curseCooldown: number = 0;
  private wailTimer: number = 0;
  private floatPhase: number = 0;
  private fadeAlpha: number = 1;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 30,
      height: 50,
      speed: 70,
      stats: {
        hp: 150,
        maxHp: 150,
        mana: 0,
        maxMana: 0,
        energyShield: 50,
        maxEnergyShield: 50,
        armor: 0,
        evasion: 30,
        strength: 10,
        agility: 15,
        spirit: 35,
      },
      name: 'Lanetli Ruh',
      attackRange: 100,
      attackDamage: 25,
      attackSpeed: 0.7,
      aggroRange: 250,
      expReward: 85,
      color: '#9932cc',
    });
    this.itemLevel = 22;
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 3;

    // Fade in and out
    this.fadeAlpha = 0.6 + Math.sin(this.floatPhase * 0.5) * 0.3;

    if (this.curseCooldown > 0) {
      this.curseCooldown -= deltaTime;
    }

    if (this.wailTimer > 0) {
      this.wailTimer -= deltaTime;
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Cast curse at range
    if (distance <= this.attackRange && this.curseCooldown <= 0) {
      this.castCurse();
      return;
    }

    if (distance > this.attackRange) {
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    }
  }

  private castCurse(): void {
    if (!this.target) return;
    this.curseCooldown = 6;
    this.wailTimer = 1.0;

    // Curse damage + debuff effect (visual)
    this.target.takeDamage(this.attackDamage, this.attackElement);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 8 * camera.zoom;

    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    // Wail effect
    if (this.wailTimer > 0) {
      ctx.save();
      ctx.globalAlpha = this.wailTimer * 0.5;
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(
          screenPos.x,
          screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
          (30 + (1 - this.wailTimer) * 50 + i * 20) * camera.zoom,
          0, Math.PI * 2
        );
        ctx.strokeStyle = '#9932cc';
        ctx.lineWidth = (3 - i) * camera.zoom;
        ctx.stroke();
      }
      ctx.restore();
    }

    // Ghostly trail
    for (let i = 0; i < 4; i++) {
      ctx.globalAlpha = (this.fadeAlpha - i * 0.15) * 0.5;
      ctx.beginPath();
      ctx.ellipse(
        screenPos.x,
        screenPos.y - this.height * 0.3 * camera.zoom + floatOffset + i * 8 * camera.zoom,
        (15 - i * 2) * camera.zoom,
        (10 - i * 2) * camera.zoom,
        0, 0, Math.PI * 2
      );
      ctx.fillStyle = this.color;
      ctx.fill();
    }

    ctx.globalAlpha = this.fadeAlpha;

    // Main ethereal body
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      25 * camera.zoom
    );
    gradient.addColorStop(0, this.hitFlashTimer > 0 ? '#ffffff' : '#da70d6');
    gradient.addColorStop(1, 'rgba(153, 50, 204, 0)');

    ctx.beginPath();
    ctx.arc(
      screenPos.x,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      22 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fillStyle = gradient;
    ctx.fill();

    // Inner core
    ctx.beginPath();
    ctx.arc(
      screenPos.x,
      screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      10 * camera.zoom,
      0, Math.PI * 2
    );
    ctx.fillStyle = '#e6e6fa';
    ctx.fill();

    // Hollow eyes
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.ellipse(screenPos.x - 5 * camera.zoom, screenPos.y - this.height * 0.55 * camera.zoom + floatOffset, 4 * camera.zoom, 6 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.ellipse(screenPos.x + 5 * camera.zoom, screenPos.y - this.height * 0.55 * camera.zoom + floatOffset, 4 * camera.zoom, 6 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    // Screaming mouth
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y - this.height * 0.4 * camera.zoom + floatOffset, 6 * camera.zoom, 8 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Ölüm Habercisi - Death Herald
 * Elite enemy that buffs nearby undead and deals AoE damage
 */
export class OlumHabercisi extends Enemy {
  private auraPhase: number = 0;
  private deathMarkCooldown: number = 0;
  private markActive: boolean = false;
  private markTimer: number = 0;
  private scytheSwing: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 40,
      height: 70,
      speed: 80,
      stats: {
        hp: 400,
        maxHp: 400,
        mana: 0,
        maxMana: 0,
        energyShield: 0,
        maxEnergyShield: 0,
        armor: 20,
        evasion: 20,
        strength: 30,
        agility: 22,
        spirit: 30,
      },
      name: 'Ölüm Habercisi',
      attackRange: 65,
      attackDamage: 55,
      attackSpeed: 0.8,
      aggroRange: 280,
      expReward: 120,
      color: '#1c1c1c',
    });
    this.itemLevel = 23;
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.auraPhase += deltaTime * 2;

    if (this.deathMarkCooldown > 0) {
      this.deathMarkCooldown -= deltaTime;
    }

    if (this.markActive) {
      this.markTimer -= deltaTime;
      if (this.markTimer <= 0) {
        this.markActive = false;
      }
    }

    if (this.state === 'attack') {
      this.scytheSwing += deltaTime * 8;
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Death Mark - AoE damage ability
    if (distance <= 120 && this.deathMarkCooldown <= 0) {
      this.castDeathMark();
    }

    if (distance <= this.attackRange) {
      this.state = 'attack';
      return;
    }

    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private castDeathMark(): void {
    if (!this.target) return;
    this.markActive = true;
    this.markTimer = 1.5;
    this.deathMarkCooldown = 8;

    // AoE damage
    const distance = this.distanceTo(this.target);
    if (distance <= 120) {
      this.target.takeDamage(Math.floor(this.attackDamage * 0.8), this.attackElement);
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Death aura
    ctx.save();
    const auraSize = 40 + Math.sin(this.auraPhase) * 5;
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      auraSize * camera.zoom
    );
    gradient.addColorStop(0, 'rgba(50, 0, 50, 0.3)');
    gradient.addColorStop(1, 'rgba(20, 0, 20, 0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom, auraSize * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Death Mark effect
    if (this.markActive) {
      ctx.save();
      const markProgress = 1 - this.markTimer / 1.5;
      ctx.globalAlpha = 0.6 * (1 - markProgress);

      // Skull symbol
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 1.2 * camera.zoom, 15 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();

      // Expanding ring
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y, (30 + markProgress * 90) * camera.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = '#8b0000';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.stroke();

      ctx.restore();
    }

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 3 * camera.zoom, 20 * camera.zoom, 10 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fill();

    // Robed body
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y - this.height * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.15 * camera.zoom);
    ctx.lineTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y);
    ctx.lineTo(screenPos.x + this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.15 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Robe trim
    ctx.strokeStyle = '#4a0080';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.stroke();

    // Hood
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : '#0a0a0a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.85 * camera.zoom, 14 * camera.zoom, Math.PI, 0);
    ctx.fill();

    // Face in shadow - only eyes visible
    ctx.fillStyle = '#ff0000';
    ctx.beginPath();
    ctx.arc(screenPos.x - 4 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 4 * camera.zoom, screenPos.y - this.height * 0.8 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Scythe
    const swingAngle = this.state === 'attack' ? Math.sin(this.scytheSwing) * 0.3 : 0;
    ctx.save();
    ctx.translate(screenPos.x + 25 * camera.zoom, screenPos.y - 40 * camera.zoom);
    ctx.rotate(-0.5 + swingAngle);

    // Handle
    ctx.strokeStyle = '#4a3728';
    ctx.lineWidth = 4 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, 50 * camera.zoom);
    ctx.stroke();

    // Blade
    ctx.fillStyle = '#a0a0a0';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(-30 * camera.zoom, -10 * camera.zoom, -35 * camera.zoom, 15 * camera.zoom);
    ctx.quadraticCurveTo(-20 * camera.zoom, 10 * camera.zoom, 0, 8 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    ctx.restore();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Erlik Elçisi - Act 3 Boss (Erlik's Messenger)
 * Powerful undead that summons minions and deals massive damage
 */
export class ErlikElcisi extends Enemy {
  private phase: number = 1;
  private phaseTransitioned2: boolean = false;
  private phaseTimer: number = 0;
  private summonCooldown: number = 0;
  private darkBoltCooldown: number = 0;
  private soulDrainCooldown: number = 0;
  private isCasting: boolean = false;
  private castTimer: number = 0;
  private darkBoltActive: boolean = false;
  private darkBoltTimer: number = 0;
  private soulDrainActive: boolean = false;
  private soulDrainTimer: number = 0;
  private floatPhase: number = 0;

  // Phase 2: Shadow clones
  private shadowClones: { x: number; y: number; hp: number; maxHp: number; active: boolean }[] = [];
  private teleportBetweenClonesTimer: number = 5;
  private cloneSwapFlash: number = 0;

  // Phase 2: Dark orbs orbiting
  private darkOrbCount: number = 0;
  private darkOrbPhase: number = 0;

  // Enrage timer (5 minutes)
  private enrageTimer: number = 300;
  private isEnraged: boolean = false;
  private readonly baseAttackDamage: number = 75;
  private readonly baseSpeed: number = 60;

  // Phase transition
  private invulnerableTimer: number = 0;
  private phaseTransitionFlash: number = 0;

  // Screen shake request
  public screenShakeRequest: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 60,
      height: 90,
      speed: 60,
      stats: {
        hp: 4000,
        maxHp: 4000,
        mana: 0,
        maxMana: 0,
        energyShield: 500,
        maxEnergyShield: 500,
        armor: 30,
        evasion: 20,
        strength: 50,
        agility: 25,
        spirit: 60,
      },
      name: 'Erlik Elçisi',
      attackRange: 70,
      attackDamage: 75,
      attackSpeed: 0.7,
      aggroRange: 450,
      expReward: 800,
      color: '#2d0a3e',
    });
    this.itemLevel = 28;
    this.attackElement = 'karanlik';
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    if (this.invulnerableTimer > 0) return 0;
    return super.takeDamage(amount, type, bypassES);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 2;
    this.phaseTimer += deltaTime;
    this.darkOrbPhase += deltaTime * 3;

    // Invulnerability timer
    if (this.invulnerableTimer > 0) {
      this.invulnerableTimer -= deltaTime;
    }

    // Enrage timer countdown
    if (!this.isEnraged && this.isAlive && !this.isDying) {
      this.enrageTimer -= deltaTime;
      if (this.enrageTimer <= 0) {
        this.isEnraged = true;
        this.enrageTimer = 0;
        this.attackDamage = this.baseAttackDamage * 2;
        this.speed = this.baseSpeed * 1.5;
      }
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      this.phaseTransitionFlash -= deltaTime * 2;
    }

    // Clone swap flash
    if (this.cloneSwapFlash > 0) {
      this.cloneSwapFlash -= deltaTime * 3;
    }

    // Screen shake
    if (this.screenShakeRequest > 0) {
      this.screenShakeRequest -= deltaTime;
    }

    // Phase transitions
    const hpPercent = this.getHealthPercent();
    if (hpPercent <= 0.25 && this.phase < 3) {
      this.phase = 3;
    } else if (hpPercent <= 0.5 && this.phase < 2 && !this.phaseTransitioned2) {
      this.phase = 2;
      this.phaseTransitioned2 = true;
      this.onPhaseTransition2();
    }

    // Phase 2+: Shadow clone management
    if (this.phase >= 2) {
      // Update dark orbs count
      this.darkOrbCount = this.phase === 3 ? 6 : 4;

      // Teleport between clones every 5s
      this.teleportBetweenClonesTimer -= deltaTime;
      if (this.teleportBetweenClonesTimer <= 0) {
        this.teleportToClone();
        this.teleportBetweenClonesTimer = 5;
      }

      // Update clone HP (clones can be "killed" by player proximity damage)
      for (const clone of this.shadowClones) {
        if (clone.active && this.target) {
          const dx = this.target.position.x - clone.x;
          const dy = this.target.position.y - clone.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          // Clones deal contact damage
          if (dist < 50) {
            this.target.takeDamage(Math.floor(5 * deltaTime), this.attackElement);
          }
        }
      }
    }

    // Cooldowns
    if (this.summonCooldown > 0) this.summonCooldown -= deltaTime;
    if (this.darkBoltCooldown > 0) this.darkBoltCooldown -= deltaTime;
    if (this.soulDrainCooldown > 0) this.soulDrainCooldown -= deltaTime;

    if (this.isCasting) {
      this.castTimer -= deltaTime;
      if (this.castTimer <= 0) {
        this.isCasting = false;
      }
    }

    if (this.darkBoltActive) {
      this.darkBoltTimer -= deltaTime;
      if (this.darkBoltTimer <= 0) this.darkBoltActive = false;
    }

    if (this.soulDrainActive) {
      this.soulDrainTimer -= deltaTime;
      if (this.soulDrainTimer <= 0) this.soulDrainActive = false;
    }

    // Boss abilities
    if (this.state === 'chase' || this.state === 'attack') {
      if (this.darkBoltCooldown <= 0) {
        this.castDarkBolt();
      } else if (this.phase >= 2 && this.soulDrainCooldown <= 0) {
        this.castSoulDrain();
      }
    }
  }

  private onPhaseTransition2(): void {
    this.invulnerableTimer = 1.0;
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 0.5;

    // Create 2 shadow clones at 30% HP
    const cloneHp = Math.floor(this.stats.maxHp * 0.2);
    for (let i = 0; i < 2; i++) {
      const angle = (i === 0) ? Math.PI / 3 : -Math.PI / 3;
      const dist = 100;
      this.shadowClones.push({
        x: this.position.x + Math.cos(angle) * dist,
        y: this.position.y + Math.sin(angle) * dist,
        hp: cloneHp,
        maxHp: cloneHp,
        active: true,
      });
    }

    // Particle burst
    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 35,
        color: ['#8b00ff', '#4b0082', '#9400d3'],
        speed: { min: 120, max: 300 },
        size: { min: 3, max: 7 },
        life: { min: 0.5, max: 1.0 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 30,
        friction: 1,
        alphaDecay: 1.5,
        sizeDecay: 2,
      });
    }
  }

  private teleportToClone(): void {
    const activeClones = this.shadowClones.filter(c => c.active);
    if (activeClones.length === 0) return;

    // Pick a random active clone to swap with
    const clone = activeClones[Math.floor(Math.random() * activeClones.length)];

    // Swap positions
    const oldX = this.position.x;
    const oldY = this.position.y;
    this.position.x = clone.x;
    this.position.y = clone.y;
    clone.x = oldX;
    clone.y = oldY;

    this.cloneSwapFlash = 0.5;
  }

  getShadowClones(): { x: number; y: number; hp: number; maxHp: number; active: boolean }[] {
    return this.shadowClones;
  }

  getDarkOrbCount(): number {
    return this.darkOrbCount;
  }

  private castDarkBolt(): void {
    if (!this.target) return;
    this.darkBoltActive = true;
    this.darkBoltTimer = 0.8;
    this.darkBoltCooldown = 4 - this.phase * 0.5;
    this.isCasting = true;
    this.castTimer = 0.5;

    const distance = this.distanceTo(this.target);
    if (distance <= 200) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.2), this.attackElement);
    }
  }

  private castSoulDrain(): void {
    if (!this.target) return;
    this.soulDrainActive = true;
    this.soulDrainTimer = 2.0;
    this.soulDrainCooldown = 12;
    this.isCasting = true;
    this.castTimer = 0.8;

    const distance = this.distanceTo(this.target);
    if (distance <= 150) {
      const damage = Math.floor(this.attackDamage * 0.5);
      this.target.takeDamage(damage, this.attackElement);
      // Heal self
      this.stats.hp = Math.min(this.stats.maxHp, this.stats.hp + damage);
    }
  }

  shouldSummon(): boolean {
    if (this.summonCooldown <= 0 && this.isAlive) {
      this.summonCooldown = 20 - this.phase * 3;
      return true;
    }
    return false;
  }

  getHealthPercent(): number {
    return this.stats.hp / this.stats.maxHp;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 6 * camera.zoom;

    // Shadow clones (phase 2+)
    for (const clone of this.shadowClones) {
      if (!clone.active) continue;
      const cloneScreenX = screenPos.x + (clone.x - this.position.x) * camera.zoom;
      const cloneScreenY = screenPos.y + (clone.y - this.position.y) * camera.zoom;

      ctx.save();
      ctx.globalAlpha = 0.4;

      // Clone body (ghostly version)
      ctx.fillStyle = '#4b0082';
      ctx.beginPath();
      ctx.moveTo(cloneScreenX, cloneScreenY - this.height * camera.zoom);
      ctx.lineTo(cloneScreenX - this.width * 0.4 * camera.zoom, cloneScreenY - this.height * 0.1 * camera.zoom);
      ctx.lineTo(cloneScreenX - this.width * 0.3 * camera.zoom, cloneScreenY);
      ctx.lineTo(cloneScreenX + this.width * 0.3 * camera.zoom, cloneScreenY);
      ctx.lineTo(cloneScreenX + this.width * 0.4 * camera.zoom, cloneScreenY - this.height * 0.1 * camera.zoom);
      ctx.closePath();
      ctx.fill();

      // Clone eyes
      ctx.fillStyle = '#ff4400';
      ctx.beginPath();
      ctx.arc(cloneScreenX - 4 * camera.zoom, cloneScreenY - this.height * 0.82 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
      ctx.arc(cloneScreenX + 4 * camera.zoom, cloneScreenY - this.height * 0.82 * camera.zoom, 3 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();

      // Clone HP bar
      const cloneBarWidth = 50 * camera.zoom;
      const cloneBarHeight = 4 * camera.zoom;
      const cloneBarY = cloneScreenY - this.height * camera.zoom - 10 * camera.zoom;
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = '#222222';
      ctx.fillRect(cloneScreenX - cloneBarWidth / 2, cloneBarY, cloneBarWidth, cloneBarHeight);
      ctx.fillStyle = '#9932cc';
      ctx.fillRect(cloneScreenX - cloneBarWidth / 2, cloneBarY, cloneBarWidth * (clone.hp / clone.maxHp), cloneBarHeight);

      ctx.restore();
    }

    // Clone swap flash
    if (this.cloneSwapFlash > 0) {
      ctx.save();
      ctx.globalAlpha = this.cloneSwapFlash * 0.4;
      ctx.fillStyle = '#8b00ff';
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 60 * camera.zoom, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      ctx.save();
      ctx.globalAlpha = this.phaseTransitionFlash * 0.5;
      const flashRadius = (1 - this.phaseTransitionFlash) * 150 * camera.zoom;
      const flashGrad = ctx.createRadialGradient(
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 0,
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius
      );
      flashGrad.addColorStop(0, '#8b00ff');
      flashGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = flashGrad;
      ctx.fill();
      ctx.restore();
    }

    // Invulnerability shield
    if (this.invulnerableTimer > 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 55 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Dark Bolt effect
    if (this.darkBoltActive && this.target) {
      ctx.save();
      ctx.globalAlpha = this.darkBoltTimer;
      ctx.strokeStyle = '#8b00ff';
      ctx.lineWidth = 4 * camera.zoom;
      ctx.beginPath();
      ctx.moveTo(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom);
      // Direction to target
      const targetScreenPos = camera.worldToScreen(this.target.position.x, this.target.position.y);
      ctx.lineTo(targetScreenPos.x, targetScreenPos.y);
      ctx.stroke();

      // Impact
      ctx.beginPath();
      ctx.arc(targetScreenPos.x, targetScreenPos.y, 20 * camera.zoom * (1 - this.darkBoltTimer), 0, Math.PI * 2);
      ctx.strokeStyle = '#ff00ff';
      ctx.stroke();
      ctx.restore();
    }

    // Soul Drain effect
    if (this.soulDrainActive && this.target) {
      ctx.save();
      ctx.globalAlpha = 0.5;
      const targetScreenPos = camera.worldToScreen(this.target.position.x, this.target.position.y);

      // Soul stream
      const gradient = ctx.createLinearGradient(
        targetScreenPos.x, targetScreenPos.y,
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom
      );
      gradient.addColorStop(0, '#00ff88');
      gradient.addColorStop(1, '#88ff00');

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 3 * camera.zoom;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(targetScreenPos.x, targetScreenPos.y);
      ctx.lineTo(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // Boss aura
    ctx.save();
    const auraGradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      70 * camera.zoom
    );
    auraGradient.addColorStop(0, 'rgba(75, 0, 130, 0.4)');
    auraGradient.addColorStop(0.5, 'rgba(45, 10, 62, 0.2)');
    auraGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 70 * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = auraGradient;
    ctx.fill();
    ctx.restore();

    // Floating particles
    for (let i = 0; i < 6; i++) {
      const angle = (this.floatPhase + i * Math.PI / 3) % (Math.PI * 2);
      const dist = 35 + Math.sin(this.floatPhase * 2 + i) * 10;
      const px = screenPos.x + Math.cos(angle) * dist * camera.zoom;
      const py = screenPos.y - this.height * 0.5 * camera.zoom + floatOffset + Math.sin(angle) * dist * 0.3 * camera.zoom;

      ctx.beginPath();
      ctx.arc(px, py, 3 * camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = i % 2 === 0 ? '#9400d3' : '#00ff88';
      ctx.fill();
    }

    // Dark orbs orbiting (phase 2+)
    if (this.darkOrbCount > 0) {
      for (let i = 0; i < this.darkOrbCount; i++) {
        const orbAngle = (this.darkOrbPhase + i * (Math.PI * 2 / this.darkOrbCount)) % (Math.PI * 2);
        const orbDist = 45;
        const orbX = screenPos.x + Math.cos(orbAngle) * orbDist * camera.zoom;
        const orbY = screenPos.y - this.height * 0.5 * camera.zoom + floatOffset + Math.sin(orbAngle) * orbDist * 0.3 * camera.zoom;

        ctx.save();
        ctx.beginPath();
        ctx.arc(orbX, orbY, 6 * camera.zoom, 0, Math.PI * 2);
        const orbGrad = ctx.createRadialGradient(orbX, orbY, 0, orbX, orbY, 6 * camera.zoom);
        orbGrad.addColorStop(0, '#1a0033');
        orbGrad.addColorStop(0.5, '#4b0082');
        orbGrad.addColorStop(1, 'rgba(75, 0, 130, 0)');
        ctx.fillStyle = orbGrad;
        ctx.fill();
        ctx.restore();
      }
    }

    // Enrage pulsing aura
    if (this.isEnraged) {
      ctx.save();
      const enragePulse = 0.3 + Math.sin(this.floatPhase * 4) * 0.2;
      ctx.globalAlpha = enragePulse;
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 60 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Large shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 8 * camera.zoom, 32 * camera.zoom, 16 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fill();

    // Robed body with elaborate design
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y - this.height * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.1 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y + floatOffset);
    ctx.lineTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y + floatOffset);
    ctx.lineTo(screenPos.x + this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.1 * camera.zoom + floatOffset);
    ctx.closePath();
    ctx.fill();

    // Robe details
    ctx.strokeStyle = '#6a0dad';
    ctx.lineWidth = 3 * camera.zoom;
    ctx.stroke();

    // Runes on robe
    ctx.strokeStyle = '#9400d3';
    ctx.lineWidth = 1 * camera.zoom;
    for (let i = 0; i < 3; i++) {
      const runeY = screenPos.y - this.height * (0.3 + i * 0.15) * camera.zoom + floatOffset;
      ctx.beginPath();
      ctx.moveTo(screenPos.x - 10 * camera.zoom, runeY);
      ctx.lineTo(screenPos.x + 10 * camera.zoom, runeY);
      ctx.moveTo(screenPos.x, runeY - 5 * camera.zoom);
      ctx.lineTo(screenPos.x, runeY + 5 * camera.zoom);
      ctx.stroke();
    }

    // Crown/horns
    ctx.fillStyle = '#4b0082';
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 15 * camera.zoom, screenPos.y - this.height * 0.9 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - 20 * camera.zoom, screenPos.y - this.height * 1.1 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - 10 * camera.zoom, screenPos.y - this.height * 0.95 * camera.zoom + floatOffset);
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(screenPos.x + 15 * camera.zoom, screenPos.y - this.height * 0.9 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 20 * camera.zoom, screenPos.y - this.height * 1.1 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 10 * camera.zoom, screenPos.y - this.height * 0.95 * camera.zoom + floatOffset);
    ctx.fill();

    // Face - skull with glowing eyes
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.8 * camera.zoom + floatOffset, 16 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Glowing eyes that intensify by phase
    const eyeGlow = this.phase === 3 ? '#ff0000' : this.phase === 2 ? '#ff4400' : '#ff8800';
    ctx.fillStyle = eyeGlow;
    ctx.shadowColor = eyeGlow;
    ctx.shadowBlur = 10 * camera.zoom;
    ctx.beginPath();
    ctx.arc(screenPos.x - 5 * camera.zoom, screenPos.y - this.height * 0.82 * camera.zoom + floatOffset, 4 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 5 * camera.zoom, screenPos.y - this.height * 0.82 * camera.zoom + floatOffset, 4 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Staff
    ctx.strokeStyle = '#2f1f4f';
    ctx.lineWidth = 5 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x + 30 * camera.zoom, screenPos.y - 20 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 30 * camera.zoom, screenPos.y - 80 * camera.zoom + floatOffset);
    ctx.stroke();

    // Staff orb
    ctx.beginPath();
    ctx.arc(screenPos.x + 30 * camera.zoom, screenPos.y - 85 * camera.zoom + floatOffset, 10 * camera.zoom, 0, Math.PI * 2);
    const orbGradient = ctx.createRadialGradient(
      screenPos.x + 30 * camera.zoom, screenPos.y - 85 * camera.zoom + floatOffset, 0,
      screenPos.x + 30 * camera.zoom, screenPos.y - 85 * camera.zoom + floatOffset, 10 * camera.zoom
    );
    orbGradient.addColorStop(0, '#00ff88');
    orbGradient.addColorStop(1, '#006633');
    ctx.fillStyle = orbGradient;
    ctx.fill();

    // Boss health bar
    const barWidth = 150 * camera.zoom;
    const barHeight = 14 * camera.zoom;
    const barY = screenPos.y - this.height * camera.zoom - 50 * camera.zoom + floatOffset;

    // Border
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 3;
    ctx.strokeRect(screenPos.x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);

    // Background
    ctx.fillStyle = '#222222';
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);

    // Health (changes color by phase)
    const healthColor = this.isEnraged ? '#ff0000' : this.phase === 3 ? '#ff0000' : this.phase === 2 ? '#ff6600' : '#9932cc';
    ctx.fillStyle = healthColor;
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth * this.getHealthPercent(), barHeight);

    // Boss name
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${18 * camera.zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(this.name, screenPos.x, barY - 12 * camera.zoom);

    // Phase indicator
    ctx.fillStyle = '#ffffff';
    ctx.font = `${12 * camera.zoom}px Arial`;
    ctx.fillText(`Faz ${this.phase}`, screenPos.x, barY + barHeight + 18 * camera.zoom);

    // Enrage timer display
    if (!this.isEnraged) {
      const minutes = Math.floor(this.enrageTimer / 60);
      const seconds = Math.floor(this.enrageTimer % 60);
      const timerColor = this.enrageTimer < 30 ? '#ff4444' : '#aaaaaa';
      ctx.fillStyle = timerColor;
      ctx.font = `${9 * camera.zoom}px Arial`;
      ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, screenPos.x, barY + barHeight + 30 * camera.zoom);
    } else {
      ctx.fillStyle = '#ff0000';
      ctx.font = `bold ${10 * camera.zoom}px Arial`;
      ctx.fillText('GAZAP!', screenPos.x, barY + barHeight + 30 * camera.zoom);
    }
  }
}

// ===========================================
// ACT 4 - ERLIK DIYARI (ERLIK'S REALM) ENEMIES
// ===========================================

/**
 * Erlik Şövalyesi - Erlik Knight
 * Elite armored undead warrior with shield bash and dark strikes
 */
export class ErlikSovalyesi extends Enemy {
  private shieldBashCooldown: number = 0;
  private isBashing: boolean = false;
  private bashTimer: number = 0;
  private darkStrikeCooldown: number = 0;
  private darkStrikeActive: boolean = false;
  private armorPhase: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 45,
      height: 70,
      speed: 75,
      stats: {
        hp: 550,
        maxHp: 550,
        mana: 0,
        maxMana: 0,
        energyShield: 100,
        maxEnergyShield: 100,
        armor: 45,
        evasion: 10,
        strength: 45,
        agility: 18,
        spirit: 20,
      },
      name: 'Erlik Şövalyesi',
      attackRange: 55,
      attackDamage: 65,
      attackSpeed: 0.9,
      aggroRange: 250,
      expReward: 150,
      color: '#2b1a1a',
    });
    this.itemLevel = 32;
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.armorPhase += deltaTime * 2;

    if (this.shieldBashCooldown > 0) {
      this.shieldBashCooldown -= deltaTime;
    }

    if (this.darkStrikeCooldown > 0) {
      this.darkStrikeCooldown -= deltaTime;
    }

    if (this.isBashing) {
      this.bashTimer -= deltaTime;
      if (this.bashTimer <= 0) {
        this.isBashing = false;
      }
    }

    if (this.darkStrikeActive) {
      this.darkStrikeActive = false;
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Shield bash at mid range
    if (distance <= 100 && distance > 50 && this.shieldBashCooldown <= 0) {
      this.performShieldBash();
      return;
    }

    if (distance <= this.attackRange) {
      // Dark strike attack
      if (this.darkStrikeCooldown <= 0) {
        this.performDarkStrike();
      }
      this.state = 'attack';
      return;
    }

    const direction = this.directionTo(this.target);
    this.move(direction.x, direction.y, deltaTime);
  }

  private performShieldBash(): void {
    if (!this.target) return;
    this.isBashing = true;
    this.bashTimer = 0.5;
    this.shieldBashCooldown = 6;

    // Rush toward target
    const direction = this.directionTo(this.target);
    this.position.x += direction.x * 60;
    this.position.y += direction.y * 60;

    const distance = this.distanceTo(this.target);
    if (distance <= 70) {
      this.target.takeDamage(Math.floor(this.attackDamage * 0.8), this.attackElement);
    }
  }

  private performDarkStrike(): void {
    if (!this.target) return;
    this.darkStrikeActive = true;
    this.darkStrikeCooldown = 3;

    // Extra damage with dark energy
    this.target.takeDamage(Math.floor(this.attackDamage * 1.3), this.attackElement);
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;

    // Dark aura
    ctx.save();
    const auraSize = 35 + Math.sin(this.armorPhase) * 5;
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
      auraSize * camera.zoom
    );
    gradient.addColorStop(0, 'rgba(80, 0, 0, 0.3)');
    gradient.addColorStop(1, 'rgba(40, 0, 0, 0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom, auraSize * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();

    // Shield bash effect
    if (this.isBashing) {
      ctx.save();
      ctx.globalAlpha = this.bashTimer * 2;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.4 * camera.zoom, 40 * camera.zoom, 0, Math.PI * 2);
      ctx.strokeStyle = '#ff4400';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.stroke();
      ctx.restore();
    }

    // Shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 3 * camera.zoom, 22 * camera.zoom, 11 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fill();

    // Armored body
    const bodyColor = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.fillStyle = bodyColor;

    // Legs (armored)
    ctx.beginPath();
    ctx.rect(screenPos.x - 12 * camera.zoom, screenPos.y - 25 * camera.zoom, 8 * camera.zoom, 25 * camera.zoom);
    ctx.rect(screenPos.x + 4 * camera.zoom, screenPos.y - 25 * camera.zoom, 8 * camera.zoom, 25 * camera.zoom);
    ctx.fill();

    // Torso armor
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 20 * camera.zoom, screenPos.y - 25 * camera.zoom);
    ctx.lineTo(screenPos.x - 22 * camera.zoom, screenPos.y - 55 * camera.zoom);
    ctx.lineTo(screenPos.x, screenPos.y - 65 * camera.zoom);
    ctx.lineTo(screenPos.x + 22 * camera.zoom, screenPos.y - 55 * camera.zoom);
    ctx.lineTo(screenPos.x + 20 * camera.zoom, screenPos.y - 25 * camera.zoom);
    ctx.closePath();
    ctx.fill();

    // Armor highlight
    ctx.strokeStyle = '#4a2a2a';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.stroke();

    // Helmet
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.9 * camera.zoom, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Helmet visor
    ctx.fillStyle = '#1a0a0a';
    ctx.beginPath();
    ctx.rect(screenPos.x - 8 * camera.zoom, screenPos.y - this.height * 0.92 * camera.zoom, 16 * camera.zoom, 6 * camera.zoom);
    ctx.fill();

    // Glowing eyes behind visor
    ctx.fillStyle = '#ff0000';
    ctx.beginPath();
    ctx.arc(screenPos.x - 4 * camera.zoom, screenPos.y - this.height * 0.9 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 4 * camera.zoom, screenPos.y - this.height * 0.9 * camera.zoom, 2 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Shield (left arm)
    ctx.fillStyle = '#3a2a2a';
    ctx.beginPath();
    ctx.ellipse(screenPos.x - 25 * camera.zoom, screenPos.y - 40 * camera.zoom, 12 * camera.zoom, 18 * camera.zoom, 0.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#5a3a3a';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.stroke();

    // Sword (right arm)
    ctx.strokeStyle = '#888888';
    ctx.lineWidth = 4 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x + 22 * camera.zoom, screenPos.y - 35 * camera.zoom);
    ctx.lineTo(screenPos.x + 30 * camera.zoom, screenPos.y - 70 * camera.zoom);
    ctx.stroke();

    // Dark energy on sword
    if (this.darkStrikeActive) {
      ctx.strokeStyle = '#8b0000';
      ctx.lineWidth = 6 * camera.zoom;
      ctx.stroke();
    }

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Karanlık Seraf - Dark Seraph
 * Fallen angel with dark magic and flight ability
 */
export class KaranlikSeraf extends Enemy {
  private floatPhase: number = 0;
  private wingPhase: number = 0;
  private darkOrbCooldown: number = 0;
  private darkOrbActive: boolean = false;
  private darkOrbTimer: number = 0;
  private diveBombCooldown: number = 0;
  private isDiving: boolean = false;
  private diveTimer: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 50,
      height: 80,
      speed: 100,
      stats: {
        hp: 450,
        maxHp: 450,
        mana: 0,
        maxMana: 0,
        energyShield: 150,
        maxEnergyShield: 150,
        armor: 20,
        evasion: 35,
        strength: 30,
        agility: 40,
        spirit: 45,
      },
      name: 'Karanlık Seraf',
      attackRange: 150,
      attackDamage: 55,
      attackSpeed: 1.2,
      aggroRange: 300,
      expReward: 180,
      color: '#1a1a2e',
    });
    this.itemLevel = 33;
    this.attackElement = 'karanlik';
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 2;
    this.wingPhase += deltaTime * 8;

    if (this.darkOrbCooldown > 0) {
      this.darkOrbCooldown -= deltaTime;
    }

    if (this.diveBombCooldown > 0) {
      this.diveBombCooldown -= deltaTime;
    }

    if (this.darkOrbActive) {
      this.darkOrbTimer -= deltaTime;
      if (this.darkOrbTimer <= 0) this.darkOrbActive = false;
    }

    if (this.isDiving) {
      this.diveTimer -= deltaTime;
      if (this.diveTimer <= 0) this.isDiving = false;
    }
  }

  protected updateChase(deltaTime: number): void {
    if (!this.target || !this.target.isAlive) {
      this.state = 'idle';
      return;
    }

    const distance = this.distanceTo(this.target);

    // Dive bomb from far away
    if (distance > 200 && distance < 350 && this.diveBombCooldown <= 0) {
      this.performDiveBomb();
      return;
    }

    // Dark orb at range
    if (distance <= this.attackRange && distance > 80 && this.darkOrbCooldown <= 0) {
      this.shootDarkOrb();
      return;
    }

    if (distance <= 60) {
      this.state = 'attack';
      return;
    }

    // Circle around target
    if (distance < 120) {
      const perpAngle = Math.atan2(this.target.position.y - this.position.y, this.target.position.x - this.position.x) + Math.PI / 2;
      this.move(Math.cos(perpAngle), Math.sin(perpAngle), deltaTime);
    } else {
      const direction = this.directionTo(this.target);
      this.move(direction.x, direction.y, deltaTime);
    }
  }

  private shootDarkOrb(): void {
    if (!this.target) return;
    this.darkOrbActive = true;
    this.darkOrbTimer = 0.6;
    this.darkOrbCooldown = 2.5;

    const distance = this.distanceTo(this.target);
    if (distance <= this.attackRange) {
      this.target.takeDamage(this.attackDamage, this.attackElement);
    }
  }

  private performDiveBomb(): void {
    if (!this.target) return;
    this.isDiving = true;
    this.diveTimer = 0.8;
    this.diveBombCooldown = 10;

    // Teleport close to target
    const angle = Math.atan2(this.target.position.y - this.position.y, this.target.position.x - this.position.x);
    this.position.x = this.target.position.x - Math.cos(angle) * 50;
    this.position.y = this.target.position.y - Math.sin(angle) * 50;

    // Deal damage
    const distance = this.distanceTo(this.target);
    if (distance <= 100) {
      this.target.takeDamage(Math.floor(this.attackDamage * 1.5), this.attackElement);
    }
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 10 * camera.zoom;
    const wingSpan = Math.sin(this.wingPhase) * 0.2 + 1;

    // Dive bomb trail
    if (this.isDiving) {
      ctx.save();
      ctx.globalAlpha = this.diveTimer;
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.arc(
          screenPos.x - i * 15 * camera.zoom,
          screenPos.y - this.height * 0.5 * camera.zoom + floatOffset + i * 5 * camera.zoom,
          (15 - i * 2) * camera.zoom,
          0, Math.PI * 2
        );
        ctx.fillStyle = `rgba(100, 0, 150, ${0.3 - i * 0.05})`;
        ctx.fill();
      }
      ctx.restore();
    }

    // Dark orb projectile
    if (this.darkOrbActive && this.target) {
      const progress = 1 - this.darkOrbTimer / 0.6;
      const targetScreenPos = camera.worldToScreen(this.target.position.x, this.target.position.y);
      const orbX = screenPos.x + (targetScreenPos.x - screenPos.x) * progress;
      const orbY = (screenPos.y - this.height * 0.5 * camera.zoom) +
                   (targetScreenPos.y - (screenPos.y - this.height * 0.5 * camera.zoom)) * progress;

      ctx.beginPath();
      ctx.arc(orbX, orbY, 12 * camera.zoom, 0, Math.PI * 2);
      const orbGradient = ctx.createRadialGradient(orbX, orbY, 0, orbX, orbY, 12 * camera.zoom);
      orbGradient.addColorStop(0, '#9400d3');
      orbGradient.addColorStop(1, '#4b0082');
      ctx.fillStyle = orbGradient;
      ctx.fill();
    }

    // Shadow (smaller, floating)
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 20 * camera.zoom, 18 * camera.zoom, 9 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fill();

    // Wings (dark feathered)
    ctx.fillStyle = '#2a1a3a';
    // Left wing
    ctx.save();
    ctx.translate(screenPos.x - 10 * camera.zoom, screenPos.y - this.height * 0.6 * camera.zoom + floatOffset);
    ctx.scale(wingSpan, 1);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(-35 * camera.zoom, -20 * camera.zoom, -50 * camera.zoom, 10 * camera.zoom);
    ctx.quadraticCurveTo(-30 * camera.zoom, 5 * camera.zoom, 0, 15 * camera.zoom);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Right wing
    ctx.save();
    ctx.translate(screenPos.x + 10 * camera.zoom, screenPos.y - this.height * 0.6 * camera.zoom + floatOffset);
    ctx.scale(wingSpan, 1);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(35 * camera.zoom, -20 * camera.zoom, 50 * camera.zoom, 10 * camera.zoom);
    ctx.quadraticCurveTo(30 * camera.zoom, 5 * camera.zoom, 0, 15 * camera.zoom);
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Body
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y - this.height * 0.4 * camera.zoom + floatOffset, 15 * camera.zoom, 25 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    // Robes
    ctx.fillStyle = '#1a0a2a';
    ctx.beginPath();
    ctx.moveTo(screenPos.x - 12 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - 15 * camera.zoom, screenPos.y + 10 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 15 * camera.zoom, screenPos.y + 10 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 12 * camera.zoom, screenPos.y - this.height * 0.3 * camera.zoom + floatOffset);
    ctx.closePath();
    ctx.fill();

    // Head with halo (corrupted)
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : '#3a2a4a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.75 * camera.zoom + floatOffset, 12 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Corrupted halo
    ctx.strokeStyle = '#8b0000';
    ctx.lineWidth = 2 * camera.zoom;
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y - this.height * 0.9 * camera.zoom + floatOffset, 15 * camera.zoom, 5 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Glowing eyes
    ctx.fillStyle = '#ff00ff';
    ctx.beginPath();
    ctx.arc(screenPos.x - 4 * camera.zoom, screenPos.y - this.height * 0.77 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 4 * camera.zoom, screenPos.y - this.height * 0.77 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    this.renderHealthBar(renderer, screenPos, camera.zoom);
  }
}

/**
 * Erlik Han - FINAL BOSS (Lord of the Underworld)
 * The ultimate enemy with multiple phases, devastating attacks, and minion summoning
 */
export class ErlikHan extends Enemy {
  private phase: number = 1;
  private phaseTransitioned2: boolean = false;
  private phaseTransitioned3: boolean = false;
  private phaseTimer: number = 0;
  private summonCooldown: number = 0;
  private soulStormCooldown: number = 0;
  private deathGripCooldown: number = 0;
  private ultimateCooldown: number = 0;

  private soulStormActive: boolean = false;
  private soulStormTimer: number = 0;
  private deathGripActive: boolean = false;
  private deathGripTimer: number = 0;
  private ultimateActive: boolean = false;
  private ultimateTimer: number = 0;

  private floatPhase: number = 0;
  private auraIntensity: number = 0;

  // Phase 2: Karanlık Dalga (Dark Wave) - expanding ring
  private darkWaveCooldown: number = 0;
  private darkWaveActive: boolean = false;
  private darkWaveTimer: number = 0;

  // Phase 2: Shadow minion summon timer
  private shadowSummonTimer: number = 20;

  // Phase 3: Enrage aura and arena shrink
  private enrageAuraPhase: number = 0;
  private arenaShrinkRadius: number = 400; // Initial arena radius
  private arenaShrinkRate: number = 2; // Pixels per second
  private arenaCenter: { x: number; y: number } = { x: 0, y: 0 };
  private arenaCenterSet: boolean = false;

  // Enrage timer (5 minutes)
  private enrageTimer: number = 300;
  private isEnraged: boolean = false;
  private readonly baseAttackDamage: number = 120;
  private readonly baseSpeed: number = 50;

  // Phase transition
  private invulnerableTimer: number = 0;
  private phaseTransitionFlash: number = 0;

  // Screen shake
  public screenShakeRequest: number = 0;

  constructor(x: number, y: number) {
    super({
      x,
      y,
      width: 80,
      height: 120,
      speed: 50,
      stats: {
        hp: 10000,
        maxHp: 10000,
        mana: 0,
        maxMana: 0,
        energyShield: 1000,
        maxEnergyShield: 1000,
        armor: 50,
        evasion: 25,
        strength: 80,
        agility: 30,
        spirit: 100,
      },
      name: 'Erlik Han',
      attackRange: 100,
      attackDamage: 120,
      attackSpeed: 0.6,
      aggroRange: 600,
      expReward: 5000,
      color: '#0a0a0a',
    });
    this.itemLevel = 40;
    this.attackElement = 'karanlik';
  }

  takeDamage(amount: number, type?: import('@shared/types').ElementType, bypassES?: boolean): number {
    if (this.invulnerableTimer > 0) return 0;
    return super.takeDamage(amount, type, bypassES);
  }

  update(deltaTime: number): void {
    super.update(deltaTime);
    this.floatPhase += deltaTime * 1.5;
    this.phaseTimer += deltaTime;
    this.auraIntensity = 0.5 + Math.sin(this.floatPhase * 2) * 0.3;

    // Set arena center on first update
    if (!this.arenaCenterSet) {
      this.arenaCenter = { x: this.position.x, y: this.position.y };
      this.arenaCenterSet = true;
    }

    // Invulnerability timer
    if (this.invulnerableTimer > 0) {
      this.invulnerableTimer -= deltaTime;
    }

    // Enrage timer countdown
    if (!this.isEnraged && this.isAlive && !this.isDying) {
      this.enrageTimer -= deltaTime;
      if (this.enrageTimer <= 0) {
        this.isEnraged = true;
        this.enrageTimer = 0;
        this.attackDamage = this.baseAttackDamage * 2;
        this.speed = this.baseSpeed * 1.5;
      }
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      this.phaseTransitionFlash -= deltaTime * 2;
    }

    // Screen shake
    if (this.screenShakeRequest > 0) {
      this.screenShakeRequest -= deltaTime;
    }

    // Phase transitions based on HP
    const hpPercent = this.getHealthPercent();
    if (hpPercent <= 0.2 && this.phase < 4) {
      this.phase = 4; // Enrage phase
    } else if (hpPercent <= 0.4 && this.phase < 3 && !this.phaseTransitioned3) {
      this.phase = 3;
      this.phaseTransitioned3 = true;
      this.onPhaseTransition3();
    } else if (hpPercent <= 0.7 && this.phase < 2 && !this.phaseTransitioned2) {
      this.phase = 2;
      this.phaseTransitioned2 = true;
      this.onPhaseTransition2();
    }

    // Phase 2+: Dark Wave
    if (this.darkWaveCooldown > 0) this.darkWaveCooldown -= deltaTime;
    if (this.darkWaveActive) {
      this.darkWaveTimer -= deltaTime;
      // Damage player if they are caught in the expanding ring
      if (this.target) {
        const dist = this.distanceTo(this.target);
        const ringRadius = (1 - this.darkWaveTimer / 2.0) * 300;
        const ringThickness = 30;
        if (Math.abs(dist - ringRadius) < ringThickness) {
          this.target.takeDamage(Math.floor(25 * deltaTime), this.attackElement);
        }
      }
      if (this.darkWaveTimer <= 0) {
        this.darkWaveActive = false;
      }
    }

    // Phase 2+: Shadow minion summoning every 20s
    if (this.phase >= 2) {
      this.shadowSummonTimer -= deltaTime;
    }

    // Phase 3+: Enrage aura pulsing
    if (this.phase >= 3) {
      this.enrageAuraPhase += deltaTime * 5;

      // Phase 3: +60% damage
      if (!this.isEnraged) {
        this.attackDamage = Math.floor(this.baseAttackDamage * 1.6);
      }

      // Arena shrinks - outer tiles become dangerous
      this.arenaShrinkRadius = Math.max(150, this.arenaShrinkRadius - this.arenaShrinkRate * deltaTime);

      // Damage player if outside arena
      if (this.target) {
        const dx = this.target.position.x - this.arenaCenter.x;
        const dy = this.target.position.y - this.arenaCenter.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > this.arenaShrinkRadius) {
          this.target.takeDamage(Math.floor(20 * deltaTime), this.attackElement);
        }
      }
    }

    // Phase 3+: All abilities faster (cooldowns reduced via phase checks above)

    // Cooldowns
    if (this.summonCooldown > 0) this.summonCooldown -= deltaTime;
    if (this.soulStormCooldown > 0) this.soulStormCooldown -= deltaTime;
    if (this.deathGripCooldown > 0) this.deathGripCooldown -= deltaTime;
    if (this.ultimateCooldown > 0) this.ultimateCooldown -= deltaTime;

    // Active abilities
    if (this.soulStormActive) {
      this.soulStormTimer -= deltaTime;
      if (this.soulStormTimer <= 0) this.soulStormActive = false;
    }

    if (this.deathGripActive) {
      this.deathGripTimer -= deltaTime;
      if (this.deathGripTimer <= 0) this.deathGripActive = false;
    }

    if (this.ultimateActive) {
      this.ultimateTimer -= deltaTime;
      if (this.ultimateTimer <= 0) this.ultimateActive = false;
    }

    // Use abilities in combat
    if (this.state === 'chase' || this.state === 'attack') {
      // Phase 4 ultimate ability
      if (this.phase === 4 && this.ultimateCooldown <= 0) {
        this.castUltimate();
      }
      // Phase 2+: Dark Wave
      else if (this.phase >= 2 && !this.darkWaveActive && this.darkWaveCooldown <= 0 && Math.random() < 0.15) {
        this.castDarkWave();
      }
      // Soul storm - AoE
      else if (this.phase >= 2 && this.soulStormCooldown <= 0) {
        this.castSoulStorm();
      }
      // Death grip - single target
      else if (this.deathGripCooldown <= 0) {
        this.castDeathGrip();
      }
    }
  }

  private onPhaseTransition2(): void {
    this.invulnerableTimer = 1.0;
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 0.5;

    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 40,
        color: ['#ff0000', '#880000', '#440000'],
        speed: { min: 150, max: 400 },
        size: { min: 4, max: 9 },
        life: { min: 0.6, max: 1.2 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 30,
        friction: 1,
        alphaDecay: 1.2,
        sizeDecay: 2,
      });
    }
  }

  private onPhaseTransition3(): void {
    this.invulnerableTimer = 1.5;
    this.phaseTransitionFlash = 1.0;
    this.screenShakeRequest = 1.0;

    const ps = getParticleSystem();
    if (ps) {
      ps.emit({
        position: { x: this.position.x, y: this.position.y - this.height / 2 },
        count: 60,
        color: ['#ff0000', '#ff4400', '#000000'],
        speed: { min: 200, max: 500 },
        size: { min: 5, max: 12 },
        life: { min: 0.8, max: 1.5 },
        direction: 0,
        spread: Math.PI * 2,
        gravity: 20,
        friction: 1,
        alphaDecay: 1.0,
        sizeDecay: 1.5,
      });
    }
  }

  /**
   * Karanlık Dalga - Expanding dark ring that must be dodged
   */
  private castDarkWave(): void {
    this.darkWaveActive = true;
    this.darkWaveTimer = 2.0;
    this.darkWaveCooldown = this.phase >= 3 ? 8 : 12;
    this.screenShakeRequest = 0.3;
  }

  /**
   * Check if should summon shadow minions (phase 2+, every 20s)
   */
  shouldSummonShadows(): boolean {
    if (this.phase >= 2 && this.shadowSummonTimer <= 0 && this.isAlive) {
      this.shadowSummonTimer = this.phase >= 3 ? 15 : 20;
      return true;
    }
    return false;
  }

  getArenaShrinkRadius(): number {
    return this.arenaShrinkRadius;
  }

  getArenaCenter(): { x: number; y: number } {
    return this.arenaCenter;
  }

  isDarkWaveActive(): boolean {
    return this.darkWaveActive;
  }

  getDarkWaveProgress(): number {
    return this.darkWaveActive ? 1 - this.darkWaveTimer / 2.0 : 0;
  }

  private castSoulStorm(): void {
    if (!this.target) return;
    this.soulStormActive = true;
    this.soulStormTimer = 3.0;
    this.soulStormCooldown = 15 - this.phase * 2;

    // AoE damage over time effect
    const distance = this.distanceTo(this.target);
    if (distance <= 200) {
      this.target.takeDamage(Math.floor(this.attackDamage * 0.4), this.attackElement);
    }
  }

  private castDeathGrip(): void {
    if (!this.target) return;
    this.deathGripActive = true;
    this.deathGripTimer = 1.0;
    this.deathGripCooldown = 8;

    // Pull target closer and deal damage
    const distance = this.distanceTo(this.target);
    if (distance <= 300) {
      this.target.takeDamage(Math.floor(this.attackDamage * 0.6), this.attackElement);
    }
  }

  private castUltimate(): void {
    if (!this.target) return;
    this.ultimateActive = true;
    this.ultimateTimer = 2.5;
    this.ultimateCooldown = 30;

    // Massive screen-wide damage
    const distance = this.distanceTo(this.target);
    if (distance <= 400) {
      this.target.takeDamage(Math.floor(this.attackDamage * 2.0), this.attackElement);
    }
  }

  shouldSummon(): boolean {
    if (this.summonCooldown <= 0 && this.isAlive && this.phase >= 2) {
      this.summonCooldown = 25 - this.phase * 4;
      return true;
    }
    return false;
  }

  getHealthPercent(): number {
    return this.stats.hp / this.stats.maxHp;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (!this.isAlive && !this.isDying) return;

    const screenPos = this.getScreenPosition(camera);
    const ctx = renderer.context;
    const floatOffset = Math.sin(this.floatPhase) * 8 * camera.zoom;

    // Arena shrink danger zone (phase 3+)
    if (this.phase >= 3 && this.arenaCenterSet) {
      ctx.save();
      const arenaCenterScreen = camera.worldToScreen(this.arenaCenter.x, this.arenaCenter.y);
      const shrinkRadius = this.arenaShrinkRadius * camera.zoom;

      // Draw danger zone outer ring
      ctx.globalAlpha = 0.3 + Math.sin(this.enrageAuraPhase) * 0.1;
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 4 * camera.zoom;
      ctx.setLineDash([10 * camera.zoom, 5 * camera.zoom]);
      ctx.beginPath();
      ctx.arc(arenaCenterScreen.x, arenaCenterScreen.y, shrinkRadius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Fading red outside arena
      ctx.globalAlpha = 0.15;
      ctx.fillStyle = '#330000';
      // Draw a large rect with the circle cut out via composite
      ctx.fillRect(arenaCenterScreen.x - 500 * camera.zoom, arenaCenterScreen.y - 500 * camera.zoom, 1000 * camera.zoom, 1000 * camera.zoom);

      ctx.restore();
    }

    // Karanlık Dalga (Dark Wave) - expanding ring
    if (this.darkWaveActive) {
      ctx.save();
      const waveProgress = 1 - this.darkWaveTimer / 2.0;
      const waveRadius = waveProgress * 300 * camera.zoom;
      const waveAlpha = 1 - waveProgress;

      ctx.strokeStyle = `rgba(100, 0, 0, ${waveAlpha})`;
      ctx.lineWidth = 20 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, waveRadius, 0, Math.PI * 2);
      ctx.stroke();

      // Inner dark fill
      ctx.fillStyle = `rgba(30, 0, 30, ${waveAlpha * 0.2})`;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, waveRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Phase transition flash
    if (this.phaseTransitionFlash > 0) {
      ctx.save();
      ctx.globalAlpha = this.phaseTransitionFlash * 0.5;
      const flashRadius = (1 - this.phaseTransitionFlash) * 200 * camera.zoom;
      const flashGrad = ctx.createRadialGradient(
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 0,
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius
      );
      flashGrad.addColorStop(0, '#ff0000');
      flashGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, flashRadius, 0, Math.PI * 2);
      ctx.fillStyle = flashGrad;
      ctx.fill();
      ctx.restore();
    }

    // Invulnerability shield
    if (this.invulnerableTimer > 0) {
      ctx.save();
      ctx.globalAlpha = 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 4 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 70 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Ultimate effect - screen-wide doom
    if (this.ultimateActive) {
      ctx.save();
      ctx.globalAlpha = this.ultimateTimer / 2.5 * 0.4;
      const gradient = ctx.createRadialGradient(
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
        0,
        screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom,
        300 * camera.zoom
      );
      gradient.addColorStop(0, '#ff0000');
      gradient.addColorStop(0.5, '#8b0000');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }

    // Soul Storm effect
    if (this.soulStormActive) {
      ctx.save();
      const stormProgress = 1 - this.soulStormTimer / 3.0;
      ctx.globalAlpha = 0.3 + stormProgress * 0.2;

      // Swirling souls
      for (let i = 0; i < 8; i++) {
        const angle = (this.floatPhase * 3 + i * Math.PI / 4) % (Math.PI * 2);
        const dist = 80 + Math.sin(this.floatPhase * 2 + i) * 30;
        const soulX = screenPos.x + Math.cos(angle) * dist * camera.zoom;
        const soulY = screenPos.y - this.height * 0.4 * camera.zoom + floatOffset + Math.sin(angle) * dist * 0.3 * camera.zoom;

        ctx.beginPath();
        ctx.arc(soulX, soulY, 8 * camera.zoom, 0, Math.PI * 2);
        ctx.fillStyle = i % 2 === 0 ? '#00ff88' : '#88ffaa';
        ctx.fill();
      }
      ctx.restore();
    }

    // Death Grip effect
    if (this.deathGripActive && this.target) {
      ctx.save();
      ctx.globalAlpha = this.deathGripTimer;
      const targetScreenPos = camera.worldToScreen(this.target.position.x, this.target.position.y);

      // Dark chains
      ctx.strokeStyle = '#4a0000';
      ctx.lineWidth = 5 * camera.zoom;
      ctx.setLineDash([10, 5]);
      ctx.beginPath();
      ctx.moveTo(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset);
      ctx.lineTo(targetScreenPos.x, targetScreenPos.y);
      ctx.stroke();
      ctx.setLineDash([]);

      // Grip indicator
      ctx.beginPath();
      ctx.arc(targetScreenPos.x, targetScreenPos.y, 30 * camera.zoom * (1 - this.deathGripTimer), 0, Math.PI * 2);
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3 * camera.zoom;
      ctx.stroke();
      ctx.restore();
    }

    // Massive dark aura
    ctx.save();
    const auraSize = 100 * this.auraIntensity;
    const auraGradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      0,
      screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset,
      auraSize * camera.zoom
    );
    const isPhase3Plus = this.phase >= 3 || this.isEnraged;
    auraGradient.addColorStop(0, isPhase3Plus ? 'rgba(150, 0, 0, 0.6)' : 'rgba(100, 0, 0, 0.5)');
    auraGradient.addColorStop(0.5, isPhase3Plus ? 'rgba(80, 0, 30, 0.4)' : 'rgba(50, 0, 50, 0.3)');
    auraGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, auraSize * camera.zoom, 0, Math.PI * 2);
    ctx.fillStyle = auraGradient;
    ctx.fill();
    ctx.restore();

    // Phase 3+ enrage pulsing red/black aura
    if (this.phase >= 3) {
      ctx.save();
      const enragePulse = 0.2 + Math.sin(this.enrageAuraPhase) * 0.15;
      ctx.globalAlpha = enragePulse;
      ctx.strokeStyle = this.isEnraged ? '#ff0000' : '#cc0000';
      ctx.lineWidth = 5 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 80 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();

      // Inner black pulse
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2 * camera.zoom;
      ctx.beginPath();
      ctx.arc(screenPos.x, screenPos.y - this.height * 0.5 * camera.zoom + floatOffset, 75 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // Floating dark particles
    for (let i = 0; i < 12; i++) {
      const angle = (this.floatPhase + i * Math.PI / 6) % (Math.PI * 2);
      const dist = 50 + Math.sin(this.floatPhase * 2 + i) * 20;
      const px = screenPos.x + Math.cos(angle) * dist * camera.zoom;
      const py = screenPos.y - this.height * 0.5 * camera.zoom + floatOffset + Math.sin(angle) * dist * 0.3 * camera.zoom;

      ctx.beginPath();
      ctx.arc(px, py, (3 + i % 3) * camera.zoom, 0, Math.PI * 2);
      ctx.fillStyle = i % 3 === 0 ? '#ff0000' : i % 3 === 1 ? '#880088' : '#440044';
      ctx.fill();
    }

    // Large shadow
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y + 15 * camera.zoom, 45 * camera.zoom, 22 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fill();

    // Massive robed body
    ctx.fillStyle = this.hitFlashTimer > 0 ? '#ffffff' : this.color;
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y - this.height * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.1 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x - this.width * 0.4 * camera.zoom, screenPos.y + floatOffset);
    ctx.lineTo(screenPos.x + this.width * 0.4 * camera.zoom, screenPos.y + floatOffset);
    ctx.lineTo(screenPos.x + this.width * 0.5 * camera.zoom, screenPos.y - this.height * 0.1 * camera.zoom + floatOffset);
    ctx.closePath();
    ctx.fill();

    // Robe details - dark red trim
    ctx.strokeStyle = '#4a0000';
    ctx.lineWidth = 4 * camera.zoom;
    ctx.stroke();

    // Arcane symbols on robe
    ctx.strokeStyle = '#660000';
    ctx.lineWidth = 2 * camera.zoom;
    for (let i = 0; i < 5; i++) {
      const symbolY = screenPos.y - this.height * (0.25 + i * 0.12) * camera.zoom + floatOffset;
      ctx.beginPath();
      ctx.arc(screenPos.x, symbolY, 8 * camera.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(screenPos.x - 8 * camera.zoom, symbolY);
      ctx.lineTo(screenPos.x + 8 * camera.zoom, symbolY);
      ctx.moveTo(screenPos.x, symbolY - 8 * camera.zoom);
      ctx.lineTo(screenPos.x, symbolY + 8 * camera.zoom);
      ctx.stroke();
    }

    // Crown of bones/horns
    ctx.fillStyle = '#2a1a1a';
    for (let i = 0; i < 5; i++) {
      const hornAngle = -Math.PI * 0.7 + (i / 4) * Math.PI * 0.4;
      const hornX = screenPos.x + Math.cos(hornAngle) * 25 * camera.zoom;
      const hornY = screenPos.y - this.height * 0.85 * camera.zoom + floatOffset + Math.sin(hornAngle) * 10 * camera.zoom;
      const hornTipX = screenPos.x + Math.cos(hornAngle) * 40 * camera.zoom;
      const hornTipY = screenPos.y - this.height * camera.zoom - 20 * camera.zoom + floatOffset + Math.sin(hornAngle) * 15 * camera.zoom;

      ctx.beginPath();
      ctx.moveTo(hornX - 5 * camera.zoom, hornY);
      ctx.lineTo(hornTipX, hornTipY);
      ctx.lineTo(hornX + 5 * camera.zoom, hornY);
      ctx.closePath();
      ctx.fill();
    }

    // Face - skull-like with glowing features
    ctx.fillStyle = '#1a0a0a';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y - this.height * 0.8 * camera.zoom + floatOffset, 20 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Burning eyes - intensity based on phase
    const eyeColor = this.phase === 4 ? '#ff0000' : this.phase === 3 ? '#ff4400' : this.phase === 2 ? '#ff8800' : '#ffaa00';
    ctx.shadowColor = eyeColor;
    ctx.shadowBlur = 15 * camera.zoom;
    ctx.fillStyle = eyeColor;
    ctx.beginPath();
    ctx.arc(screenPos.x - 7 * camera.zoom, screenPos.y - this.height * 0.83 * camera.zoom + floatOffset, 5 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 7 * camera.zoom, screenPos.y - this.height * 0.83 * camera.zoom + floatOffset, 5 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Mouth - glowing maw
    ctx.fillStyle = '#880000';
    ctx.beginPath();
    ctx.ellipse(screenPos.x, screenPos.y - this.height * 0.72 * camera.zoom + floatOffset, 8 * camera.zoom, 5 * camera.zoom, 0, 0, Math.PI * 2);
    ctx.fill();

    // Staff of Darkness
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 8 * camera.zoom;
    ctx.beginPath();
    ctx.moveTo(screenPos.x + 45 * camera.zoom, screenPos.y - 20 * camera.zoom + floatOffset);
    ctx.lineTo(screenPos.x + 45 * camera.zoom, screenPos.y - 100 * camera.zoom + floatOffset);
    ctx.stroke();

    // Staff skull topper
    ctx.fillStyle = '#3a2a2a';
    ctx.beginPath();
    ctx.arc(screenPos.x + 45 * camera.zoom, screenPos.y - 110 * camera.zoom + floatOffset, 15 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // Skull eyes on staff
    ctx.fillStyle = '#00ff00';
    ctx.beginPath();
    ctx.arc(screenPos.x + 41 * camera.zoom, screenPos.y - 112 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.arc(screenPos.x + 49 * camera.zoom, screenPos.y - 112 * camera.zoom + floatOffset, 3 * camera.zoom, 0, Math.PI * 2);
    ctx.fill();

    // BOSS HEALTH BAR (larger, more ornate)
    const barWidth = 200 * camera.zoom;
    const barHeight = 18 * camera.zoom;
    const barY = screenPos.y - this.height * camera.zoom - 70 * camera.zoom + floatOffset;

    // Ornate border
    ctx.strokeStyle = '#ffd700';
    ctx.lineWidth = 4;
    ctx.strokeRect(screenPos.x - barWidth / 2 - 4, barY - 4, barWidth + 8, barHeight + 8);

    // Inner border
    ctx.strokeStyle = '#aa8800';
    ctx.lineWidth = 2;
    ctx.strokeRect(screenPos.x - barWidth / 2 - 2, barY - 2, barWidth + 4, barHeight + 4);

    // Background
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth, barHeight);

    // Health (color by phase)
    const healthColors = ['#880000', '#aa0000', '#cc0000', '#ff0000'];
    ctx.fillStyle = healthColors[this.phase - 1];
    ctx.fillRect(screenPos.x - barWidth / 2, barY, barWidth * this.getHealthPercent(), barHeight);

    // Boss title
    ctx.fillStyle = '#ffd700';
    ctx.font = `bold ${22 * camera.zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText('ERLIK HAN', screenPos.x, barY - 18 * camera.zoom);

    // Subtitle
    ctx.fillStyle = '#cc9900';
    ctx.font = `${14 * camera.zoom}px Arial`;
    ctx.fillText('Yeraltı Hükümdarı', screenPos.x, barY - 3 * camera.zoom);

    // Phase indicator
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${14 * camera.zoom}px Arial`;
    const phaseNames = ['Uyanış', 'Öfke', 'Gazap', 'SON FIRTINA'];
    ctx.fillText(`Faz ${this.phase}: ${phaseNames[this.phase - 1]}`, screenPos.x, barY + barHeight + 22 * camera.zoom);

    // Enrage timer display
    if (!this.isEnraged) {
      const minutes = Math.floor(this.enrageTimer / 60);
      const seconds = Math.floor(this.enrageTimer % 60);
      const timerColor = this.enrageTimer < 30 ? '#ff4444' : '#aaaaaa';
      ctx.fillStyle = timerColor;
      ctx.font = `${10 * camera.zoom}px Arial`;
      ctx.fillText(`${minutes}:${seconds.toString().padStart(2, '0')}`, screenPos.x, barY + barHeight + 36 * camera.zoom);
    } else {
      ctx.fillStyle = '#ff0000';
      ctx.font = `bold ${12 * camera.zoom}px Arial`;
      ctx.fillText('GAZAP!', screenPos.x, barY + barHeight + 36 * camera.zoom);
    }
  }
}
